import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { IconButton } from "@/components/IconButton";
import { FullscreenIcon, NextIcon, PauseIcon, PlayIcon, RestartIcon, SkipBackIcon, SkipForwardIcon, VolumeIcon } from "@/components/PlayerIcons";
import { WatchToggleButton } from "@/components/WatchToggleButton";
import {
  deletePlaybackSession,
  getAllItems,
  getMySettings,
  getWatchStates,
  reportPlaybackProblem,
  updateMySettings,
} from "@/api/endpoints";
import {
  createPlaybackSession,
  getPlaybackResumeItems,
  getPlaybackSession,
  getPlaybackSessions,
  recoverPlaybackDelivery,
  reportPlaybackClientLogs,
  reportPlaybackEvent,
  refreshPlaybackSession,
  sendPlaybackClientLogsBeacon,
  sendPlaybackEventKeepalive,
} from "@/pages/playerControlPlane";
import {
  PLAYBACK_CLIENT_LOG_MAX_BATCH_ENTRIES,
  PlaybackClientLogTooLargeError,
  selectPlaybackClientLogBatch,
} from "@/pages/playbackClientLogBatching";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import type { ChangeEvent, CSSProperties, MouseEvent, PointerEvent } from "react";
import type { BuildIdentityAttributes, Item, PlaybackAudioTrack, PlaybackClientLogEntry, PlaybackClientState, PlaybackSession, PlaybackSubtitleTrack, PlaybackVariant, ResumeItem } from "@/types/api";
import type Hls from "hls.js";
import type { MediaPlaylist, SubtitleSelectionOption } from "hls.js";
import { ApiError, ApiUnavailableError } from "@/api/client";
import { writeLocalPlaybackPosition } from "@/lib/localPlaybackPosition";
import {
  buildIdentityDateStamp,
  buildIdentityTitle,
  CLIENT_BUILD_INFO,
  clientBuildCompactLabel,
  clientBuildTitle,
  formatBuildDateStamp,
} from "@/lib/buildInfo";
import { useClientBuildFreshness } from "@/hooks/useClientBuildFreshness";
import {
  findNextEpisode,
  NEXT_EPISODE_AUTOPLAY_DELAY_SECONDS,
  shouldShowNextEpisodePrompt,
  sortSeriesEpisodes,
} from "@/pages/playerNextEpisode";
import {
  computePreviewPanelPosition,
  locateTrickplayFrame,
  parseTrickplayImagePlaylist,
  sliderPreviewOffsetFromPointer,
  sliderPreviewTimeFromPointer,
} from "@/pages/playerTrickplay";
import {
  buildPlayerHlsConfig,
  HLS_BUFFERED_RETRY_THRESHOLD_SECONDS,
  resolveFatalHlsAction,
  resolveFastUpshiftLevel,
  resolveNativeAbrCap,
  resolveStallRecoveryLevelCap,
  shouldReleaseStallRecoveryCap,
  shouldVideoUseNativeHls,
} from "@/pages/playerHlsRuntime";
import {
  formatAudioTrackLabel,
  preferredAudioLanguagesFromNavigator,
  selectPreferredAudioTrackIndex,
  type PlayerAudioTrack,
} from "@/pages/playerAudioTracks";
import { attachPlayerTextTrackCuePositioning, positionPlayerTextTrackCues } from "@/pages/playerTextTracks";
import {
  STALL_WATCHDOG_TICK_MS,
  evaluateStallWatchdog,
  initialStallWatchdogMemory,
  stallWatchdogMayRecoverDelivery,
  type StallWatchdogMemory,
} from "@/pages/playerStallWatchdog";
import {
  absolutePositionMS,
  generationRelativeMediaTimeSeconds,
  rawVideoPositionMS,
  reanchorDoubleCountsElapsed,
  wallClockMS,
  type TimelineSample,
} from "@/pages/playerTimeline";
import {
  evaluateNativeHlsPositionRestore,
  verifiedNativeHlsDeliveryReload,
  type VerifiedNativeHlsDeliveryReload,
} from "@/pages/playerNativeHlsPositionRestore";
import { bufferedStartupSeekTarget, evaluateStartupResumeSeek } from "@/pages/playerStartupResumeSeek";
import { evaluateStartupPlaybackGate } from "@/pages/playerStartupGate";
import {
  authoritativeResumeTuple,
  evaluateResumeAuthority,
  retargetResumeAuthority,
  type AuthoritativeResumeTuple,
  type PendingResumeAuthority,
} from "@/pages/playerResumeAuthority";
import {
  clearItemPlaybackState,
  markItemPlayed,
} from "@/lib/watchPlayback";

/**
 * Full-screen video player.
 *
 * Key design constraints from internal-docs/collaboration.md:
 * - KnotFin exists to solve poor remote streaming on unreliable uplinks.
 * - Do not assume LAN-like playback conditions.
 * - Show buffering state, bitrate info, and connection quality clearly.
 * - All media URLs go through playback session routes, never direct file URLs.
 */
type QualityOption = {
  id: string;
  label: string;
  maxVideoBitrate?: number;
};

type PlaybackSessionSwitchReason =
  | "expired_session_recovery"
  | "native_hls_restart_recovery"
  | "next_episode"
  | "playback_session_ownership_lost"
  | "playback_session_startup_timeout"
  | "quality_change"
  | "runtime_recovery"
  | "seek_replace"
  | "stopped_session_recovery";

type PlaybackSessionSwitchToken = {
  id: number;
  reason: PlaybackSessionSwitchReason;
};

type ApplyRecoveredPlaybackSessionOptions = {
  forceNativeHlsReload?: boolean;
  recoveryReason?: string;
};

type PlaybackLogCause = {
  id: string;
  event: string;
};

type ClientPlaybackLogFlushOptions = {
  includeRecent?: boolean;
  flushReason?: string;
};

type PlaybackHandoffReason = "gone_session_resource" | "runtime_recovery";

type PlaybackDebugWindow = Window & {
  __knotfinPlaybackDebug?: () => Record<string, unknown>;
};

const TRICKPLAY_PENDING_RETRY_MS = 2_000;
// Playback must hold this long after a runtime recovery before the recovery
// budget is restored. Shorter than one segment and a recovery loop refills its
// own budget every cycle (#846).
const RUNTIME_RECOVERY_BUDGET_RESTORE_MS = 60_000;

const NATIVE_HLS_CLIENT_PROFILE_IDS = new Set([
  "apple_tv_v1",
  "ios_v1",
  "web_safari_v1",
]);

function isNativeHlsClientProfile(profileID: string | null | undefined) {
  return NATIVE_HLS_CLIENT_PROFILE_IDS.has((profileID ?? "").trim().toLowerCase());
}

function nativeHlsRestartBuildKey(expectedBuild: BuildIdentityAttributes | null) {
  return [
    expectedBuild?.build_id,
    expectedBuild?.commit,
    expectedBuild?.date,
    expectedBuild?.version,
  ].find((part) => part && part.trim().length > 0) ?? "stale";
}

function nativeHlsLowQualitySnapshot(
  variants: PlaybackVariant[] | undefined,
  decodedVideoSize: { width: number; height: number } | null,
) {
  const decodedHeight = decodedVideoSize?.height ?? 0;
  if (decodedHeight <= 0 || !variants || variants.length < 2) {
    return null;
  }
  const targetHeight = variants.reduce((height, variant) => Math.max(height, variant.height), 0);
  if (targetHeight < 720) {
    return null;
  }
  const thresholdHeight = Math.min(720, Math.floor(targetHeight * 0.75));
  if (decodedHeight >= thresholdHeight) {
    return null;
  }
  return {
    decodedHeight,
    targetHeight,
    thresholdHeight,
  };
}

class StalePlaybackHandoffError extends Error {
  constructor() {
    super("Playback handoff no longer matches the active session");
    this.name = "StalePlaybackHandoffError";
  }
}

class InvalidResumeAuthorityError extends Error {
  constructor() {
    super("Resume response did not provide a complete generation clock");
    this.name = "InvalidResumeAuthorityError";
  }
}

function isStalePlaybackHandoffError(error: unknown): boolean {
  return error instanceof StalePlaybackHandoffError;
}

async function getPlaybackEpisodeContext(currentItem: Item): Promise<Item[]> {
  if (currentItem.attributes.item_type !== "episode") {
    return [];
  }

  const seriesID = currentItem.attributes.series_id ?? null;
  const seriesTitle = currentItem.attributes.series_title ?? null;
  const seasonID = currentItem.attributes.season_id ?? null;
  const seasonNumber = currentItem.attributes.season_number ?? null;
  if (!seriesID && !seriesTitle) {
    return [];
  }

  const currentSeason = await getAllItems({
    "filter[item_type]": "episode",
    "filter[series_id]": seriesID ?? undefined,
    "filter[series_title]": seriesID ? undefined : seriesTitle ?? undefined,
    "filter[season_id]": seasonID ?? undefined,
    "filter[season_number]": seasonID || seasonNumber == null ? undefined : String(seasonNumber),
  });
  const currentSeasonEpisodes = currentSeason.data;
  if (findNextEpisode(currentItem, currentSeasonEpisodes)) {
    return currentSeasonEpisodes;
  }
  if (!seasonID && seasonNumber == null) {
    return currentSeasonEpisodes;
  }

  const seasons = await getAllItems({
    "filter[item_type]": "season",
    "filter[series_id]": seriesID ?? undefined,
    "filter[series_title]": seriesID ? undefined : seriesTitle ?? undefined,
  });
  const orderedSeasons = [...seasons.data].sort((a, b) => {
    const left = a.attributes.season_number ?? Number.MAX_SAFE_INTEGER;
    const right = b.attributes.season_number ?? Number.MAX_SAFE_INTEGER;
    if (left !== right) return left - right;
    return a.attributes.title.localeCompare(b.attributes.title);
  });
  const currentSeasonIndex = orderedSeasons.findIndex((season) => {
    if (seasonID && season.id === seasonID) return true;
    return seasonNumber != null && season.attributes.season_number === seasonNumber;
  });
  const nextSeason = currentSeasonIndex >= 0 ? orderedSeasons[currentSeasonIndex + 1] : null;
  if (!nextSeason) {
    return currentSeasonEpisodes;
  }

  const nextSeasonEpisodes = await getAllItems({
    "filter[item_type]": "episode",
    "filter[series_id]": seriesID ?? undefined,
    "filter[series_title]": seriesID ? undefined : seriesTitle ?? undefined,
    "filter[season_id]": nextSeason.id,
    "filter[season_number]": nextSeason.attributes.season_number == null ? undefined : String(nextSeason.attributes.season_number),
  });

  return sortSeriesEpisodes([...currentSeasonEpisodes, ...nextSeasonEpisodes.data]);
}

type DeliverableSubtitleTrack = PlaybackSubtitleTrack & {
  attributes: PlaybackSubtitleTrack["attributes"] & {
    delivery: "sidecar_text";
    status?: "ready";
    url: string;
  };
};

function hlsSubtitlePreference(track: DeliverableSubtitleTrack | null | undefined): SubtitleSelectionOption | undefined {
  if (!track) {
    return undefined;
  }
  return {
    name: track.attributes.label,
    lang: track.attributes.language || undefined,
    forced: track.attributes.forced === true,
  };
}

type ParsedSubtitleCue = {
  startTime: number;
  endTime: number;
  text: string;
  id: string;
};

function parseVttTimestamp(value: string): number | null {
  const parts = value.trim().split(":");
  if (parts.length < 2 || parts.length > 3) {
    return null;
  }
  const seconds = Number.parseFloat(parts.pop() ?? "");
  const minutes = Number.parseInt(parts.pop() ?? "", 10);
  const hours = parts.length > 0 ? Number.parseInt(parts.pop() ?? "0", 10) : 0;
  if (!Number.isFinite(seconds) || !Number.isFinite(minutes) || !Number.isFinite(hours)) {
    return null;
  }
  return (hours * 3600) + (minutes * 60) + seconds;
}

function hlsCueHash(text: string): string {
  let hash = 5381;
  let index = text.length;
  while (index) {
    hash = (hash * 33) ^ text.charCodeAt(--index);
  }
  return (hash >>> 0).toString();
}

function hlsCueId(startTime: number, endTime: number, text: string): string {
  return hlsCueHash(startTime.toString()) + hlsCueHash(endTime.toString()) + hlsCueHash(text);
}

function parseWebVttCues(raw: string): ParsedSubtitleCue[] {
  const blocks = raw
    .replace(/\r\n|\r/g, "\n")
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);
  const cues: ParsedSubtitleCue[] = [];

  for (const block of blocks) {
    let lines = block.split("\n");
    if (lines[0]?.startsWith("WEBVTT")) {
      lines = lines.slice(1);
    }
    if (lines.length === 0 || /^(NOTE|STYLE|REGION)\b/.test(lines[0] ?? "")) {
      continue;
    }
    const timingIndex = lines.findIndex((line) => line.includes("-->"));
    if (timingIndex < 0) {
      continue;
    }
    const [startRaw, endAndSettingsRaw] = (lines[timingIndex] ?? "").split("-->");
    const endRaw = endAndSettingsRaw?.trim().split(/\s+/)[0] ?? "";
    const startTime = parseVttTimestamp(startRaw ?? "");
    const endTime = parseVttTimestamp(endRaw);
    const text = lines.slice(timingIndex + 1).join("\n").trim();
    if (startTime === null || endTime === null || endTime <= startTime || text === "") {
      continue;
    }
    cues.push({
      startTime,
      endTime,
      text,
      id: hlsCueId(startTime, endTime, text),
    });
  }
  return cues;
}

function matchingSubtitleTextTrack(video: HTMLVideoElement, track: DeliverableSubtitleTrack): TextTrack | null {
  const expectedLanguage = track.attributes.language || "";
  for (let index = 0; index < video.textTracks.length; index += 1) {
    const textTrack = video.textTracks[index];
    if (!textTrack || (textTrack.kind !== "subtitles" && textTrack.kind !== "captions")) {
      continue;
    }
    const textTrackId = (textTrack as TextTrack & { id?: string }).id;
    if (textTrackId && textTrackId === track.id) {
      return textTrack;
    }
    if (textTrack.label === track.attributes.label && (textTrack.language || "") === expectedLanguage) {
      return textTrack;
    }
  }
  return null;
}

function addParsedCueToTextTrack(textTrack: TextTrack, parsedCue: ParsedSubtitleCue) {
  const CueConstructor = window.VTTCue ?? (window as typeof window & { TextTrackCue?: typeof VTTCue }).TextTrackCue;
  if (!CueConstructor) {
    return;
  }
  if (textTrack.cues?.getCueById(parsedCue.id)) {
    return;
  }
  const cue = new CueConstructor(parsedCue.startTime, parsedCue.endTime, parsedCue.text);
  cue.id = parsedCue.id;
  try {
    textTrack.addCue(cue);
  } catch {
    // hls.js may win the race and add the same cue while the fallback is fetching.
  }
}

const QUALITY_OPTIONS: QualityOption[] = [
  { id: "auto", label: "Auto" },
  { id: "2m", label: "2 Mbps", maxVideoBitrate: 2_000_000 },
  { id: "4m", label: "4 Mbps", maxVideoBitrate: 4_000_000 },
  { id: "6m", label: "6 Mbps", maxVideoBitrate: 6_000_000 },
  { id: "8m", label: "8 Mbps", maxVideoBitrate: 8_000_000 },
  { id: "12m", label: "12 Mbps", maxVideoBitrate: 12_000_000 },
  { id: "source", label: "Source", maxVideoBitrate: 20_000_000 },
];

const PLAYBACK_CHECKPOINTS_KEY = "knotfin_playback_checkpoints";
const PLAYBACK_VOLUME_KEY = "knotfin_playback_volume";
const PLAYBACK_MUTED_KEY = "knotfin_playback_muted";
const BANDWIDTH_REPORT_INTERVAL_MS = 5_000;
const BANDWIDTH_REPORT_DELTA_RATIO = 0.2;
const CLIENT_PLAYBACK_LOG_INTERVAL_MS = 1_000;
const CLIENT_PLAYBACK_LOG_FLUSH_INTERVAL_MS = 5_000;
// Threshold for the per-fragment declared-vs-actual timeline signal: when a
// buffered fragment's real PTS start diverges from the playlist-declared
// start by more than this, the player's clock and the visible content have
// desynced (the "content skips while the clock keeps moving" condition).
const FRAG_TIMELINE_DRIFT_THRESHOLD_MS = 200;
// Local client-log ring depth = the "logging period" retained on the device for
// Copy Debug and the consent-based problem report. Kept locally regardless of any
// upload opt-in; the server report cap is sized above this (see ParseClientReportBatch).
// Sized for ~5 minutes of retention even under a high-frequency event burst
// (e.g. an hls.js level-switch storm at ~20 events/sec → ~6000 events). Pending
// The upload queue is deep enough to absorb that burst; flushes drain it in
// serialized-byte-bounded requests rather than assuming every event is small.
const CLIENT_PLAYBACK_LOG_RING_MAX = 6000;
const CLIENT_PLAYBACK_LOG_MAX_PENDING = 6000;
const CLIENT_TIMELINE_REGRESSION_TOLERANCE_MS = 2_000;
const CLIENT_TIMELINE_FORWARD_JUMP_TOLERANCE_MS = 3_000;
const STALE_STARTUP_RECOVERY_TIMEOUT_MS = 8_000;
// awaiting_playback_start otherwise has no bound of its own. hls.js
// constrained mode targets ~90s to fill its buffer and caps at ~120s (see
// the web player buffer policy in docs/deployment.md), so a legitimately
// slow constrained-network or cold-transcode start can approach that
// without anything being wrong. Past the documented cap the buffer policy
// is no longer "still working" -- something is actually stuck. (#707: a
// postmortem captured a session parked indefinitely with
// awaiting_playback_start=true, buffering_visible=true, host load average
// 39.98 -- segment production could not keep up, and STALE_STARTUP_RECOVERY
// above never caught it because that watchdog only fires when literally no
// media ever arrives; here media trickles in, just never enough to cross
// the startup buffer threshold.) On expiry, route through the same
// recovery machinery used for a confirmed disowned session (#705) rather
// than leaving an indefinite spinner or surfacing a bare timeout error: to
// the user, a stuck host and a disowned session look identical, and
// recovery gives the player a chance to come back on its own.
const AWAITING_PLAYBACK_START_TIMEOUT_MS = 120_000;
const BUFFERING_INDICATOR_DELAY_MS = 350;
// Keyboard skips whose target is already buffered commit on keyup; this
// coalescing delay applies only to targets outside the buffer (#992).
const KEYBOARD_SEEK_COMMIT_DEBOUNCE_MS = 650;
const KEYBOARD_SEEK_REPEAT_INTERVAL_MS = 350;
const NATIVE_HLS_SEEK_SETTLE_TIMEOUT_MS = 30_000;
const HLS_RESUME_AUTHORITY_TIMEOUT_MS = 30_000;
const SEEK_TARGET_REACHED_TOLERANCE_SECONDS = 1.5;
// A forward seek that lands just past the hls.js buffer is usually media the
// encoder has already produced but hls.js has not yet seen: the ready-window
// playlist has no ENDLIST and a 12s target duration, so hls.js re-polls it
// only every 6-12s. Rather than replace the session (a fresh encoder run and
// a cold start), force a playlist re-read and give the buffer this long to
// reach the target before falling back to replacement (#943).
const HLS_SEEK_FRONTIER_GRACE_SECONDS = 24;
const HLS_SEEK_FRONTIER_WAIT_TIMEOUT_MS = 8_000;
const HLS_SEEK_FRONTIER_POLL_INTERVAL_MS = 200;
// Native ABR releases the startup level cap to fully automatic once playback is
// established with the same buffer depth required for startup. Holding the cap
// longer makes hls.js fill the queue with low-quality media before it can climb.
const BUFFER_PROTECT_HOLD_SECONDS = 4;
const HLS_RESTART_HANDOFF_AFTER_MS = 3_000;
const HLS_BAD_SEGMENT_RETRY_LIMIT = 2;
const HLS_BAD_SEGMENT_HANDOFF_AFTER_MS = 3_000;
const STARTUP_NO_MEDIA_TIMEOUT_LIMIT = 3;
const EARLY_ENDED_COMPLETION_GRACE_SECONDS = 5;
const NATIVE_HLS_LOW_QUALITY_STUCK_MS = 60_000;
const NATIVE_HLS_LOW_QUALITY_WATCHDOG_INTERVAL_MS = 5_000;
const NATIVE_HLS_LOW_QUALITY_MIN_BUFFER_SECONDS = 12;
const NATIVE_HLS_LOW_QUALITY_RECOVERY_COOLDOWN_MS = 180_000;
const NATIVE_HLS_LOW_QUALITY_MAX_RECOVERIES = 2;
// Hold initial playback until this much is buffered ahead, so playback doesn't
// begin into the transcode cold-start trickle at a seek (first segments take
// ~0.3-1s each to produce) and immediately stall. Hard deadline so a slow link
// still starts rather than hanging on a black screen.
const STARTUP_MIN_BUFFER_SECONDS = 4;
const STARTUP_MIN_BUFFER_DEADLINE_MS = 6_000;
const EXPIRED_SESSION_RESUME_RECOVERY_PAGE_LIMIT = 20;
const EXPIRED_SESSION_RESUME_RECOVERY_MAX_ATTEMPTS = 60;
const TRICKPLAY_PREVIEW_PANEL_WIDTH_PX = 160;

type PlaybackCheckpoint = {
  sessionId: string;
  itemId: string;
  positionMs: number;
  maxVideoBitrate?: number;
};

type AudioTrackOption = PlayerAudioTrack & {
  name: string;
  lang?: string;
};

function audioTrackSessionID(
  serverTracks: PlaybackAudioTrack[],
  hlsTracks: MediaPlaylist[],
  index: number,
): string | null {
  if (!Number.isInteger(index) || index < 0) return null;
  const hlsTrack = hlsTracks[index];
  const candidates = [
    typeof hlsTrack?.name === "string" ? hlsTrack.name : "",
    `aud_${String(index + 1).padStart(2, "0")}`,
  ];
  for (const candidate of candidates) {
    const normalized = candidate.trim();
    if (normalized && serverTracks.some((track) => track.id === normalized)) {
      return normalized;
    }
  }
  return candidates[1] ?? null;
}

function serverAudioTrackForHlsTrack(
  serverTracks: PlaybackAudioTrack[],
  hlsTrack: MediaPlaylist,
): PlaybackAudioTrack | null {
  const hlsName = typeof hlsTrack.name === "string" ? hlsTrack.name.trim() : "";
  if (!hlsName) return null;
  return serverTracks.find((track) => track.id === hlsName) ?? null;
}

function serverAudioTrackLabel(
  serverTracks: PlaybackAudioTrack[],
  hlsTrack: MediaPlaylist,
  fallback: string | undefined,
): string {
  return serverAudioTrackForHlsTrack(serverTracks, hlsTrack)?.attributes.label ?? fallback ?? "";
}

function serverAudioTrackLanguage(
  serverTracks: PlaybackAudioTrack[],
  hlsTrack: MediaPlaylist,
  fallback: string | undefined,
): string | undefined {
  return serverAudioTrackForHlsTrack(serverTracks, hlsTrack)?.attributes.language ?? fallback;
}

type SeekDebugSnapshot = {
  event: string;
  pointerClientX: number | null;
  wrapperLeft: number;
  wrapperWidth: number;
  inputLeft: number;
  inputWidth: number;
  wrapperRatio: number | null;
  inputRatio: number | null;
  hoverTime: number | null;
  sliderValue: number;
};

type SeekIntentPhase = "start" | "update" | "commit" | "result" | "cancel";
type SeekIntentSource =
  | "document_keyboard"
  | "seek_rail_keyboard"
  | "progress_click"
  | "progress_drag"
  | "touch_scrub"
  | "seek_button"
  | "programmatic";

type SeekIntentState = {
  id: string;
  source: SeekIntentSource;
  input: string | null;
  startedAtMS: number;
  startPositionSeconds: number;
  targetSeconds: number;
  previewSeconds: number | null;
  resumeExpected: boolean;
};

type SeekIntentLogDetails = {
  id: string;
  phase: SeekIntentPhase;
  source: SeekIntentSource;
  input: string | null;
  startPositionSeconds: number | null;
  targetSeconds: number | null;
  previewSeconds: number | null;
  resumeExpected: boolean | null;
  resultDeltaMS?: number | null;
};

type PlaybackCommandPhase = "request" | "execute" | "blocked" | "result";
type PlaybackCommandType = "play" | "pause" | "toggle_play" | "seek" | "session_handoff" | "stop";
type PlaybackCommandSource =
  | "document_keyboard_space"
  | "document_keyboard_k"
  | "video_surface_click"
  | "center_toggle_click"
  | "control_button_click"
  | "media_event"
  | "seek_preview"
  | "seek_commit"
  | "progress_click"
  | "progress_drag"
  | "touch_scrub"
  | "seek_button"
  | "retry_button"
  | "skip_intro_button"
  | "quality_change"
  | "next_episode"
  | "runtime_recovery"
  | "stop_button"
  | "end_button"
  | "document_keyboard_escape"
  | "programmatic";

type PlaybackCommandState = {
  id: string;
  type: PlaybackCommandType;
  source: PlaybackCommandSource;
  input: string | null;
};

type PlaybackCommandLogDetails = PlaybackCommandState & {
  phase: PlaybackCommandPhase;
  blockedReason?: string | null;
  targetPositionSeconds?: number | null;
  sourcePositionSeconds?: number | null;
  sourceSessionId?: string | null;
  targetSessionId?: string | null;
  shouldResume?: boolean | null;
};

type ClientPlaybackLogOptions = {
  seekIntent?: SeekIntentLogDetails;
  command?: PlaybackCommandLogDetails;
  flushImmediately?: boolean;
  flushReason?: string;
};

function playbackCommandSourceForSeekIntent(source: SeekIntentSource): PlaybackCommandSource {
  switch (source) {
    case "progress_click":
      return "progress_click";
    case "progress_drag":
      return "progress_drag";
    case "touch_scrub":
      return "touch_scrub";
    case "seek_button":
      return "seek_button";
    case "document_keyboard":
    case "seek_rail_keyboard":
      return "seek_commit";
    case "programmatic":
    default:
      return "programmatic";
  }
}

function readStoredPlaybackVolume(): { volume: number; muted: boolean } {
  const rawVolume = Number(localStorage.getItem(PLAYBACK_VOLUME_KEY));
  const volume = Number.isFinite(rawVolume)
    ? Math.max(0, Math.min(1, rawVolume))
    : 1;
  const muted = localStorage.getItem(PLAYBACK_MUTED_KEY) === "true";
  return { volume, muted };
}

function getQualityOptionId(targetBitrate: number | undefined): QualityOption["id"] {
  return QUALITY_OPTIONS.find((option) => option.maxVideoBitrate === targetBitrate)?.id ?? "auto";
}

function maxVideoBitrateForQualityOption(id: QualityOption["id"]): number | undefined {
  return QUALITY_OPTIONS.find((option) => option.id === id)?.maxVideoBitrate;
}

function normalizeRequestedBitrateCap(targetBitrate: number | undefined): number | undefined {
  if (!targetBitrate || !Number.isFinite(targetBitrate) || targetBitrate <= 0) {
    return undefined;
  }
  return Math.floor(targetBitrate);
}

function mediaErrorMessage(error: MediaError): string {
  switch (error.code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return "media playback aborted";
    case MediaError.MEDIA_ERR_NETWORK:
      return "media network error";
    case MediaError.MEDIA_ERR_DECODE:
      return "media decode error";
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return "media source not supported";
    default:
      return error.message || "unknown media error";
  }
}

const DEFAULT_STARTUP_LEVEL_BANDWIDTH_BPS = 1_200_000;

export function selectStartupLevelHint(
  variants: PlaybackVariant[] | undefined,
  conservative: boolean,
  bandwidthHintBps: number | undefined,
): number {
  if (!variants || variants.length === 0) {
    return -1;
  }
  if (!Number.isFinite(bandwidthHintBps) || !bandwidthHintBps || bandwidthHintBps <= 0) {
    if (conservative) {
      return 0;
    }
    const topVariant = variants[variants.length - 1];
    if (variants.length <= 2 || (topVariant?.bandwidth ?? 0) <= 3_500_000) {
      return variants.length - 1;
    }
    let selected = 0;
    for (let index = 0; index < variants.length; index += 1) {
      const bandwidth = variants[index]?.bandwidth ?? 0;
      if (Number.isFinite(bandwidth) && bandwidth > 0 && bandwidth <= DEFAULT_STARTUP_LEVEL_BANDWIDTH_BPS) {
        selected = index;
      }
    }
    return selected;
  }

  // The bandwidth hint is a STEADY-STATE measurement, and with batch
  // coalescing (#757) steady-state runs ~2x what cold per-segment startup can
  // carry (transfer-size curve, hls-poc 2026-08-04). Discount it hard so a
  // session never opens on a rung it can only sustain once batching engages:
  // session pbs_00003199 opened at max on a 4-6 Mbit/s hint through the relay
  // and died in startup_waiting_for_media without ever playing a frame. On a
  // LAN-class hint (20M+) the discount still lands on the top rung, so fast
  // links keep their instant-start behavior (#399).
  const budget = Math.max(
    240_000,
    Math.floor(bandwidthHintBps * (conservative ? 0.4 : 0.5)),
  );
  let selected = 0;
  for (let index = 0; index < variants.length; index += 1) {
    if ((variants[index]?.bandwidth ?? 0) <= budget) {
      selected = index;
    }
  }
  return selected;
}

function readCheckpoint(sessionId: string): PlaybackCheckpoint | null {
  const raw = localStorage.getItem(PLAYBACK_CHECKPOINTS_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Record<string, PlaybackCheckpoint>;
    return parsed[sessionId] ?? null;
  } catch {
    return null;
  }
}

function writeCheckpoint(checkpoint: PlaybackCheckpoint): void {
  const raw = localStorage.getItem(PLAYBACK_CHECKPOINTS_KEY);
  let parsed: Record<string, PlaybackCheckpoint> = {};
  if (raw) {
    try {
      parsed = JSON.parse(raw) as Record<string, PlaybackCheckpoint>;
    } catch {
      parsed = {};
    }
  }

  parsed[checkpoint.sessionId] = checkpoint;
  localStorage.setItem(PLAYBACK_CHECKPOINTS_KEY, JSON.stringify(parsed));
}

function isPlaybackSessionExpiredError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 410);
}

function isTimeBuffered(video: HTMLVideoElement, targetSeconds: number, sessionOffsetSeconds: number): boolean {
  const relativeTargetSeconds = targetSeconds - sessionOffsetSeconds;
  if (relativeTargetSeconds < 0) {
    return false;
  }
  const tolerance = 0.35;
  for (let i = 0; i < video.buffered.length; i += 1) {
    const start = video.buffered.start(i) - tolerance;
    const end = video.buffered.end(i) + tolerance;
    if (relativeTargetSeconds >= start && relativeTargetSeconds <= end) {
      return true;
    }
  }
  return false;
}

// Seconds between the end of the buffered range that holds the playhead (or
// the last buffered range) and a forward target beyond it. Null when the target
// is not ahead of the buffer, so the caller cannot mistake a backward seek or
// an already-buffered target for a frontier seek.
function seekFrontierGapSeconds(video: HTMLVideoElement, targetSeconds: number, sessionOffsetSeconds: number): number | null {
  const relativeTargetSeconds = targetSeconds - sessionOffsetSeconds;
  if (relativeTargetSeconds < 0 || video.buffered.length === 0) {
    return null;
  }
  let frontierEnd: number | null = null;
  for (let i = 0; i < video.buffered.length; i += 1) {
    const start = video.buffered.start(i);
    const end = video.buffered.end(i);
    if (video.currentTime >= start - 0.35 && video.currentTime <= end + 0.35) {
      frontierEnd = end;
      break;
    }
  }
  if (frontierEnd === null) {
    frontierEnd = video.buffered.end(video.buffered.length - 1);
  }
  const gap = relativeTargetSeconds - frontierEnd;
  return gap > 0 ? gap : null;
}

function getBufferedAheadSeconds(video: HTMLVideoElement, absoluteSeconds: number, sessionOffsetSeconds: number): number {
  const relativeTargetSeconds = absoluteSeconds - sessionOffsetSeconds;
  if (relativeTargetSeconds < 0) {
    return 0;
  }
  const tolerance = 0.35;
  const initialRangeGapTolerance = 2.5;
  for (let i = 0; i < video.buffered.length; i += 1) {
    const start = video.buffered.start(i);
    const end = video.buffered.end(i);
    if (relativeTargetSeconds >= start - tolerance && relativeTargetSeconds <= end + tolerance) {
      return Math.max(0, end - Math.max(relativeTargetSeconds, start));
    }
    if (i === 0 && relativeTargetSeconds < start && start - relativeTargetSeconds <= initialRangeGapTolerance) {
      // Some browsers report the first remuxed HLS range with a positive timestamp offset
      // even though it is the start of the session timeline. Treat that as startup-ready.
      return Math.max(0, end - start);
    }
  }
  return 0;
}

function nativeHlsSeekTargetReady(video: HTMLVideoElement, targetSeconds: number, sessionOffsetSeconds: number): boolean {
  if (video.seeking || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    return false;
  }
  return isTimeBuffered(video, targetSeconds, sessionOffsetSeconds)
    || getBufferedAheadSeconds(video, targetSeconds, sessionOffsetSeconds) > 0.1;
}

function isEarlyMediaEnded(positionSeconds: number, durationSeconds: number): boolean {
  return durationSeconds > 0
    && Number.isFinite(positionSeconds)
    && positionSeconds + EARLY_ENDED_COMPLETION_GRACE_SECONDS < durationSeconds;
}

function getHlsErrorStatusCode(data: unknown): number | null {
  if (!data || typeof data !== "object") {
    return null;
  }
  const response = (data as { response?: unknown }).response;
  if (!response || typeof response !== "object") {
    return null;
  }
  const code = (response as { code?: unknown; status?: unknown }).code
    ?? (response as { code?: unknown; status?: unknown }).status;
  return typeof code === "number" && Number.isFinite(code) ? code : null;
}

function getHlsErrorLevel(data: unknown): number | null {
  if (!data || typeof data !== "object") {
    return null;
  }
  const frag = (data as { frag?: unknown }).frag;
  if (frag && typeof frag === "object") {
    const level = (frag as { level?: unknown }).level;
    if (typeof level === "number" && Number.isInteger(level) && level >= 0) {
      return level;
    }
  }
  const level = (data as { level?: unknown }).level;
  return typeof level === "number" && Number.isInteger(level) && level >= 0 ? level : null;
}

function getHlsErrorResponseUrl(data: unknown): string | null {
  if (!data || typeof data !== "object") {
    return null;
  }
  const response = (data as { response?: unknown }).response;
  if (!response || typeof response !== "object") {
    return null;
  }
  const url = (response as { url?: unknown }).url;
  return typeof url === "string" ? url : null;
}

function isHlsSegmentNetworkError(data: unknown): boolean {
  if (!data || typeof data !== "object") {
    return false;
  }
  const details = (data as { details?: unknown }).details;
  if (typeof details === "string" && details.toLowerCase().includes("frag")) {
    return true;
  }
  return getHlsErrorResponseUrl(data)?.includes("/segments/") ?? false;
}

function isPlaybackSessionResourceGoneStatus(statusCode: number | null): boolean {
  return statusCode === 404 || statusCode === 409 || statusCode === 410;
}

function alignToBufferedStart(video: HTMLVideoElement, sessionOffsetSeconds: number): boolean {
  if (video.buffered.length === 0 || !Number.isFinite(video.currentTime)) {
    return false;
  }
  const currentRelativeTime = Math.max(0, video.currentTime);
  const firstBufferedStart = Math.max(0, video.buffered.start(0));
  const initialRangeGapTolerance = 2.5;
  if (
    currentRelativeTime < firstBufferedStart
    && firstBufferedStart - currentRelativeTime <= initialRangeGapTolerance
    && sessionOffsetSeconds + firstBufferedStart >= sessionOffsetSeconds
  ) {
    video.currentTime = firstBufferedStart;
    return true;
  }
  return false;
}

function formatEpisodeOverlayCode(item: Item): string | null {
  const season = item.attributes.season_number;
  const episode = item.attributes.episode_number;
  if (season == null && episode == null) {
    return null;
  }
  if (season == null) {
    return `E${String(episode).padStart(2, "0")}`;
  }
  if (episode == null) {
    return `S${String(season).padStart(2, "0")}`;
  }
  return `S${String(season).padStart(2, "0")} E${String(episode).padStart(2, "0")}`;
}

function playbackSessionBaselineSeconds(attributes: PlaybackSession["attributes"] | undefined): number {
  if (!attributes) {
    return 0;
  }
  const startSeconds = Math.max(0, (attributes.start_position_ms ?? 0) / 1000);
  const positionSeconds = Math.max(0, (attributes.position_ms ?? 0) / 1000);
  return Math.max(startSeconds, positionSeconds);
}

// playbackSessionTimelineOriginSeconds is the source time the server maps to
// media-element time zero for the session's current generation. A seek-anchored
// hls.js session is served a ready-only window that starts at the anchor
// fragment, so its media clock is anchor-relative rather than title-absolute.
function playbackSessionTimelineOriginSeconds(
  attributes: PlaybackSession["attributes"] | undefined,
): number {
  if (!attributes) {
    return 0;
  }
  return Math.max(0, (attributes.media_timeline_origin_ms ?? 0) / 1000);
}

function isTemporaryPlaybackRecoveryError(error: unknown): boolean {
  if (error instanceof ApiUnavailableError) {
    return true;
  }
  if (error instanceof ApiError && [502, 503, 504].includes(error.status)) {
    return true;
  }
  return false;
}

// A rejected client-log flush should only be retried when the failure is
// transient. Any other 4xx (validation, not-found, and — critically —
// 403 ownership rejections) can never succeed on retry, so retrying it
// forever just re-sends the same rejected batch once a second (#705). The
// byte-bounded transport prevents the known edge-size 400/413 case before a
// request is sent; an unexpected 400/413 therefore remains permanent rather
// than looping an unchanged request.
function isRetryableClientPlaybackLogError(error: unknown): boolean {
  if (error instanceof PlaybackClientLogTooLargeError) {
    // No smaller batch exists for this payload, so retrying it can only
    // repeat forever and block everything queued behind it (#818).
    return false;
  }
  if (error instanceof ApiUnavailableError) {
    return true;
  }
  if (error instanceof ApiError) {
    return error.status >= 500 || error.status === 408 || error.status === 429;
  }
  return true;
}

// A 403 on any request that carries this player instance's client-owner
// identity (client_event_source_id, stamped on every playback event and
// client-log entry by playbackEventOrdering.ts) means the server has
// permanently rejected this instance as the session's owner. Client-log
// flush and playback-event reporting both hit the same server-side
// ownership check, but only the events path is telemetry-independent: it is
// sent unconditionally, while client logs require the diagnostics or error
// reporting toggle to be on (#707).
function isPlaybackSessionOwnershipError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

function resumableItemId(resumeItem: ResumeItem): string {
  return resumeItem.relationships.item.data.id;
}

function clampPlaybackSeconds(value: number, durationSeconds: number): number {
  const clampedValue = Number.isFinite(value) ? Math.max(0, value) : 0;
  if (Number.isFinite(durationSeconds) && durationSeconds > 0) {
    return Math.min(clampedValue, durationSeconds);
  }
  return clampedValue;
}

// The mapped absolute position is offset + element time, clamped to duration, so
// an inflated offset saturates it at exactly the duration and is indistinguishable
// from genuinely finishing. That is how an episode was force-completed twice with
// the element holding 23 minutes of undecoded media (#867): the mapped position
// read 2,576,620 ms — the duration, to the millisecond — while element time was
// 1,168,752 ms.
//
// The element is the witness that cannot be fooled by the offset. At a real end it
// is also at its end; when only the mapping has saturated, the remaining media is
// still there. The tolerance is deliberately generous: it only has to separate "a
// few seconds of tail" from "minutes of episode left", and being too strict would
// withhold a legitimate next-episode prompt.
const COMPLETION_MEDIA_REMAINING_TOLERANCE_SECONDS = 5;

export function mediaElementCorroboratesCompletion(video: HTMLVideoElement | null | undefined): boolean {
  if (!video) {
    return true;
  }
  const elementDuration = video.duration;
  const elementTime = video.currentTime;
  if (!Number.isFinite(elementDuration) || elementDuration <= 0 || !Number.isFinite(elementTime)) {
    // Unknown element duration (live-style or not yet loaded). Preserve the prior
    // behaviour rather than blocking completion on missing information.
    return true;
  }
  return elementDuration - elementTime <= COMPLETION_MEDIA_REMAINING_TOLERANCE_SECONDS;
}

function seekTargetReached(absoluteSeconds: number, targetSeconds: number, sourceSeconds: number | null): boolean {
  if (!Number.isFinite(absoluteSeconds) || !Number.isFinite(targetSeconds)) {
    return false;
  }
  if (Math.abs(absoluteSeconds - targetSeconds) <= SEEK_TARGET_REACHED_TOLERANCE_SECONDS) {
    return true;
  }
  if (sourceSeconds !== null && Number.isFinite(sourceSeconds) && targetSeconds < sourceSeconds) {
    return false;
  }
  return absoluteSeconds > targetSeconds;
}

function shouldIgnoreDocumentPlaybackShortcut(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable || target.closest("[contenteditable='true']")) {
    return true;
  }
  return ["BUTTON", "INPUT", "SELECT", "TEXTAREA"].includes(target.tagName);
}

function shouldIgnoreDocumentArrowSeekShortcut(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable || target.closest("[contenteditable='true']")) {
    return true;
  }
  // Buttons retain focus after pointer activation, but Left/Right do not
  // activate them. Keep arrow seeking available after the user clicks
  // play/pause or the video surface while preserving native keyboard behavior
  // for editable fields, selects, and sliders. Letter shortcuts remain
  // suppressed on buttons so a focused control keeps its normal key contract.
  return ["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName);
}

function mediaURLTail(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const parsed = new URL(trimmed, window.location.origin);
    const parts = parsed.pathname.split("/").filter(Boolean);
    return parts.at(-1) ?? null;
  } catch {
    const withoutQuery = trimmed.split(/[?#]/, 1)[0] ?? "";
    const parts = withoutQuery.replace(/\\/g, "/").split("/").filter(Boolean);
    return parts.at(-1) ?? null;
  }
}

function parseRetryAfterSeconds(value: string | null | undefined): number | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  const seconds = Number(trimmed);
  if (Number.isFinite(seconds) && seconds > 0) {
    return Math.min(300, Math.ceil(seconds));
  }
  const dateMS = Date.parse(trimmed);
  if (!Number.isNaN(dateMS)) {
    return Math.min(300, Math.max(1, Math.ceil((dateMS - Date.now()) / 1000)));
  }
  return null;
}

export function PlayerPage() {
  const hlsNetworkRetryRef = useRef(0);
  const hlsNetworkOutageStartedAtRef = useRef<number | null>(null);
  const hlsRestartHandoffTriggeredRef = useRef(false);
  const hlsBadSegmentErrorCountRef = useRef(0);
  const activeSessionStoppedRef = useRef(false);
  const subtitleItemIdRef = useRef<string | null>(null);
  const { sessionId } = useParams<{ sessionId: string }>();
  const activePlaybackSessionIdRef = useRef<string | null>(sessionId ?? null);
  // The session resource the runtime timeline is keyed to, readable from
  // callbacks that publish an accepted origin (#873).
  const currentSessionResourceIdRef = useRef<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const seekWrapRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const nativeHlsPlaybackRef = useRef(false);
  const nativeHlsDeliveryRef = useRef(false);
  const audioTrackUserSelectedRef = useRef(false);
  const audioTrackAutoSelectedRef = useRef(false);
  const hideTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const progressTimerRef = useRef<ReturnType<typeof window.setInterval> | null>(null);
  const periodicProgressReportInFlightRef = useRef(false);
  const retryTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const hlsRetryTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const runtimeRecoveryRetryTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const startupPlayTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const startupBufferTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const startupBufferDeadlineRef = useRef(0);
  const resumeAuthorityDeadlineTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const resumeAuthorityFailedRef = useRef(false);
  const startupResumeSeekSettledRef = useRef(false);
  const awaitingPlaybackStartRef = useRef(true);
  const segmentDurationSecondsRef = useRef(0);
  // Duration hls.js reports for the first fragment of the loaded playlist,
  // i.e. the anchor fragment of a seek-anchored session (#965).
  const anchorFragmentDurationSecondsRef = useRef<number | null>(null);
  const staleStartupRecoveryTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const awaitingPlaybackStartTimeoutTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const bufferingIndicatorTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const recoverAttemptedRef = useRef(false);
  const stoppedRecoveryAttemptedRef = useRef<string | null>(null);
  const playbackStartedRef = useRef(false);
  const playbackCompletedRef = useRef(false);
  const retryCountRef = useRef(0);
  const seekInFlightRef = useRef(false);
  // Cancels an in-progress frontier-grace wait (#943) when a stop or media
  // teardown supersedes it. Other commands are held off by seekInFlightRef.
  const frontierSeekWaitCancelRef = useRef<(() => void) | null>(null);
  const nativeSeekInFlightRef = useRef(false);
  const nativeSeekTargetSecondsRef = useRef<number | null>(null);
  const nativeSeekSettleTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const seekRequestRef = useRef(0);
  const failedSeekTargetRef = useRef<number | null>(null);
  const seekPointerActiveRef = useRef(false);
  const seekInteractionValueRef = useRef<number | null>(null);
  const keySeekDirectionRef = useRef<"backward" | "forward" | null>(null);
  const seekRailKeyRef = useRef<string | null>(null);
  const seekToPositionRef = useRef<(
    targetSeconds: number,
    shouldResume?: boolean,
    source?: PlaybackCommandSource,
    input?: string | null,
  ) => Promise<void>>(async () => {});
  const seekPreviewTimeRef = useRef<number | null>(null);
  const previewWasPlayingRef = useRef(false);
  const previewSeekingRef = useRef(false);
  const previewPausedPlaybackRef = useRef(false);
  const previewSeekRepeatTimerRef = useRef<ReturnType<typeof window.setInterval> | null>(null);
  const keyboardSeekRepeatTimerRef = useRef<ReturnType<typeof window.setInterval> | null>(null);
  const keyboardSeekCommitTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const seekIntentSequenceRef = useRef(0);
  const activeSeekIntentRef = useRef<SeekIntentState | null>(null);
  const seekIntentResultTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const playbackCommandSequenceRef = useRef(0);
  const resumeAfterSessionSwitchRef = useRef(false);
  const pendingSeekTimeRef = useRef<number | null>(null);
  const pendingSeekSourceTimeRef = useRef<number | null>(null);
  const bufferingStartedAtRef = useRef<number | null>(null);
  const lastBandwidthReportAtRef = useRef(0);
  const lastBandwidthReportValueRef = useRef(0);
  const runtimeRenegotiatingRef = useRef(false);
  const runtimeRecoveryCountRef = useRef(0);
  const lastObservedTimelineOffsetMSRef = useRef<number | null>(null);
  const lastRuntimeRecoveryAtMSRef = useRef(0);
  const stallWatchdogMemoryRef = useRef<StallWatchdogMemory>(initialStallWatchdogMemory());
  const mediaRecoveryCountRef = useRef(0);
  const nextEpisodeStartingRef = useRef(false);
  const sessionSwitchSequenceRef = useRef(0);
  const sessionSwitchInProgressRef = useRef<PlaybackSessionSwitchToken | null>(null);
  const nextEpisodeAutoplayTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const nextEpisodeAutoplayCountdownRef = useRef<ReturnType<typeof window.setInterval> | null>(null);
  const currentTimeRef = useRef(0);
  const effectiveDurationRef = useRef(0);
  const sessionStartSecondsRef = useRef(0);
  const sessionBaselineSecondsRef = useRef(0);
  const mediaTimelineOffsetSecondsRef = useRef(0);
  const sessionAtEndRef = useRef(false);
  const conservativeStartupRef = useRef(true);
  const deliverySelectedAudioTrackIdRef = useRef<string | null>(null);
  const requestedMaxVideoBitrateRef = useRef<number | undefined>(undefined);
  const startupGateReleasedRef = useRef(false);
  const startupPlayRequestRef = useRef(false);
  const startupNoMediaTimeoutCountRef = useRef(0);
  const previousObservedLevelRef = useRef<number | null>(null);
  const observedBandwidthRangeRef = useRef<{ min: number; max: number } | null>(null);
  const autoplayBlockedRef = useRef(false);
  const lastClientStateReportRef = useRef<{ signature: string; at: number } | null>(null);
  const lastSeekDebugReportRef = useRef<{ signature: string; at: number } | null>(null);
  const userPausedRef = useRef(false);
  const lastNonZeroVolumeRef = useRef(1);
  const startupLevelCapRef = useRef<number | null>(null);
  const stallRecoveryLevelCapRef = useRef<number | null>(null);
  const lastStallAtMsRef = useRef<number | null>(null);
  // Last autoLevelCapping value we actually wrote to hls.js. Native ABR owns
  // level selection; we only set the cap (startup hold, stall ratchet, then
  // -1). Writing an unchanged value makes hls.js re-emit LEVEL_SWITCHING, so
  // we apply the cap idempotently — only when it changes.
  const abrCommandedCapRef = useRef<number | null>(null);
  const lastFastUpshiftLevelRef = useRef<number>(-1);
  const attemptStartupPlaybackRef = useRef<(() => void) | null>(null);
  const requestStartupPlaybackRef = useRef<(() => void) | null>(null);
  const reportBandwidthSampleRef = useRef<(() => void) | null>(null);
  const reportClientPlaybackStateRef = useRef<((reason: string) => void) | null>(null);
  const clientPlaybackLogSeqRef = useRef(0);
  const clientPlaybackLogPendingRef = useRef<PlaybackClientLogEntry[]>([]);
  const clientPlaybackLogRingRef = useRef<PlaybackClientLogEntry[]>([]);
  const clientPlaybackLogSessionRef = useRef(sessionId ?? null);
  // Diagnostics upload gate. Default OFF: client log entries are kept in the
  // local ring buffer (so Copy Debug still works) but NOTHING is uploaded
  // unless the user turns on diagnostics. Error reporting is the lighter
  // opt-in that uploads only error-class events. Refs back the upload-path
  // callbacks; the state drives the player's Debug toggle UI.
  const playbackDiagnosticsEnabledRef = useRef(false);
  const playbackErrorReportingEnabledRef = useRef(false);
  const [playbackDiagnosticsEnabled, setPlaybackDiagnosticsEnabled] = useState(false);
  const [diagnosticsToggleBusy, setDiagnosticsToggleBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void getMySettings()
      .then((res) => {
        if (cancelled) {
          return;
        }
        const attrs = res.data.attributes;
        const diagnostics = attrs.playback_diagnostics_enabled === true;
        playbackDiagnosticsEnabledRef.current = diagnostics;
        playbackErrorReportingEnabledRef.current = attrs.playback_error_reporting_enabled === true;
        setPlaybackDiagnosticsEnabled(diagnostics);
      })
      .catch(() => {
        // Settings are best-effort; default stays OFF (no uploads).
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const togglePlaybackDiagnostics = useCallback(() => {
    const next = !playbackDiagnosticsEnabledRef.current;
    setDiagnosticsToggleBusy(true);
    void updateMySettings({ playback_diagnostics_enabled: next })
      .then((res) => {
        const enabled = res.data.attributes.playback_diagnostics_enabled === true;
        playbackDiagnosticsEnabledRef.current = enabled;
        setPlaybackDiagnosticsEnabled(enabled);
        if (enabled) {
          // Deliver recent buffered context immediately so support gets the
          // lead-up to the problem without waiting for the next interval.
          flushClientPlaybackLogsRef.current?.(false, {
            includeRecent: true,
            flushReason: "diagnostics_enabled",
          });
        }
      })
      .catch(() => {
        // Leave the toggle state unchanged on failure.
      })
      .finally(() => {
        setDiagnosticsToggleBusy(false);
      });
  }, []);
  const clientPlaybackLogFlushInFlightRef = useRef(false);
  // A session can become unusable in ways that are only detectable on the
  // normal playback path, not through telemetry the user may have turned
  // off (#707): a 403 means the server has permanently rejected this player
  // instance as the session's owner (see #705 -- originally observed on a
  // client-log flush, which requires the diagnostics/error-reporting toggle
  // to be on, but the same rejection also lands on playback-event reporting,
  // which is unconditional); an awaiting_playback_start timeout means
  // startup is stuck for long enough that it looks the same to the user
  // either way. `playbackSessionOwnershipLostSessionId` drives the recovery
  // effect below regardless of which of these triggered it;
  // `playbackSessionRecoveryReasonRef` records which one so the effect can
  // report the real cause. The ref lets synchronous, high-frequency callers
  // (flush/queue) check the disowned set without depending on render state.
  const playbackSessionOwnershipLostSessionsRef = useRef<Set<string>>(new Set());
  const playbackSessionOwnershipRecoveryAttemptedSessionsRef = useRef<Set<string>>(new Set());
  const playbackSessionRecoveryReasonRef = useRef<PlaybackSessionSwitchReason>("playback_session_ownership_lost");
  const [playbackSessionOwnershipLostSessionId, setPlaybackSessionOwnershipLostSessionId] =
    useState<string | null>(null);
  // Idempotent per session id (a second signal for the same session, e.g. a
  // client-log 403 followed moments later by a playback-event 403, must not
  // re-trigger recovery or overwrite the reason already recorded).
  const markPlaybackSessionUnusable = useCallback((
    unusableSessionId: string,
    reason: PlaybackSessionSwitchReason,
  ) => {
    if (playbackSessionOwnershipLostSessionsRef.current.has(unusableSessionId)) {
      return;
    }
    playbackSessionOwnershipLostSessionsRef.current.add(unusableSessionId);
    playbackSessionRecoveryReasonRef.current = reason;
    setPlaybackSessionOwnershipLostSessionId(unusableSessionId);
  }, []);
  const lastClientPlaybackLogFlushAtRef = useRef(0);
  const lastClientPlaybackLogPositionMSRef = useRef(0);
  const lastClientPlaybackLogSampleRef = useRef<{ positionMS: number; capturedAtMS: number } | null>(null);
  const runtimeRecoveryPositionOverrideMSRef = useRef<number | null>(null);
  const clientPlaybackCauseSeqRef = useRef(0);
  const activePlaybackLogCauseRef = useRef<PlaybackLogCause | null>(null);
  const hlsGenerationIdRef = useRef<string | null>(null);
  const recoverPlaybackRuntimeRef = useRef<((fallbackMessage: string) => void) | null>(null);
  const nativeHlsBuildRecoveryKeyRef = useRef<string | null>(null);
  const nativeHlsLowQualitySinceRef = useRef<number | null>(null);
  const nativeHlsLowQualityRecoveryCountRef = useRef(0);
  const nativeHlsLowQualityCooldownUntilRef = useRef(0);
  const trickplayFetchInFlightRef = useRef<Promise<void> | null>(null);
  const trickplayRetryAfterRef = useRef(0);
  const maxObservedPositionMSRef = useRef(0);
  const serverCheckpointPositionMSRef = useRef(0);
  const lastAcceptedTimelineSampleRef = useRef<TimelineSample | null>(null);
  const nativeHlsPositionRestoreListenerRef = useRef<(() => void) | null>(null);
  const pendingNativeHlsPositionRestoreRef = useRef<VerifiedNativeHlsDeliveryReload | null>(null);
  const nativeHlsInitialPositionPendingRef = useRef(false);
  const nativeHlsInitialPositionSettledTargetRef = useRef<number | null>(null);
  const pendingResumeAuthorityRef = useRef<PendingResumeAuthority | null>(null);
  const acceptedResumeAuthorityRef = useRef<{
    sessionId: string;
    tuple: AuthoritativeResumeTuple;
    generationSequence: number | null;
  } | null>(null);
  const mediaElementReloadNonceRef = useRef(0);
  const observedItemIdRef = useRef<string | null>(null);
  const storedPlaybackVolume = useMemo(() => readStoredPlaybackVolume(), []);

  const [controlsVisible, setControlsVisible] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [playerError, setPlayerError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [qualitySwitching, setQualitySwitching] = useState(false);
  const [scrubbing, setScrubbing] = useState(false);
  const [scrubTime, setScrubTime] = useState<number | null>(null);
  const [seekSwitching, setSeekSwitching] = useState(false);
  // A seek into buffered media completes in tens of milliseconds; the
  // buffering overlay and the video fade are held back for the indicator
  // delay so a quick jump stays a quick jump (#992).
  const [bufferedSeekGrace, setBufferedSeekGrace] = useState(false);
  const bufferedSeekGraceTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const [recoveringSession, setRecoveringSession] = useState(false);
  const [awaitingPlaybackStart, setAwaitingPlaybackStart] = useState(true);
  const [readyToStart, setReadyToStart] = useState(false);
  const [startupAutoplayBlocked, setStartupAutoplayBlocked] = useState(false);
  const [seekPreviewTime, setSeekPreviewTime] = useState<number | null>(null);
  const [previewSeeking, setPreviewSeeking] = useState(false);
  const [pendingSeekTime, setPendingSeekTime] = useState<number | null>(null);
  const [currentLevel, setCurrentLevel] = useState<number | null>(null);
  const [observedBandwidthRange, setObservedBandwidthRange] = useState<{ min: number; max: number } | null>(null);
  const [decodedVideoSize, setDecodedVideoSize] = useState<{ width: number; height: number } | null>(null);
  const [mediaElementReloadNonce, setMediaElementReloadNonce] = useState(0);
  mediaElementReloadNonceRef.current = mediaElementReloadNonce;
  // The timeline origin accepted for the media element built by reload
  // `instance`, and the generation it came from. Held so a later generation's
  // origin cannot be applied to an element whose clock is already established
  // (#846). instance starts unmatched so the first render uses the server's
  // value directly rather than a placeholder zero.
  const [elementTimelineOrigin, setElementTimelineOrigin] = useState<{
    instance: number;
    generationId: string | null;
    offsetSeconds: number;
  }>({
    instance: -1,
    generationId: null,
    offsetSeconds: 0,
  });
  const [bufferedAheadSeconds, setBufferedAheadSeconds] = useState(0);
  const [seekHoverTime, setSeekHoverTime] = useState<number | null>(null);
  const [seekHoverOffsetPx, setSeekHoverOffsetPx] = useState<number | null>(null);
  const [seekRailWidthPx, setSeekRailWidthPx] = useState(0);
  const [trickplayTrack, setTrickplayTrack] = useState<ReturnType<typeof parseTrickplayImagePlaylist> | null>(null);
  const [trickplayRetryTick, setTrickplayRetryTick] = useState(0);
  const [copyDebugStatus, setCopyDebugStatus] = useState<"idle" | "copied" | "failed">("idle");
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportStatus, setReportStatus] = useState<"idle" | "sending" | "sent" | "downloaded" | "failed">("idle");
  const buildPlaybackProblemBundle = useCallback(() => ({
    session_id: sessionId,
    captured_at: new Date().toISOString(),
    entries: clientPlaybackLogRingRef.current.slice(-CLIENT_PLAYBACK_LOG_RING_MAX),
  }), [sessionId]);
  const submitPlaybackProblemReport = useCallback(async () => {
    if (!sessionId) {
      return;
    }
    setReportStatus("sending");
    try {
      // One-shot, user-consented upload of the local client-log ring (the logging
      // period). Privacy-first: only sent because the user confirmed the dialog.
      await reportPlaybackProblem(sessionId, { entries: buildPlaybackProblemBundle().entries });
      setReportStatus("sent");
      setReportDialogOpen(false);
    } catch {
      setReportStatus("failed");
    }
  }, [sessionId, buildPlaybackProblemBundle]);
  // Local download: same bundle, but nothing is sent — the user can inspect it or
  // hand it to a developer. Honors privacy-first (no transmission).
  const downloadPlaybackProblemReport = useCallback(() => {
    try {
      const blob = new Blob([JSON.stringify(buildPlaybackProblemBundle(), null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `knotfin-playback-${sessionId || "session"}-${Date.now()}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setReportStatus("downloaded");
      setReportDialogOpen(false);
    } catch {
      setReportStatus("failed");
    }
  }, [sessionId, buildPlaybackProblemBundle]);
  const [selectedSubtitleId, setSelectedSubtitleId] = useState<string | null>(null);
  const selectedSubtitleIdRef = useRef<string | null>(null);
  const availableSubtitleTracksRef = useRef<DeliverableSubtitleTrack[]>([]);
  const masterPlaylistSubtitlesRef = useRef(false);
  const [audioTracks, setAudioTracks] = useState<AudioTrackOption[]>([]);
  const [selectedAudioTrackIndex, setSelectedAudioTrackIndex] = useState(-1);
  const [selectedAudioTrackId, setSelectedAudioTrackId] = useState<string | null>(null);
  const selectedAudioTrackIdRef = useRef<string | null>(null);
  const deliveryAudioTracksRef = useRef<PlaybackAudioTrack[]>([]);
  const [volumeLevel, setVolumeLevel] = useState(storedPlaybackVolume.volume);
  const [muted, setMuted] = useState(storedPlaybackVolume.muted || storedPlaybackVolume.volume <= 0);
  const [isFullscreen, setIsFullscreen] = useState(() => Boolean(document.fullscreenElement));

  useEffect(() => {
    const syncFullscreen = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () => document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);
  const [seriesEpisodes, setSeriesEpisodes] = useState<Item[]>([]);
  const [playbackEnded, setPlaybackEnded] = useState(false);
  const [watchUpdating, setWatchUpdating] = useState(false);
  const [watchMessage, setWatchMessage] = useState<string | null>(null);
  const [watchError, setWatchError] = useState<string | null>(null);
  const [nextEpisodeAutoplayRemainingSeconds, setNextEpisodeAutoplayRemainingSeconds] = useState(
    NEXT_EPISODE_AUTOPLAY_DELAY_SECONDS,
  );
  const [seekDebugSnapshot, setSeekDebugSnapshot] = useState<SeekDebugSnapshot | null>(null);
  const staleStartupStatusRef = useRef({
    playerError: null as string | null,
    readyToStart: false,
    recoveringSession: false,
  });
  staleStartupStatusRef.current = { playerError, readyToStart, recoveringSession };
  const buildFreshness = useClientBuildFreshness();

  const session = useApi(
    () => getPlaybackSession(sessionId!, "item"),
    [sessionId],
  );
  const refetchSession = session.refetch;

  const sessionDocument = session.data?.data ?? null;
  const currentSessionResource = sessionDocument?.id === sessionId ? sessionDocument : null;
  const currentSessionResourceId = currentSessionResource?.id ?? null;
  activeSessionStoppedRef.current = currentSessionResource?.attributes.state === "stopped";
  const seekDebugEnabled = useMemo(
    () => new URLSearchParams(location.search).get("seek_debug") === "1",
    [location.search],
  );
  const delivery = currentSessionResource?.attributes;
  const item = currentSessionResource?.relationships.item.data;
  const currentItem = item && "attributes" in item ? item : null;
  const itemId = item?.id;
  const watchStates = useApi(
    () => getWatchStates(itemId ? [itemId] : []),
    [itemId ?? ""],
  );
  const refetchWatchStates = watchStates.refetch;
  const currentWatchState = watchStates.data?.data.find((state) => state.attributes.item_id === itemId) ?? null;
  const currentItemWatched = currentWatchState?.attributes.watched === true;
  const canToggleCurrentItemWatched = !!itemId
    && (currentWatchState?.attributes.can_mark_watched ?? (currentItem?.attributes.duration_ms ?? 0) > 0)
    && !watchStates.loading;
  const itemTitle = currentItem?.attributes.title ?? "KnotFin";
  const currentItemType = currentItem?.attributes.item_type ?? null;
  const currentItemSeriesID = currentItem?.attributes.series_id?.trim() ?? "";
  const currentItemSeriesTitle = currentItem?.attributes.series_title?.trim() ?? "";
  const trickplayPlaylistPath = delivery?.image_preview_playlist_path ?? null;
  const sessionDuration = delivery?.duration_ms && delivery.duration_ms > 0
    ? delivery.duration_ms / 1000
    : 0;
  const itemDuration = item && "attributes" in item && item.attributes.duration_ms
    ? item.attributes.duration_ms / 1000
    : 0;
  const knownDuration = sessionDuration > 0 ? sessionDuration : itemDuration;
  const conservativeStartup = delivery?.conservative_streaming ?? true;
  const deliverySelectedAudioTrackId = delivery?.selected_audio_track_id ?? null;
  deliveryAudioTracksRef.current = delivery?.available_audio_tracks ?? [];
  const currentQuality = getQualityOptionId(normalizeRequestedBitrateCap(delivery?.requested_max_video_bitrate));
  const selectedMaxVideoBitrate = maxVideoBitrateForQualityOption(currentQuality);
  const sessionBaselineSeconds = playbackSessionBaselineSeconds(currentSessionResource?.attributes);
  const reportedMediaTimelineOriginSeconds = playbackSessionTimelineOriginSeconds(currentSessionResource?.attributes);
  const acceptedResumeCandidate = acceptedResumeAuthorityRef.current?.sessionId === currentSessionResourceId
    ? acceptedResumeAuthorityRef.current
    : null;
  const reportedGenerationId = delivery?.hls_generation_id?.trim() ?? null;
  const reportedGenerationSequence = delivery?.hls_generation_sequence ?? null;
  const acceptedResumeAuthorityForSession = acceptedResumeCandidate
    && (
      reportedGenerationId !== acceptedResumeCandidate.tuple.nextGenerationId
      || reportedMediaTimelineOriginSeconds !== acceptedResumeCandidate.tuple.originSeconds
    )
    && !(
      acceptedResumeCandidate.generationSequence !== null
      && reportedGenerationSequence !== null
      && reportedGenerationSequence > acceptedResumeCandidate.generationSequence
    )
      ? acceptedResumeCandidate
      : null;
  if (acceptedResumeCandidate && !acceptedResumeAuthorityForSession) {
    // Canonical session metadata either caught up with the resume response or
    // advanced beyond it. The response override has served its purpose.
    acceptedResumeAuthorityRef.current = null;
  }
  const nativeHlsDelivery = isNativeHlsClientProfile(delivery?.client_profile_id);
  const hlsJsFullTitleDelivery = delivery?.protocol === "hls" && !nativeHlsDelivery;
  const [runtimeTimeline, setRuntimeTimeline] = useState<{ sessionId: string | null; offsetSeconds: number }>({
    sessionId: null,
    offsetSeconds: 0,
  });
  const [runtimeMediaTimeline, setRuntimeMediaTimeline] = useState<{ sessionId: string | null; originSeconds: number }>({
    sessionId: null,
    originSeconds: 0,
  });
  const sessionStartSeconds = runtimeTimeline.sessionId === currentSessionResourceId
    ? runtimeTimeline.offsetSeconds
    : sessionBaselineSeconds;
  const mediaTimelineOriginSeconds = runtimeMediaTimeline.sessionId === currentSessionResourceId
    ? runtimeMediaTimeline.originSeconds
    : reportedMediaTimelineOriginSeconds;
  // Browser HLS generations expose an anchor-relative media clock. The server
  // reports the source anchor that maps to media time zero; both hls.js and
  // native WebKit add it back when reporting the absolute title position (#861).
  //
  // That anchor describes the element the runtime built for it, and only that
  // element. Once fragments are appended the element's clock is fixed for its
  // lifetime, so adopting a later generation's anchor onto it adds the anchor a
  // second time: the reported position leaps by the anchor delta while
  // video.currentTime keeps running on the original clock, and the client then
  // prewarms segments minutes past its real playhead until the playhead starves
  // and hls.js seeks over the hole it created (#846). The origin is therefore
  // pinned per element instance on the hls.js path; a generation that changes it
  // rebuilds the element instead, resuming at the live playhead (#873). Native
  // WebKit rebases through adoptVerifiedNativeHlsTimeline and is not pinned here.
  const pinnedTimelineOrigin = hlsJsFullTitleDelivery
    && elementTimelineOrigin.instance === mediaElementReloadNonce
    ? elementTimelineOrigin.offsetSeconds
    : mediaTimelineOriginSeconds;
  const mediaTimelineOffsetSeconds = delivery?.protocol === "hls"
    ? pinnedTimelineOrigin
    : sessionStartSeconds;
  const effectiveDuration = knownDuration > 0
    ? knownDuration
    : duration + mediaTimelineOffsetSeconds;
  const sessionAtEnd = effectiveDuration > 0 && sessionStartSeconds >= effectiveDuration - 0.25;
  activePlaybackSessionIdRef.current = sessionId ?? null;
  currentSessionResourceIdRef.current = currentSessionResourceId ?? null;
  currentTimeRef.current = currentTime;
  effectiveDurationRef.current = effectiveDuration;
  sessionStartSecondsRef.current = sessionStartSeconds;
  mediaTimelineOffsetSecondsRef.current = mediaTimelineOffsetSeconds;
  sessionAtEndRef.current = sessionAtEnd;
  sessionBaselineSecondsRef.current = sessionBaselineSeconds;
  serverCheckpointPositionMSRef.current = currentSessionResource?.attributes.position_ms ?? 0;
  conservativeStartupRef.current = conservativeStartup;
  nativeHlsDeliveryRef.current = nativeHlsDelivery;
  awaitingPlaybackStartRef.current = awaitingPlaybackStart;
  segmentDurationSecondsRef.current = delivery?.segment_duration_seconds ?? 0;
  deliverySelectedAudioTrackIdRef.current = deliverySelectedAudioTrackId;
  requestedMaxVideoBitrateRef.current = selectedMaxVideoBitrate;

  useEffect(() => {
    return () => {
      activePlaybackSessionIdRef.current = null;
    };
  }, []);

  const availableSubtitleTracks = (delivery?.available_subtitles ?? []).filter(
    (track): track is DeliverableSubtitleTrack =>
      track.attributes.delivery === "sidecar_text" &&
      (track.attributes.status ?? "ready") === "ready" &&
      typeof track.attributes.url === "string",
  );
  const preparingSubtitleTracks = (delivery?.available_subtitles ?? []).filter(
    (track) => track.attributes.delivery === "sidecar_text" && track.attributes.status === "preparing",
  );
  const showSubtitleControl = availableSubtitleTracks.length > 0 || preparingSubtitleTracks.length > 0;
  // In HLS master-playlist mode the API injects EXT-X-MEDIA:TYPE=SUBTITLES
  // renditions into the manifest and hls.js owns subtitle track loading.
  const masterPlaylistSubtitles = delivery?.playlist_mode === "master";
  const inlineSubtitleTracks = masterPlaylistSubtitles ? [] : availableSubtitleTracks;
  availableSubtitleTracksRef.current = availableSubtitleTracks;
  masterPlaylistSubtitlesRef.current = masterPlaylistSubtitles;
  const selectedSubtitleTrack = availableSubtitleTracks.find((track) => track.id === selectedSubtitleId) ?? null;
  const subtitleButtonTitle = availableSubtitleTracks.length === 0
    ? preparingSubtitleTracks.length > 0
      ? "Preparing embedded subtitles"
      : delivery?.subtitle_delivery === "burn_in_required"
      ? "Image subtitles require burn-in and are not selectable yet"
      : delivery?.subtitle_delivery === "unsupported"
        ? "This source has subtitles that are unsupported by this playback path"
        : "No subtitles available"
    : selectedSubtitleTrack
      ? `Subtitles: ${selectedSubtitleTrack.attributes.label}`
      : "Subtitles off";
  const displayedCurrentTime =
    seekPreviewTime ??
    (scrubbing && scrubTime !== null ? scrubTime : null) ??
    seekHoverTime ??
    pendingSeekTime ??
    currentTime;
  const nextEpisode = useMemo(
    () => findNextEpisode(currentItem, seriesEpisodes),
    [currentItem, seriesEpisodes],
  );
  const handleToggleCurrentItemWatched = useCallback(async () => {
    if (!itemId || !sessionId || !canToggleCurrentItemWatched || watchUpdating) {
      return;
    }
    setWatchUpdating(true);
    setWatchMessage(null);
    setWatchError(null);
    try {
      if (currentItemWatched) {
        await clearItemPlaybackState(itemId, { preserveSessionId: sessionId });
        setWatchMessage("Marked as unplayed");
      } else {
        await markItemPlayed(itemId, { preserveSessionId: sessionId });
        setWatchMessage("Marked as played");
      }
      await refetchWatchStates();
      void refetchSession().catch(() => {});
    } catch {
      setWatchError(currentItemWatched ? "Failed to mark as unplayed" : "Failed to mark as played");
    } finally {
      setWatchUpdating(false);
    }
  }, [
    canToggleCurrentItemWatched,
    currentItemWatched,
    itemId,
    refetchSession,
    refetchWatchStates,
    sessionId,
    watchUpdating,
  ]);

  useEffect(() => {
    if (!currentSessionResourceId) {
      setRuntimeTimeline((previous) => (
        previous.sessionId === null && previous.offsetSeconds === 0
          ? previous
          : { sessionId: null, offsetSeconds: 0 }
      ));
      return;
    }

    setRuntimeTimeline((previous) => (
      previous.sessionId === currentSessionResourceId
        ? previous
        : { sessionId: currentSessionResourceId, offsetSeconds: sessionBaselineSeconds }
    ));
  }, [currentSessionResourceId, sessionBaselineSeconds]);

  useEffect(() => {
    const accepted = acceptedResumeAuthorityForSession;
    if (
      accepted?.sessionId === currentSessionResourceId
      && (
        delivery?.hls_generation_id?.trim() !== accepted.tuple.nextGenerationId
        || reportedMediaTimelineOriginSeconds !== accepted.tuple.originSeconds
      )
    ) {
      // A normal session refetch can remain behind the resume response for one
      // or more polling intervals. It is not allowed to overwrite the tuple
      // already used to attach the replacement generation (#873).
      return;
    }
    setRuntimeMediaTimeline((previous) => (
      previous.sessionId === currentSessionResourceId
        && previous.originSeconds === reportedMediaTimelineOriginSeconds
        ? previous
        : {
            sessionId: currentSessionResourceId ?? null,
            originSeconds: reportedMediaTimelineOriginSeconds,
          }
    ));
  }, [
    acceptedResumeAuthorityForSession,
    currentSessionResourceId,
    delivery?.hls_generation_id,
    reportedMediaTimelineOriginSeconds,
  ]);

  const adoptVerifiedNativeHlsTimeline = useCallback((
    sourceSessionId: string,
    targetAbsoluteSeconds: number,
    timelineOriginSeconds: number,
  ) => {
    if (!Number.isFinite(targetAbsoluteSeconds) || targetAbsoluteSeconds < 0) {
      return;
    }
    const offsetSeconds = Math.max(0, targetAbsoluteSeconds);
    const originSeconds = Number.isFinite(timelineOriginSeconds)
      ? Math.max(0, timelineOriginSeconds)
      : offsetSeconds;
    // A same-session generation replacement gives WebKit a new relative
    // media clock. Publish the accepted absolute origin synchronously through
    // the refs used by media events, then through React state for the reload
    // render. Keeping the old same-session offset turns relative zero into an
    // apparent rewind to the beginning (#691).
    sessionStartSecondsRef.current = offsetSeconds;
    mediaTimelineOffsetSecondsRef.current = originSeconds;
    sessionBaselineSecondsRef.current = offsetSeconds;
    setRuntimeTimeline((previous) => (
      previous.sessionId === sourceSessionId && previous.offsetSeconds === offsetSeconds
        ? previous
        : { sessionId: sourceSessionId, offsetSeconds }
    ));
    setRuntimeMediaTimeline((previous) => (
      previous.sessionId === sourceSessionId && previous.originSeconds === originSeconds
        ? previous
        : { sessionId: sourceSessionId, originSeconds }
    ));
  }, []);

  useEffect(() => {
    if (!itemId) {
      return;
    }
    if (subtitleItemIdRef.current === null) {
      subtitleItemIdRef.current = itemId;
      return;
    }
    if (subtitleItemIdRef.current !== itemId) {
      subtitleItemIdRef.current = itemId;
      setSelectedSubtitleId(null);
    }
  }, [itemId]);

  useEffect(() => {
    const serverSelectedSubtitleId = delivery?.selected_subtitle_id?.trim() || null;
    setSelectedSubtitleId(serverSelectedSubtitleId);
  }, [currentSessionResourceId, delivery?.selected_subtitle_id]);

  const episodeOverlaySeriesTitle = currentItem?.attributes.item_type === "episode"
    ? (currentItem.attributes.series_title ?? currentItem.attributes.title)
    : null;
  const episodeOverlayTitle = currentItem?.attributes.item_type === "episode"
    ? (currentItem.attributes.episode_title ?? currentItem.attributes.title)
    : null;
  const episodeOverlayMeta = currentItem?.attributes.item_type === "episode"
    ? [
        currentItem.attributes.season_title ?? null,
        formatEpisodeOverlayCode(currentItem),
      ].filter(Boolean).join(" • ")
    : null;

  const setPreviewTime = useCallback((nextTime: number | null) => {
    seekPreviewTimeRef.current = nextTime;
    setSeekPreviewTime(nextTime);
  }, []);

  const updateTimelinePreviewGeometryForTime = useCallback((timeSeconds: number) => {
    const target = seekWrapRef.current;
    if (!target || effectiveDuration <= 0) {
      return;
    }
    const rect = target.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, timeSeconds / Math.max(1, effectiveDuration)));
    setSeekRailWidthPx(rect.width);
    setSeekHoverOffsetPx(ratio * rect.width);
  }, [effectiveDuration]);

  const clearPreviewSeekRepeat = useCallback(() => {
    if (previewSeekRepeatTimerRef.current !== null) {
      window.clearInterval(previewSeekRepeatTimerRef.current);
      previewSeekRepeatTimerRef.current = null;
    }
  }, []);

  const clearKeyboardSeekRepeat = useCallback(() => {
    if (keyboardSeekRepeatTimerRef.current !== null) {
      window.clearInterval(keyboardSeekRepeatTimerRef.current);
      keyboardSeekRepeatTimerRef.current = null;
    }
  }, []);

  const clearKeyboardSeekCommit = useCallback(() => {
    if (keyboardSeekCommitTimerRef.current !== null) {
      window.clearTimeout(keyboardSeekCommitTimerRef.current);
      keyboardSeekCommitTimerRef.current = null;
    }
  }, []);

  const clearSeekIntentResultTimer = useCallback(() => {
    if (seekIntentResultTimerRef.current !== null) {
      window.clearTimeout(seekIntentResultTimerRef.current);
      seekIntentResultTimerRef.current = null;
    }
  }, []);

  const setPendingSeek = useCallback((nextTime: number | null, sourceTime: number | null = null) => {
    pendingSeekTimeRef.current = nextTime;
    pendingSeekSourceTimeRef.current = nextTime === null || !Number.isFinite(sourceTime ?? Number.NaN)
      ? null
      : sourceTime;
    setPendingSeekTime(nextTime);
  }, []);

  const setSeekInteractionValue = useCallback((nextTime: number | null) => {
    seekInteractionValueRef.current = nextTime;
  }, []);

  const clearNativeSeekSettleTimer = useCallback(() => {
    if (nativeSeekSettleTimerRef.current !== null) {
      window.clearTimeout(nativeSeekSettleTimerRef.current);
      nativeSeekSettleTimerRef.current = null;
    }
  }, []);

  const releaseNativeSeekInFlight = useCallback((clearPending = false) => {
    if (!nativeSeekInFlightRef.current) {
      return;
    }
    nativeSeekInFlightRef.current = false;
    nativeSeekTargetSecondsRef.current = null;
    clearNativeSeekSettleTimer();
    if (clearPending) {
      setPendingSeek(null);
    }
  }, [clearNativeSeekSettleTimer, setPendingSeek]);

  const keepNativeSeekWaitingForTarget = useCallback((targetSeconds: number) => {
    if (!nativeHlsPlaybackRef.current) {
      return false;
    }
    const video = videoRef.current;
    if (video && nativeHlsSeekTargetReady(video, targetSeconds, mediaTimelineOffsetSecondsRef.current)) {
      return false;
    }
    if (video && !video.paused && !previewSeekingRef.current) {
      setBuffering(true);
      setAwaitingPlaybackStart(true);
      setReadyToStart(false);
    }
    return true;
  }, []);

  const scheduleNativeSeekSettleTimer = useCallback((targetSeconds: number) => {
    clearNativeSeekSettleTimer();
    nativeSeekSettleTimerRef.current = window.setTimeout(() => {
      nativeSeekSettleTimerRef.current = null;
      if (!nativeSeekInFlightRef.current || nativeSeekTargetSecondsRef.current !== targetSeconds) {
        return;
      }
      queueClientPlaybackLogRef.current("native_hls_seek_settle_timeout", "media_event", {
        flushImmediately: true,
        flushReason: "native_hls_seek_timeout",
      });
      if (!keepNativeSeekWaitingForTarget(targetSeconds)) {
        releaseNativeSeekInFlight(true);
        setBuffering(false);
        setAwaitingPlaybackStart(false);
        return;
      }
      failedSeekTargetRef.current = targetSeconds;
      nativeSeekInFlightRef.current = false;
      nativeSeekTargetSecondsRef.current = null;
      setPendingSeek(null);
      setBuffering(false);
      setAwaitingPlaybackStart(false);
      setReadyToStart(false);
      setPlayerError("Timed out waiting for media at that point.");
    }, NATIVE_HLS_SEEK_SETTLE_TIMEOUT_MS);
  }, [clearNativeSeekSettleTimer, keepNativeSeekWaitingForTarget, releaseNativeSeekInFlight, setPendingSeek]);

  const markNativeSeekInFlight = useCallback((targetSeconds: number) => {
    nativeSeekInFlightRef.current = true;
    nativeSeekTargetSecondsRef.current = targetSeconds;
    scheduleNativeSeekSettleTimer(targetSeconds);
  }, [scheduleNativeSeekSettleTimer]);

  const releaseNativeSeekIfReached = useCallback((absoluteTime: number) => {
    const target = nativeSeekTargetSecondsRef.current;
    if (
      nativeSeekInFlightRef.current &&
      target !== null &&
      seekTargetReached(absoluteTime, target, pendingSeekSourceTimeRef.current)
    ) {
      if (keepNativeSeekWaitingForTarget(target)) {
        return;
      }
      releaseNativeSeekInFlight(true);
    }
  }, [keepNativeSeekWaitingForTarget, releaseNativeSeekInFlight]);

  const clearPendingSeekIfReached = useCallback((absoluteTime: number) => {
    const target = pendingSeekTimeRef.current;
    if (
      pendingResumeAuthorityRef.current
      && target === pendingResumeAuthorityRef.current.tuple.targetAbsoluteSeconds
    ) {
      // Only the resume authority controller may clear this intent. Ordinary
      // timeupdate/timeout logic cannot waive a position it has not verified.
      return;
    }
    if (target !== null && seekTargetReached(absoluteTime, target, pendingSeekSourceTimeRef.current)) {
      if (
        nativeSeekInFlightRef.current &&
        nativeSeekTargetSecondsRef.current !== null &&
        Math.abs(nativeSeekTargetSecondsRef.current - target) <= SEEK_TARGET_REACHED_TOLERANCE_SECONDS &&
        keepNativeSeekWaitingForTarget(nativeSeekTargetSecondsRef.current)
      ) {
        return;
      }
      setPendingSeek(null);
    }
  }, [keepNativeSeekWaitingForTarget, setPendingSeek]);

  const loadTrickplayTrack = useCallback(async (force = false) => {
    if (!trickplayPlaylistPath) {
      return;
    }
    if (trickplayTrack && !force) {
      return;
    }
    const now = Date.now();
    if (now < trickplayRetryAfterRef.current) {
      return;
    }
    if (trickplayFetchInFlightRef.current) {
      await trickplayFetchInFlightRef.current;
      return;
    }

    const request = (async () => {
      try {
        const response = await fetch(trickplayPlaylistPath, {
          headers: {
            Accept: "application/vnd.apple.mpegurl, text/plain;q=0.9, */*;q=0.1",
          },
          credentials: "same-origin",
        });
        if (!response.ok) {
          const retryAfter = parseRetryAfterSeconds(response.headers?.get("Retry-After"));
          trickplayRetryAfterRef.current = Date.now() + (retryAfter ?? 30) * 1000;
          return;
        }
        const manifest = await response.text();
        const manifestURL = new URL(trickplayPlaylistPath, window.location.origin).toString();
        const nextTrack = parseTrickplayImagePlaylist(manifest, manifestURL);
        setTrickplayTrack(nextTrack);
        trickplayRetryAfterRef.current = nextTrack.placeholder
          ? Date.now() + TRICKPLAY_PENDING_RETRY_MS
          : 0;
      } catch {
        trickplayRetryAfterRef.current = Date.now() + 5_000;
      } finally {
        trickplayFetchInFlightRef.current = null;
      }
    })();

    trickplayFetchInFlightRef.current = request;
    await request;
  }, [trickplayPlaylistPath, trickplayTrack]);

  const getAbsoluteVideoTime = useCallback((video: HTMLVideoElement | null | undefined) => {
    if (!video || !Number.isFinite(video.currentTime)) {
      return clampPlaybackSeconds(sessionStartSeconds, effectiveDuration);
    }
    return clampPlaybackSeconds(mediaTimelineOffsetSeconds + Math.max(0, video.currentTime), effectiveDuration);
  }, [effectiveDuration, mediaTimelineOffsetSeconds, sessionStartSeconds]);

  const toRelativeVideoTime = useCallback((absoluteSeconds: number) => {
    return Math.max(0, absoluteSeconds - mediaTimelineOffsetSeconds);
  }, [mediaTimelineOffsetSeconds]);

  const recordObservedPositionMS = useCallback((positionMS: number) => {
    if (!Number.isFinite(positionMS) || positionMS < 0) {
      return;
    }
    const nextSample: TimelineSample = {
      offsetMS: Math.floor(mediaTimelineOffsetSecondsRef.current * 1000),
      absoluteMS: Math.floor(positionMS),
    };
    const previousSample = lastAcceptedTimelineSampleRef.current;
    // A commanded seek is allowed to move the anchor and the position together;
    // only an uncommanded re-anchor can double-count (#846).
    const commanded = pendingSeekTimeRef.current !== null
      || runtimeRenegotiatingRef.current
      || videoRef.current?.seeking === true;
    if (
      previousSample
      && !commanded
      && reanchorDoubleCountsElapsed(previousSample, nextSample, CLIENT_TIMELINE_FORWARD_JUMP_TOLERANCE_MS)
    ) {
      // The anchor moved but the media element has not rebased, so this sample
      // counts the elapsed span twice. Refusing it here is what keeps a
      // transient from becoming permanent: stableSessionPositionMS only ever
      // raises this mark, so anything latched now survives every later
      // correction and compounds on the next restart (#852).
      return;
    }
    lastAcceptedTimelineSampleRef.current = nextSample;
    maxObservedPositionMSRef.current = Math.max(maxObservedPositionMSRef.current, nextSample.absoluteMS);
  }, []);

  const resetObservedPositionMS = useCallback((positionMS: number) => {
    lastAcceptedTimelineSampleRef.current = null;
    maxObservedPositionMSRef.current = Math.max(0, Math.floor(positionMS));
  }, []);

  useEffect(() => {
    const nextGenerationId = delivery?.hls_generation_id?.trim() || null;
    const accepted = acceptedResumeAuthorityForSession;
    if (
      accepted?.sessionId === currentSessionResourceIdRef.current
      && nextGenerationId !== accepted.tuple.nextGenerationId
    ) {
      return;
    }
    const previousGenerationId = hlsGenerationIdRef.current;
    hlsGenerationIdRef.current = nextGenerationId;
    // A generation replacement hands the media element a new clock, and during
    // the swap the offset can advance before video.currentTime rebases. Because
    // stableSessionPositionMS only ever raises maxObservedPositionMS, carrying
    // the old mark across the swap latches that transient permanently and every
    // later restart compounds it. Re-seed from the server's checkpoint, which
    // is the last position the server actually accepted (#852).
    if (
      previousGenerationId
      && nextGenerationId
      && previousGenerationId !== nextGenerationId
    ) {
      resetObservedPositionMS(serverCheckpointPositionMSRef.current);
    }
  }, [acceptedResumeAuthorityForSession, delivery?.hls_generation_id, resetObservedPositionMS]);

  const beginPlaybackLogCause = useCallback((event: string): PlaybackLogCause => {
    const cause = {
      id: `pcause_${++clientPlaybackCauseSeqRef.current}`,
      event,
    };
    activePlaybackLogCauseRef.current = cause;
    return cause;
  }, []);

  const stableSessionPositionMS = useCallback((positionMS: number) => {
    return Math.max(
      Math.floor(positionMS),
      maxObservedPositionMSRef.current,
      currentSessionResource?.attributes.position_ms ?? 0,
    );
  }, [currentSessionResource?.attributes.position_ms]);

  const clearNativeHlsPositionRestoreListener = useCallback(() => {
    const video = videoRef.current;
    const handler = nativeHlsPositionRestoreListenerRef.current;
    if (video && handler) {
      video.removeEventListener("loadedmetadata", handler);
      video.removeEventListener("durationchange", handler);
      video.removeEventListener("progress", handler);
      video.removeEventListener("canplay", handler);
      video.removeEventListener("seeked", handler);
      video.removeEventListener("timeupdate", handler);
    }
    nativeHlsPositionRestoreListenerRef.current = null;
  }, []);

  // Native-HLS currentTime writes are intentionally gated by a typed proof
  // that the server changed delivery generations. A timeline anomaly alone
  // must never reach this function (#273, #401, #785).
  const applyVerifiedNativeHlsPositionRestore = useCallback(() => {
    const video = videoRef.current;
    const request = pendingNativeHlsPositionRestoreRef.current;
    if (!video || !request) {
      return;
    }
    const attemptSeek = () => {
      const currentVideo = videoRef.current;
      const currentRequest = pendingNativeHlsPositionRestoreRef.current;
      if (!currentVideo || !currentRequest) {
        return;
      }
      const timelineOffsetSeconds = sessionStartSecondsRef.current;
      const seekableRanges = Array.from({ length: currentVideo.seekable.length }, (_unused, index) => ({
        startSeconds: timelineOffsetSeconds + Math.max(0, currentVideo.seekable.start(index)),
        endSeconds: timelineOffsetSeconds + Math.max(0, currentVideo.seekable.end(index)),
      }));
      // WebKitGTK's native live-HLS element can report an empty TimeRanges
      // object even after canplay. The response tuple still proves that the
      // target lies in this generation's first ready segment. Treat only that
      // bounded segment as seekable; readiness events are observations, not
      // authority, and cannot create this range without the verified reload.
      if (
        seekableRanges.length === 0
        && currentVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
        && segmentDurationSecondsRef.current > 0
      ) {
        const originSeconds = mediaTimelineOffsetSecondsRef.current;
        seekableRanges.push({
          startSeconds: originSeconds,
          endSeconds: originSeconds + segmentDurationSecondsRef.current,
        });
      }
      const decision = evaluateNativeHlsPositionRestore({
        request: currentRequest,
        currentAbsoluteSeconds: getAbsoluteVideoTime(currentVideo),
        durationSeconds: effectiveDurationRef.current,
        seekableRanges,
      });
      if (decision.action === "wait_for_seekable_target") {
        return;
      }
      if (decision.action === "none") {
        clearNativeHlsPositionRestoreListener();
        pendingNativeHlsPositionRestoreRef.current = null;
        requestStartupPlaybackRef.current?.();
        return;
      }
      try {
        currentVideo.currentTime = toRelativeVideoTime(decision.targetSeconds);
        queueClientPlaybackLogRef.current("native_hls_generation_position_assigned", "media_event");
        const assignedAbsoluteSeconds = getAbsoluteVideoTime(currentVideo);
        if (Math.abs(assignedAbsoluteSeconds - decision.targetSeconds) <= 0.5) {
          clearNativeHlsPositionRestoreListener();
          pendingNativeHlsPositionRestoreRef.current = null;
          queueClientPlaybackLogRef.current("native_hls_generation_position_verified", "media_event");
          requestStartupPlaybackRef.current?.();
        }
      } catch {
        // Retain the verified request. A later readiness event may accept the
        // same bounded write; playback remains closed until corroboration.
      }
    };
    clearNativeHlsPositionRestoreListener();
    const handleReady = () => attemptSeek();
    nativeHlsPositionRestoreListenerRef.current = handleReady;
    video.addEventListener("loadedmetadata", handleReady);
    video.addEventListener("durationchange", handleReady);
    video.addEventListener("progress", handleReady);
    video.addEventListener("canplay", handleReady);
    video.addEventListener("seeked", handleReady);
    video.addEventListener("timeupdate", handleReady);
  }, [clearNativeHlsPositionRestoreListener, getAbsoluteVideoTime, toRelativeVideoTime]);

  const applyVerifiedNativeHlsPositionRestoreRef = useRef(applyVerifiedNativeHlsPositionRestore);
  useEffect(() => {
    applyVerifiedNativeHlsPositionRestoreRef.current = applyVerifiedNativeHlsPositionRestore;
  }, [applyVerifiedNativeHlsPositionRestore]);

  const beginPlaybackSessionSwitch = useCallback((reason: PlaybackSessionSwitchReason) => {
    if (sessionSwitchInProgressRef.current !== null) {
      return null;
    }
    const token = {
      id: ++sessionSwitchSequenceRef.current,
      reason,
    };
    sessionSwitchInProgressRef.current = token;
    return token;
  }, []);

  const releasePlaybackSessionSwitch = useCallback((token: PlaybackSessionSwitchToken) => {
    if (sessionSwitchInProgressRef.current?.id === token.id) {
      sessionSwitchInProgressRef.current = null;
    }
  }, []);

  const playbackSessionSwitchActive = useCallback(() => sessionSwitchInProgressRef.current !== null, []);

  const triggerRuntimeHandoffRecovery = useCallback((fallbackMessage: string, positionOverrideMS?: number) => {
    if (nativeHlsPlaybackRef.current) {
      queueClientPlaybackLogRef.current("native_hls_runtime_recovery_observed", "session");
      reportClientPlaybackStateRef.current?.("native_hls_runtime_recovery_observed");
      // WebKit owns native-HLS stall and timeline recovery. In particular,
      // positionOverrideMS is telemetry evidence, not seek authority (#785).
      return;
    }
    if (
      hlsRestartHandoffTriggeredRef.current
      || !playbackStartedRef.current
      || runtimeRenegotiatingRef.current
      || playbackSessionSwitchActive()
      || seekInFlightRef.current
      || nextEpisodeStartingRef.current
    ) {
      return;
    }
    if (positionOverrideMS !== undefined && Number.isFinite(positionOverrideMS) && positionOverrideMS >= 0) {
      runtimeRecoveryPositionOverrideMSRef.current = Math.floor(positionOverrideMS);
    }
    hlsRestartHandoffTriggeredRef.current = true;
    setBuffering(false);
    setAwaitingPlaybackStart(false);
    setPlayerError(null);
    recoverPlaybackRuntimeRef.current?.(fallbackMessage);
  }, [playbackSessionSwitchActive]);

  const assertActivePlaybackSession = useCallback((sourceSessionId: string) => {
    if (activePlaybackSessionIdRef.current !== sourceSessionId) {
      throw new StalePlaybackHandoffError();
    }
  }, []);

  const applyRecoveredPlaybackSession = useCallback(async (
    sourceSessionId: string,
    nextSession: PlaybackSession,
    switchToken: PlaybackSessionSwitchToken,
    options: ApplyRecoveredPlaybackSessionOptions = {},
  ) => {
    try {
      assertActivePlaybackSession(sourceSessionId);
    } catch (error) {
      await reportPlaybackEvent(nextSession.id, {
        event: "stop",
        position_ms: nextSession.attributes.position_ms,
      }).catch(() => {});
      throw error;
    }

    if (nextSession.id === sourceSessionId) {
      // Capture before refetch can publish the new generation into React
      // state; otherwise the independently observed transition collapses
      // into an apparent same-generation reload.
      const previousGenerationId = hlsGenerationIdRef.current;
      await refetchSession();
      if (options.forceNativeHlsReload && nativeHlsPlaybackRef.current) {
        const verifiedReload = verifiedNativeHlsDeliveryReload({
          previousGenerationId,
          nextGenerationId: nextSession.attributes.hls_generation_id,
          targetAbsoluteSeconds: nextSession.attributes.position_ms / 1000,
        });
        pendingNativeHlsPositionRestoreRef.current = verifiedReload;
        if (verifiedReload) {
          adoptVerifiedNativeHlsTimeline(
            sourceSessionId,
            verifiedReload.targetAbsoluteSeconds,
            playbackSessionTimelineOriginSeconds(nextSession.attributes),
          );
        }
        hlsGenerationIdRef.current = nextSession.attributes.hls_generation_id?.trim() || null;
        resumeAfterSessionSwitchRef.current = true;
        queueClientPlaybackLogRef.current(
          `native_hls_restart_recovery_reload_${options.recoveryReason ?? switchToken.reason}`,
          "session",
          {
            flushImmediately: true,
            flushReason: "native_hls_restart_recovery",
          },
        );
        reportClientPlaybackStateRef.current?.(
          `native_hls_restart_recovery_reload_${options.recoveryReason ?? switchToken.reason}`,
        );
        setMediaElementReloadNonce((value) => value + 1);
      }
      releasePlaybackSessionSwitch(switchToken);
      setRecoveringSession(false);
      setBuffering(false);
      setAwaitingPlaybackStart(true);
      setReadyToStart(false);
      setPlayerError(null);
      return;
    }

    navigate(`/play/${nextSession.id}`, { replace: true });
  }, [
    adoptVerifiedNativeHlsTimeline,
    assertActivePlaybackSession,
    navigate,
    refetchSession,
    releasePlaybackSessionSwitch,
  ]);

  useEffect(() => {
    const baselineMS = Math.floor(sessionBaselineSeconds * 1000);
    if (observedItemIdRef.current !== itemId) {
      observedItemIdRef.current = itemId ?? null;
      observedBandwidthRangeRef.current = null;
      setObservedBandwidthRange(null);
      resetObservedPositionMS(baselineMS);
      return;
    }
    recordObservedPositionMS(baselineMS);
  }, [itemId, recordObservedPositionMS, resetObservedPositionMS, sessionBaselineSeconds]);

  const updateObservedDuration = useCallback((video: HTMLVideoElement | null | undefined) => {
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) {
      return;
    }
    setDuration((previous) => Math.max(previous, video.duration));
  }, []);

  const syncBufferedAhead = useCallback((video: HTMLVideoElement | null | undefined) => {
    if (!video) {
      setBufferedAheadSeconds(0);
      return;
    }
    const absoluteTime = getAbsoluteVideoTime(video);
    setBufferedAheadSeconds(getBufferedAheadSeconds(video, absoluteTime, mediaTimelineOffsetSeconds));
  }, [getAbsoluteVideoTime, mediaTimelineOffsetSeconds]);

  const syncDecodedVideoSize = useCallback((video: HTMLVideoElement | null | undefined) => {
    if (!video) {
      setDecodedVideoSize(null);
      return;
    }
    const width = Number.isFinite(video.videoWidth) ? video.videoWidth : 0;
    const height = Number.isFinite(video.videoHeight) ? video.videoHeight : 0;
    if (width > 0 && height > 0) {
      setDecodedVideoSize((previous) => {
        if (previous && previous.width === width && previous.height === height) {
          return previous;
        }
        return { width, height };
      });
      return;
    }
    setDecodedVideoSize(null);
  }, []);

  const clearPlayerErrorIfPlaybackHealthy = useCallback((video: HTMLVideoElement | null | undefined) => {
    if (!playerError || !video || video.error) {
      return;
    }
    if (!video.paused && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      setPlayerError(null);
    }
  }, [playerError]);

  const syncAudioState = useCallback((video: HTMLVideoElement | null | undefined, persist = true) => {
    if (!video) {
      return;
    }
    const nextVolume = Math.max(0, Math.min(1, Number.isFinite(video.volume) ? video.volume : 1));
    const nextMuted = video.muted || nextVolume <= 0;
    if (nextVolume > 0) {
      lastNonZeroVolumeRef.current = nextVolume;
    }
    setVolumeLevel(nextVolume);
    setMuted(nextMuted);
    if (persist) {
      localStorage.setItem(PLAYBACK_VOLUME_KEY, String(nextVolume));
      localStorage.setItem(PLAYBACK_MUTED_KEY, nextMuted ? "true" : "false");
    }
  }, []);

  const applyAudioState = useCallback((nextVolume: number, nextMuted: boolean, persist = true) => {
    const video = videoRef.current;
    if (!video) {
      setVolumeLevel(nextVolume);
      setMuted(nextMuted || nextVolume <= 0);
      if (persist) {
        localStorage.setItem(PLAYBACK_VOLUME_KEY, String(nextVolume));
        localStorage.setItem(PLAYBACK_MUTED_KEY, nextMuted || nextVolume <= 0 ? "true" : "false");
      }
      return;
    }
    video.volume = Math.max(0, Math.min(1, nextVolume));
    video.muted = nextMuted || nextVolume <= 0;
    syncAudioState(video, persist);
  }, [syncAudioState]);

  useEffect(() => {
    if (volumeLevel > 0) {
      lastNonZeroVolumeRef.current = volumeLevel;
    }
  }, [volumeLevel]);

  useEffect(() => {
    applyAudioState(volumeLevel, muted, false);
  }, [applyAudioState, muted, sessionId, volumeLevel]);

  const clearStartupPlayTimer = useCallback(() => {
    if (startupPlayTimerRef.current !== null) {
      window.clearTimeout(startupPlayTimerRef.current);
      startupPlayTimerRef.current = null;
    }
    if (startupBufferTimerRef.current !== null) {
      window.clearTimeout(startupBufferTimerRef.current);
      startupBufferTimerRef.current = null;
    }
  }, []);

  const clearResumeAuthorityDeadline = useCallback(() => {
    if (resumeAuthorityDeadlineTimerRef.current !== null) {
      window.clearTimeout(resumeAuthorityDeadlineTimerRef.current);
      resumeAuthorityDeadlineTimerRef.current = null;
    }
  }, []);

  const armResumeAuthorityDeadline = useCallback((authority: PendingResumeAuthority) => {
    clearResumeAuthorityDeadline();
    resumeAuthorityFailedRef.current = false;
    resumeAuthorityDeadlineTimerRef.current = window.setTimeout(() => {
      resumeAuthorityDeadlineTimerRef.current = null;
      if (pendingResumeAuthorityRef.current !== authority) {
        return;
      }
      pendingResumeAuthorityRef.current = null;
      resumeAuthorityFailedRef.current = true;
      startupPlayRequestRef.current = false;
      if (startupBufferTimerRef.current !== null) {
        window.clearTimeout(startupBufferTimerRef.current);
        startupBufferTimerRef.current = null;
      }
      clearStartupPlayTimer();
      hlsRef.current?.stopLoad?.();
      videoRef.current?.pause();
      setPendingSeek(null);
      setPlaying(false);
      setBuffering(false);
      setAwaitingPlaybackStart(false);
      setReadyToStart(false);
      setPlayerError("Could not verify the resumed position. Seek to another point or reload playback.");
      queueClientPlaybackLogRef.current("resume_generation_clock_timeout", "media_event", {
        flushImmediately: true,
        flushReason: "resume_generation_clock_timeout",
      });
    }, HLS_RESUME_AUTHORITY_TIMEOUT_MS);
  }, [clearResumeAuthorityDeadline, clearStartupPlayTimer, setPendingSeek]);

  const clearStaleStartupRecoveryTimer = useCallback(() => {
    if (staleStartupRecoveryTimerRef.current !== null) {
      window.clearTimeout(staleStartupRecoveryTimerRef.current);
      staleStartupRecoveryTimerRef.current = null;
    }
  }, []);

  const clearAwaitingPlaybackStartTimeoutTimer = useCallback(() => {
    if (awaitingPlaybackStartTimeoutTimerRef.current !== null) {
      window.clearTimeout(awaitingPlaybackStartTimeoutTimerRef.current);
      awaitingPlaybackStartTimeoutTimerRef.current = null;
    }
  }, []);

  const clearBufferingIndicatorTimer = useCallback(() => {
    if (bufferingIndicatorTimerRef.current !== null) {
      window.clearTimeout(bufferingIndicatorTimerRef.current);
      bufferingIndicatorTimerRef.current = null;
    }
  }, []);

  const clearNextEpisodeAutoplayTimer = useCallback(() => {
    if (nextEpisodeAutoplayTimerRef.current !== null) {
      window.clearTimeout(nextEpisodeAutoplayTimerRef.current);
      nextEpisodeAutoplayTimerRef.current = null;
    }
    if (nextEpisodeAutoplayCountdownRef.current !== null) {
      window.clearInterval(nextEpisodeAutoplayCountdownRef.current);
      nextEpisodeAutoplayCountdownRef.current = null;
    }
  }, []);

  const releaseStartupWaitState = useCallback((video: HTMLVideoElement | null | undefined) => {
    clearStartupPlayTimer();
    clearBufferingIndicatorTimer();
    startupPlayRequestRef.current = false;
    if (video) {
      const absoluteTime = getAbsoluteVideoTime(video);
      clearPendingSeekIfReached(absoluteTime);
      syncBufferedAhead(video);
    }
    setPlaying(false);
    setBuffering(false);
    setAwaitingPlaybackStart(false);
    setReadyToStart(true);
  }, [clearBufferingIndicatorTimer, clearPendingSeekIfReached, clearStartupPlayTimer, getAbsoluteVideoTime, syncBufferedAhead]);

  const reportPlaybackEventWithAdaptation = useCallback(async (
    body: Parameters<typeof reportPlaybackEvent>[1],
  ) => {
    if (!sessionId) {
      return;
    }
    const requestSessionId = sessionId;
    try {
      await reportPlaybackEvent(requestSessionId, body);
    } catch (error) {
      // Unlike client logs, playback events are reported unconditionally --
      // no diagnostics/error-reporting toggle gates this call. That makes a
      // 403 here the telemetry-independent disowned-session signal (#707):
      // relying on the client-log path alone leaves a disowned session dead
      // for anyone with both toggles off, since no client log is ever
      // uploaded to surface the rejection. Feed the same recovery machinery
      // #705 already added instead of otherwise swallowing this silently.
      if (isPlaybackSessionOwnershipError(error)) {
        markPlaybackSessionUnusable(requestSessionId, "playback_session_ownership_lost");
      }
      // Otherwise telemetry is best-effort; playback keeps going.
    }
  }, [markPlaybackSessionUnusable, sessionId]);

  const completePlayback = useCallback((positionMs?: number, mediaEnded = false) => {
    if (playbackCompletedRef.current && !mediaEnded) {
      return;
    }
    const completedSeconds = effectiveDurationRef.current > 0
      ? effectiveDurationRef.current
      : currentTimeRef.current;
    const completedPositionMs = positionMs ?? Math.floor(completedSeconds * 1000);
    if (playbackCompletedRef.current) {
      void reportPlaybackEventWithAdaptation({
        event: "stop",
        position_ms: completedPositionMs,
      });
      return;
    }
    playbackCompletedRef.current = true;
    resumeAfterSessionSwitchRef.current = false;
    userPausedRef.current = true;
    clearStartupPlayTimer();
    clearBufferingIndicatorTimer();
    setCurrentTime(completedSeconds);
    setPendingSeek(null);
    setPlaybackEnded(true);
    setPlaying(false);
    setBuffering(false);
    setAwaitingPlaybackStart(false);
    setReadyToStart(true);
    void reportPlaybackEventWithAdaptation({
      event: mediaEnded ? "stop" : "progress",
      position_ms: completedPositionMs,
    });
  }, [
    clearBufferingIndicatorTimer,
    clearStartupPlayTimer,
    reportPlaybackEventWithAdaptation,
    setPendingSeek,
  ]);

  const buildClientPlaybackState = useCallback((
    reason: string,
    options?: { autoplayBlocked?: boolean; seekDebugSnapshot?: SeekDebugSnapshot | null },
  ): PlaybackClientState => {
    if (options?.autoplayBlocked) {
      autoplayBlockedRef.current = true;
    }
    const video = videoRef.current;
    const absoluteTime = video ? getAbsoluteVideoTime(video) : currentTime;
    const pendingSeekMS = pendingSeekTimeRef.current === null
      ? null
      : Math.floor(pendingSeekTimeRef.current * 1000);
    const reportedAbsoluteTime = pendingSeekTimeRef.current !== null
      && !playbackStartedRef.current
      && absoluteTime + 0.5 < pendingSeekTimeRef.current
        ? pendingSeekTimeRef.current
        : absoluteTime;
    const bufferedAheadMS = video
      ? Math.floor(getBufferedAheadSeconds(video, absoluteTime, mediaTimelineOffsetSeconds) * 1000)
      : 0;
    const bufferedRanges = video
      ? Array.from({ length: video.buffered.length }, (_unused, index) => ({
          start_ms: Math.floor((mediaTimelineOffsetSeconds + Math.max(0, video.buffered.start(index))) * 1000),
          end_ms: Math.floor((mediaTimelineOffsetSeconds + Math.max(0, video.buffered.end(index))) * 1000),
        }))
      : [];
    const snapshot: PlaybackClientState = {
      captured_at: new Date().toISOString(),
      reason,
      paused: video?.paused ?? !playing,
      playing,
      playback_started: playbackStartedRef.current,
      buffering_visible: buffering || seekSwitching || awaitingPlaybackStart || pendingSeekTimeRef.current !== null,
      awaiting_playback_start: awaitingPlaybackStart,
      ready_to_start: readyToStart,
      autoplay_blocked: autoplayBlockedRef.current,
      current_time_ms: Math.floor(reportedAbsoluteTime * 1000),
      duration_ms: Math.floor((effectiveDuration || 0) * 1000),
      ready_state: video?.readyState ?? 0,
      network_state: video?.networkState ?? 0,
      buffered_ahead_ms: bufferedAheadMS,
      buffered_ranges: bufferedRanges,
      pending_seek_ms: pendingSeekMS,
      seek_debug: options?.seekDebugSnapshot
        ? {
            event: options.seekDebugSnapshot.event,
            pointer_client_x: options.seekDebugSnapshot.pointerClientX,
            wrapper_left: options.seekDebugSnapshot.wrapperLeft,
            wrapper_width: options.seekDebugSnapshot.wrapperWidth,
            input_left: options.seekDebugSnapshot.inputLeft,
            input_width: options.seekDebugSnapshot.inputWidth,
            wrapper_ratio: options.seekDebugSnapshot.wrapperRatio,
            input_ratio: options.seekDebugSnapshot.inputRatio,
            hover_time_ms: options.seekDebugSnapshot.hoverTime !== null
              ? Math.floor(options.seekDebugSnapshot.hoverTime * 1000)
              : null,
            slider_value_ms: Math.floor(options.seekDebugSnapshot.sliderValue * 1000),
          }
        : undefined,
      media_error_code: video?.error?.code ?? undefined,
      media_error_message: video?.error ? mediaErrorMessage(video.error) : undefined,
      error: playerError ?? undefined,
    };
    return snapshot;
  }, [
    awaitingPlaybackStart,
    buffering,
    currentTime,
    effectiveDuration,
    getAbsoluteVideoTime,
    mediaTimelineOffsetSeconds,
    playerError,
    playing,
    readyToStart,
    seekSwitching,
  ]);

  const reportClientPlaybackState = useCallback((
    reason: string,
    options?: { autoplayBlocked?: boolean; seekDebugSnapshot?: SeekDebugSnapshot | null },
  ) => {
    if (!sessionId || pendingResumeAuthorityRef.current || resumeAuthorityFailedRef.current) {
      return;
    }
    const snapshot = buildClientPlaybackState(reason, options);
    const signature = JSON.stringify({
      reason: snapshot.reason,
      paused: snapshot.paused,
      playing: snapshot.playing,
      playback_started: snapshot.playback_started,
      buffering_visible: snapshot.buffering_visible,
      awaiting_playback_start: snapshot.awaiting_playback_start,
      ready_to_start: snapshot.ready_to_start,
      autoplay_blocked: snapshot.autoplay_blocked,
      current_time_ms: snapshot.current_time_ms,
      ready_state: snapshot.ready_state,
      network_state: snapshot.network_state,
      buffered_ahead_ms: snapshot.buffered_ahead_ms,
      media_error_code: snapshot.media_error_code ?? 0,
      media_error_message: snapshot.media_error_message ?? "",
      error: snapshot.error ?? "",
    });
    const previous = lastClientStateReportRef.current;
    const now = Date.now();
    if (previous && previous.signature === signature && now - previous.at < 1500) {
      return;
    }
    lastClientStateReportRef.current = { signature, at: now };
    void reportPlaybackEventWithAdaptation({
      event: "client_state",
      position_ms: snapshot.current_time_ms,
      client_state: snapshot,
    });
  }, [buildClientPlaybackState, reportPlaybackEventWithAdaptation, sessionId]);

  const requestStartupPlayback = useCallback(() => {
    const video = videoRef.current;
    if (
      !video
      || pendingResumeAuthorityRef.current
      || pendingNativeHlsPositionRestoreRef.current
      || nativeHlsInitialPositionPendingRef.current
      || resumeAuthorityFailedRef.current
    ) {
      return;
    }
    userPausedRef.current = false;
    clearStartupPlayTimer();
    startupPlayRequestRef.current = true;
    setStartupAutoplayBlocked(false);
    setBuffering(true);
    setAwaitingPlaybackStart(true);
    setReadyToStart(false);
    void video.play()
      .catch(() => {
        const currentVideo = videoRef.current;
        resumeAfterSessionSwitchRef.current = false;
        reportClientPlaybackState("autoplay_blocked_ready_to_start", { autoplayBlocked: true });
        releaseStartupWaitState(currentVideo);
        setStartupAutoplayBlocked(true);
      });
  }, [clearStartupPlayTimer, releaseStartupWaitState, reportClientPlaybackState]);

  // applyStartupResumeSeek moves the element onto the resume point before the
  // first play(), and reports whether it is still waiting for that point to be
  // buffered. Native HLS keeps its own restore path (#785); this is only for
  // the anchor-relative hls.js clock, where the resume point sits a partial
  // segment inside the first fragment and nothing else ever seeks there (#847).
  const applyStartupResumeSeek = useCallback((video: HTMLVideoElement) => {
    if (nativeHlsPlaybackRef.current) {
      return false;
    }
    const authority = pendingResumeAuthorityRef.current;
    if (authority) {
      const ranges = video.seekable.length > 0 ? video.seekable : video.buffered;
      const seekableRanges = Array.from({ length: ranges.length }, (_unused, index) => ({
        startSeconds: ranges.start(index),
        endSeconds: ranges.end(index),
      }));
      const decision = evaluateResumeAuthority({
        authority,
        attachedGenerationId: hlsGenerationIdRef.current,
        attachedOriginSeconds: mediaTimelineOffsetSecondsRef.current,
        currentRawSeconds: video.currentTime,
        seekableRanges,
      });
      if (decision.action === "verified") {
        const resumeAfterVerification = authority.resumeAfterVerification;
        clearResumeAuthorityDeadline();
        pendingResumeAuthorityRef.current = null;
        resumeAuthorityFailedRef.current = false;
        setPendingSeek(null);
        queueClientPlaybackLogRef.current("resume_generation_clock_verified", "media_event");
        if (!resumeAfterVerification) {
          userPausedRef.current = true;
          setBuffering(false);
          setAwaitingPlaybackStart(false);
          setReadyToStart(true);
          return true;
        }
        return false;
      }
      if (decision.action === "assign") {
        try {
          video.currentTime = decision.targetRawSeconds;
          authority.assigned = true;
          queueClientPlaybackLogRef.current("resume_generation_clock_assigned", "media_event");
        } catch {
          // Retain authority; a later readiness event retries the assignment.
        }
      }
      return true;
    }
    if (startupResumeSeekSettledRef.current) {
      return false;
    }
    const bufferedRanges = [];
    for (let index = 0; index < video.buffered.length; index += 1) {
      bufferedRanges.push({
        startSeconds: video.buffered.start(index),
        endSeconds: video.buffered.end(index),
      });
    }
    const decision = evaluateStartupResumeSeek({
      pendingSeekAbsoluteSeconds: pendingSeekTimeRef.current,
      mediaTimelineOffsetSeconds: mediaTimelineOffsetSecondsRef.current,
      currentRelativeSeconds: video.currentTime,
      reachedToleranceSeconds: SEEK_TARGET_REACHED_TOLERANCE_SECONDS,
      segmentDurationSeconds: segmentDurationSecondsRef.current,
      anchorFragmentDurationSeconds: anchorFragmentDurationSecondsRef.current,
      bufferedRanges,
    });
    if (decision.action === "wait_for_buffered_target") {
      return true;
    }
    startupResumeSeekSettledRef.current = true;
    if (decision.action === "seek") {
      try {
        video.currentTime = decision.targetSeconds;
      } catch {
        // An element that rejects the write keeps the old behaviour: the
        // pending seek clears once playback drifts up to the target.
        return false;
      }
      queueClientPlaybackLogRef.current("startup_resume_seek", "media_event");
    }
    return false;
  }, [clearResumeAuthorityDeadline, setPendingSeek]);

  const attemptStartupPlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    // An element released before the resume point was buffered (the startup
    // deadline, or an autoplay that went through early) still has to land on
    // the resume point once hls.js appends it: on 2026-09-18 the deadline
    // played into an empty buffer and, with the element unpaused, the gate
    // below never reached applyStartupResumeSeek again, so audio played the
    // anchor remainder under the buffering overlay (#965). Settling the seek
    // is idempotent, so re-driving it from every buffer event is cheap.
    if (
      (playbackStartedRef.current || !video.paused || startupPlayRequestRef.current)
      && !previewSeeking
      && !seekSwitching
      && !qualitySwitching
      && pendingResumeAuthorityRef.current === null
      && !nativeHlsInitialPositionPendingRef.current
    ) {
      applyStartupResumeSeek(video);
    }
    if (
      playbackStartedRef.current
      || previewSeeking
      || seekSwitching
      || qualitySwitching
      || (userPausedRef.current && pendingResumeAuthorityRef.current === null)
      || nativeHlsInitialPositionPendingRef.current
      || resumeAuthorityFailedRef.current
    ) {
      return;
    }

    alignToBufferedStart(video, mediaTimelineOffsetSeconds);
    updateObservedDuration(video);
    startupGateReleasedRef.current = true;
    if (!video.paused || startupPlayRequestRef.current) {
      return;
    }

    // Hold initial playback until enough is buffered ahead to ride out the
    // transcode cold-start at a seek (first segments take ~0.3-1s each to
    // produce); starting into a ~1s buffer causes the startup stutter we
    // measured. onProgress/frag events re-drive this as the buffer fills; the
    // 250ms timer guarantees the hard-deadline fallback fires even if buffering
    // stalls entirely, so startup can never hang on a black screen. The
    // deadline only ever releases an element that has media appended
    // (playerStartupGate.ts); playing into an empty buffer starts nothing.
    const now = Date.now();
    if (startupBufferDeadlineRef.current === 0) {
      startupBufferDeadlineRef.current = now + STARTUP_MIN_BUFFER_DEADLINE_MS;
    }
    // Align onto the resume point first, so the buffer gate below measures what
    // is ready ahead of where playback will actually begin rather than ahead of
    // the anchor. Waiting for the target to buffer shares the same hard
    // deadline, so an anchor whose remainder never arrives still starts.
    const awaitingStartupResumeSeek = applyStartupResumeSeek(video);
    if (userPausedRef.current) {
      return;
    }
    const bufferedAhead = getBufferedAheadSeconds(video, getAbsoluteVideoTime(video), mediaTimelineOffsetSeconds);
    const resumeNativeSessionSwitch =
      resumeAfterSessionSwitchRef.current
      && nativeHlsPlaybackRef.current
      && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA;
    if (pendingResumeAuthorityRef.current) {
      if (startupBufferTimerRef.current !== null) {
        window.clearTimeout(startupBufferTimerRef.current);
      }
      startupBufferTimerRef.current = window.setTimeout(() => {
        startupBufferTimerRef.current = null;
        attemptStartupPlaybackRef.current?.();
      }, 250);
      return;
    }
    const gate = evaluateStartupPlaybackGate({
      hasBufferedMedia: video.buffered.length > 0,
      awaitingResumeSeek: awaitingStartupResumeSeek,
      bufferedAheadSeconds: bufferedAhead,
      minBufferSeconds: STARTUP_MIN_BUFFER_SECONDS,
      nowMs: now,
      deadlineMs: startupBufferDeadlineRef.current,
      resumeNativeSessionSwitch: Boolean(resumeNativeSessionSwitch),
    });
    if (gate === "wait") {
      if (startupBufferTimerRef.current !== null) {
        window.clearTimeout(startupBufferTimerRef.current);
      }
      startupBufferTimerRef.current = window.setTimeout(() => {
        startupBufferTimerRef.current = null;
        attemptStartupPlaybackRef.current?.();
      }, 250);
      return;
    }
    if (startupBufferTimerRef.current !== null) {
      window.clearTimeout(startupBufferTimerRef.current);
      startupBufferTimerRef.current = null;
    }

    requestStartupPlayback();
  }, [
    applyStartupResumeSeek,
    getAbsoluteVideoTime,
    previewSeeking,
    qualitySwitching,
    requestStartupPlayback,
    seekSwitching,
    mediaTimelineOffsetSeconds,
    updateObservedDuration,
  ]);

  const syncPausedReadyState = useCallback((video: HTMLVideoElement | null | undefined) => {
    if (!video || previewSeeking || seekSwitching || qualitySwitching) {
      return;
    }
    if (!playbackStartedRef.current && resumeAfterSessionSwitchRef.current && !userPausedRef.current) {
      return;
    }
    if (!playbackStartedRef.current && (!startupGateReleasedRef.current || startupPlayRequestRef.current)) {
      return;
    }
    if (video.paused && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      const absoluteTime = getAbsoluteVideoTime(video);
      clearPendingSeekIfReached(absoluteTime);
      setBuffering(false);
      setAwaitingPlaybackStart(false);
      setReadyToStart(true);
    }
  }, [clearPendingSeekIfReached, getAbsoluteVideoTime, previewSeeking, qualitySwitching, seekSwitching]);

  useEffect(() => {
    selectedAudioTrackIdRef.current = selectedAudioTrackId;
  }, [selectedAudioTrackId]);

  useEffect(() => {
    selectedSubtitleIdRef.current = selectedSubtitleId;
  }, [selectedSubtitleId]);

  const buildProfile = useCallback((maxVideoBitrate?: number) => {
    const profile: {
      container: string;
      max_video_bitrate?: number;
      selected_audio_track_id?: string;
      selected_subtitle_id?: string;
    } = {
      container: "hls",
    };
    if (maxVideoBitrate !== undefined) {
      profile.max_video_bitrate = maxVideoBitrate;
    }
    const audioTrackId = selectedAudioTrackIdRef.current?.trim();
    if (audioTrackId) {
      profile.selected_audio_track_id = audioTrackId;
    }
    const subtitleId = selectedSubtitleIdRef.current?.trim();
    if (subtitleId) {
      profile.selected_subtitle_id = subtitleId;
    }
    return profile;
  }, []);

  const observedBandwidthHintBps = observedBandwidthRange?.max
    ?? (delivery?.last_bandwidth_bps && delivery.last_bandwidth_bps > 0 ? delivery.last_bandwidth_bps : undefined);
  const startupLevelHint = selectStartupLevelHint(
    delivery?.variants,
    conservativeStartup,
    observedBandwidthHintBps,
  );
  const startupLevelHintRef = useRef(startupLevelHint);

  useEffect(() => {
    startupLevelHintRef.current = startupLevelHint;
  }, [startupLevelHint]);

  const currentStreamProfile = useCallback(() => {
    if (selectedMaxVideoBitrate && selectedMaxVideoBitrate > 0) {
      return buildProfile(selectedMaxVideoBitrate);
    }
    return buildProfile();
  }, [buildProfile, selectedMaxVideoBitrate]);

  const recordBandwidthObservation = useCallback((sample: number) => {
    if (!Number.isFinite(sample) || sample <= 0) {
      return;
    }
    const current = observedBandwidthRangeRef.current;
    if (!current) {
      const next = { min: sample, max: sample };
      observedBandwidthRangeRef.current = next;
      setObservedBandwidthRange(next);
      return;
    }
    const next = {
      min: Math.min(current.min, sample),
      max: Math.max(current.max, sample),
    };
    if (next.min !== current.min || next.max !== current.max) {
      observedBandwidthRangeRef.current = next;
      setObservedBandwidthRange(next);
    }
  }, []);

  const noteObservedLevel = useCallback((level: number) => {
    if (!Number.isInteger(level) || level < 0) {
      return;
    }
    setCurrentLevel(level);
    previousObservedLevelRef.current = level;
  }, []);

  const getMeasuredBandwidth = useCallback(() => {
    const estimate = hlsRef.current?.bandwidthEstimate ?? 0;
    if (Number.isFinite(estimate) && estimate > 0) {
      return Math.round(estimate);
    }
    if (delivery?.last_bandwidth_bps && delivery.last_bandwidth_bps > 0) {
      return delivery.last_bandwidth_bps;
    }
    return 0;
  }, [delivery?.last_bandwidth_bps]);

  const activeObservedVariantName = useMemo(() => {
    if (delivery && currentLevel !== null && currentLevel >= 0 && currentLevel < delivery.variants.length) {
      return delivery.variants[currentLevel]?.name ?? delivery.active_variant_name;
    }
    return delivery?.active_variant_name;
  }, [currentLevel, delivery]);

  const buildClientPlaybackLogEntry = useCallback((
    reason: string,
    event: string,
    options: ClientPlaybackLogOptions = {},
  ): PlaybackClientLogEntry | null => {
    const video = videoRef.current;
    if (!video) {
      return null;
    }
    const capturedAtMS = Date.now();
    const absoluteTime = getAbsoluteVideoTime(video);
    const absolutePositionMS = Math.floor(absoluteTime * 1000);
    const previousSample = lastClientPlaybackLogSampleRef.current;
    const previousPositionMS = previousSample?.positionMS ?? lastClientPlaybackLogPositionMSRef.current;
    const wallDeltaMS = previousSample
      ? Math.max(0, capturedAtMS - previousSample.capturedAtMS)
      : null;
    const playbackRate = Number.isFinite(video.playbackRate) && video.playbackRate > 0
      ? video.playbackRate
      : 1;
    const expectedDeltaMS = wallDeltaMS === null
      ? null
      : Math.floor(wallDeltaMS * Math.max(1, playbackRate));
    const expectedPositionMS = expectedDeltaMS === null
      ? null
      : previousPositionMS + expectedDeltaMS;
    const positionDeltaMS = previousSample
      ? absolutePositionMS - previousPositionMS
      : null;
    const canClassifyTimelineJump =
      previousSample !== null &&
      previousPositionMS > 0 &&
      pendingSeekTimeRef.current === null &&
      !video.seeking &&
      !seekSwitching &&
      !qualitySwitching &&
      !runtimeRenegotiatingRef.current;
    let resolvedEvent = event;
    if (
      canClassifyTimelineJump &&
      absolutePositionMS + CLIENT_TIMELINE_REGRESSION_TOLERANCE_MS < previousPositionMS
    ) {
      resolvedEvent = "position_regression";
    } else if (
      canClassifyTimelineJump &&
      expectedPositionMS !== null &&
      absolutePositionMS > expectedPositionMS + CLIENT_TIMELINE_FORWARD_JUMP_TOLERANCE_MS
    ) {
      resolvedEvent = "position_jump";
    }
    lastClientPlaybackLogPositionMSRef.current = absolutePositionMS;
    lastClientPlaybackLogSampleRef.current = {
      positionMS: absolutePositionMS,
      capturedAtMS,
    };
    let cause = activePlaybackLogCauseRef.current;
    const eventStartsCause = resolvedEvent === "position_regression"
      || resolvedEvent === "position_jump"
      || (resolvedEvent === "hls_event" && reason.startsWith("hls_error_"));
    if (!cause && eventStartsCause) {
      cause = beginPlaybackLogCause(resolvedEvent);
    }

    const bufferedRanges = Array.from({ length: video.buffered.length }, (_unused, index) => ({
      start_ms: Math.floor((mediaTimelineOffsetSeconds + Math.max(0, video.buffered.start(index))) * 1000),
      end_ms: Math.floor((mediaTimelineOffsetSeconds + Math.max(0, video.buffered.end(index))) * 1000),
    }));
    const pendingSeekMS = pendingSeekTimeRef.current === null
      ? null
      : Math.floor(pendingSeekTimeRef.current * 1000);
    return {
      sequence: ++clientPlaybackLogSeqRef.current,
      captured_at: new Date().toISOString(),
      event: resolvedEvent,
      reason,
      client_version: CLIENT_BUILD_INFO.version,
      client_build_id: CLIENT_BUILD_INFO.buildId,
      client_build_date: CLIENT_BUILD_INFO.buildDate,
      client_commit: CLIENT_BUILD_INFO.commit,
      hls_generation_id: hlsGenerationIdRef.current ?? delivery?.hls_generation_id ?? null,
      cause_id: cause?.id ?? null,
      caused_by_event: cause?.event ?? null,
      action: resolvedEvent === "position_regression"
        ? (nativeHlsPlaybackRef.current ? "observe_timeline_anomaly" : "runtime_handoff_recovery")
          : resolvedEvent === "position_jump"
            ? "observe_position_jump"
            : resolvedEvent === "seek_intent"
              ? "observe_seek_intent"
              : resolvedEvent === "playback_command"
                ? "observe_playback_command"
              : reason.startsWith("hls_error_")
                ? "hls_error_observed"
                : null,
      media_url_tail: mediaURLTail(video.currentSrc),
      seek_intent_id: options.seekIntent?.id ?? null,
      seek_intent_phase: options.seekIntent?.phase ?? null,
      seek_intent_source: options.seekIntent?.source ?? null,
      seek_intent_input: options.seekIntent?.input ?? null,
      seek_intent_start_position_ms: options.seekIntent?.startPositionSeconds === null || options.seekIntent?.startPositionSeconds === undefined
        ? null
        : Math.floor(options.seekIntent.startPositionSeconds * 1000),
      seek_intent_target_ms: options.seekIntent?.targetSeconds === null || options.seekIntent?.targetSeconds === undefined
        ? null
        : Math.floor(options.seekIntent.targetSeconds * 1000),
      seek_intent_preview_ms: options.seekIntent?.previewSeconds === null || options.seekIntent?.previewSeconds === undefined
        ? null
        : Math.floor(options.seekIntent.previewSeconds * 1000),
      seek_intent_resume_expected: options.seekIntent?.resumeExpected ?? null,
      seek_intent_result_delta_ms: options.seekIntent?.resultDeltaMS ?? null,
      command_id: options.command?.id ?? null,
      command_phase: options.command?.phase ?? null,
      command_type: options.command?.type ?? null,
      command_source: options.command?.source ?? null,
      command_input: options.command?.input ?? null,
      command_blocked_reason: options.command?.blockedReason ?? null,
      command_target_position_ms: options.command?.targetPositionSeconds === null || options.command?.targetPositionSeconds === undefined
        ? null
        : Math.floor(options.command.targetPositionSeconds * 1000),
      command_source_position_ms: options.command?.sourcePositionSeconds === null || options.command?.sourcePositionSeconds === undefined
        ? null
        : Math.floor(options.command.sourcePositionSeconds * 1000),
      command_source_session_id: options.command?.sourceSessionId ?? null,
      command_target_session_id: options.command?.targetSessionId ?? null,
      command_should_resume: options.command?.shouldResume ?? null,
      video_current_time_ms: Math.floor(Math.max(0, video.currentTime) * 1000),
      absolute_position_ms: absolutePositionMS,
      previous_absolute_position_ms: previousSample ? previousPositionMS : null,
      expected_absolute_position_ms: expectedPositionMS,
      position_delta_ms: positionDeltaMS,
      wall_delta_ms: wallDeltaMS,
      media_timeline_offset_ms: Math.floor(mediaTimelineOffsetSeconds * 1000),
      duration_ms: Math.floor((effectiveDuration || 0) * 1000),
      paused: video.paused,
      seeking: video.seeking,
      ended: video.ended,
      playback_rate: video.playbackRate,
      ready_state: video.readyState,
      network_state: video.networkState,
      buffered_ahead_ms: Math.floor(getBufferedAheadSeconds(video, absoluteTime, mediaTimelineOffsetSeconds) * 1000),
      buffered_ranges: bufferedRanges,
      client_variant_name: nativeHlsPlaybackRef.current ? null : activeObservedVariantName ?? null,
      playback_started: playbackStartedRef.current,
      buffering_visible: buffering || seekSwitching || awaitingPlaybackStart || pendingSeekTimeRef.current !== null,
      awaiting_playback_start: awaitingPlaybackStart,
      ready_to_start: readyToStart,
      pending_seek_ms: pendingSeekMS,
      seek_switching: seekSwitching,
      quality_switching: qualitySwitching,
      runtime_renegotiating: runtimeRenegotiatingRef.current,
      media_error_code: video.error?.code ?? null,
      media_error_message: video.error ? mediaErrorMessage(video.error) : null,
      player_error: playerError,
    };
  }, [
    activeObservedVariantName,
    awaitingPlaybackStart,
    beginPlaybackLogCause,
    buffering,
    delivery?.hls_generation_id,
    effectiveDuration,
    getAbsoluteVideoTime,
    mediaTimelineOffsetSeconds,
    playerError,
    qualitySwitching,
    readyToStart,
    seekSwitching,
  ]);

  const flushClientPlaybackLogs = useCallback((useBeacon = false, options: ClientPlaybackLogFlushOptions = {}) => {
    if (!sessionId) {
      return;
    }
    if (activeSessionStoppedRef.current) {
      clientPlaybackLogPendingRef.current = [];
      return;
    }
    if (playbackSessionOwnershipLostSessionsRef.current.has(sessionId)) {
      // This session has been permanently rejected (403) by the server for
      // client-log uploads; stop uploading rather than spin on it (#705).
      clientPlaybackLogPendingRef.current = [];
      return;
    }
    // Privacy gate (defense-in-depth): never upload unless the user enabled
    // diagnostics or error reporting. The local ring buffer still fills.
    if (!playbackDiagnosticsEnabledRef.current && !playbackErrorReportingEnabledRef.current) {
      return;
    }
    if (options.includeRecent === true) {
      // Reinsert the bounded local context into the upload queue, deduplicated
      // and sorted by sequence, so byte chunking cannot send newer context
      // ahead of older pending diagnostics.
      const recent = clientPlaybackLogRingRef.current.slice(-PLAYBACK_CLIENT_LOG_MAX_BATCH_ENTRIES);
      const bySequence = new Map<number, PlaybackClientLogEntry>();
      for (const entry of [...clientPlaybackLogPendingRef.current, ...recent]) {
        bySequence.set(entry.sequence, entry);
      }
      clientPlaybackLogPendingRef.current = [...bySequence.values()]
        .sort((left, right) => left.sequence - right.sequence)
        .slice(-CLIENT_PLAYBACK_LOG_MAX_PENDING);
    }

    const takePendingBatch = (): PlaybackClientLogEntry[] => {
      // A single entry too large to send can never succeed in any batch, and
      // requeueing it wedges the queue head so nothing behind it uploads
      // again. Drop it here instead, and skip forward to the next entry.
      while (clientPlaybackLogPendingRef.current.length > 0) {
        const candidates = clientPlaybackLogPendingRef.current
          .slice(0, PLAYBACK_CLIENT_LOG_MAX_BATCH_ENTRIES)
          .map((entry) => options.flushReason
            ? { ...entry, flush_reason: options.flushReason }
            : entry);
        const batch = selectPlaybackClientLogBatch(candidates);
        if (batch.entries.length === 0) {
          return [];
        }
        clientPlaybackLogPendingRef.current.splice(0, batch.entries.length);
        if (batch.oversizedEntry) {
          continue;
        }
        return batch.entries;
      }
      return [];
    };
    const requeueBatch = (entries: PlaybackClientLogEntry[]) => {
      clientPlaybackLogPendingRef.current = entries.concat(clientPlaybackLogPendingRef.current)
        .slice(0, CLIENT_PLAYBACK_LOG_MAX_PENDING);
    };

    if (useBeacon) {
      while (clientPlaybackLogSessionRef.current === sessionId) {
        const payloadEntries = takePendingBatch();
        if (payloadEntries.length === 0) {
          return;
        }
        lastClientPlaybackLogFlushAtRef.current = Date.now();
        if (!sendPlaybackClientLogsBeacon(sessionId, { entries: payloadEntries })) {
          requeueBatch(payloadEntries);
          break;
        }
      }
    }
    if (clientPlaybackLogFlushInFlightRef.current || clientPlaybackLogPendingRef.current.length === 0) {
      return;
    }

    clientPlaybackLogFlushInFlightRef.current = true;
    void (async () => {
      while (clientPlaybackLogSessionRef.current === sessionId) {
        const payloadEntries = takePendingBatch();
        if (payloadEntries.length === 0) {
          return;
        }
        lastClientPlaybackLogFlushAtRef.current = Date.now();
        try {
          await reportPlaybackClientLogs(sessionId, { entries: payloadEntries });
        } catch (error) {
          if (!isRetryableClientPlaybackLogError(error)) {
            // Permanent rejection (any 4xx other than 408/429): the same batch
            // can never succeed, so drop it instead of requeuing it forever.
            if (isPlaybackSessionOwnershipError(error)) {
              clientPlaybackLogPendingRef.current = [];
              markPlaybackSessionUnusable(sessionId, "playback_session_ownership_lost");
            }
            return;
          }
          requeueBatch(payloadEntries);
          return;
        }
      }
    })().finally(() => {
      if (clientPlaybackLogSessionRef.current === sessionId) {
        clientPlaybackLogFlushInFlightRef.current = false;
      }
    });
  }, [markPlaybackSessionUnusable, sessionId]);

  const queueClientPlaybackLog = useCallback((reason: string, event = "sample", options: ClientPlaybackLogOptions = {}) => {
    if (!sessionId) {
      return;
    }
    const entry = buildClientPlaybackLogEntry(reason, event, options);
    if (!entry) {
      return;
    }
    clientPlaybackLogRingRef.current.push(entry);
    if (clientPlaybackLogRingRef.current.length > CLIENT_PLAYBACK_LOG_RING_MAX) {
      clientPlaybackLogRingRef.current.splice(
        0,
        clientPlaybackLogRingRef.current.length - CLIENT_PLAYBACK_LOG_RING_MAX,
      );
    }
    // Upload gate: always retain locally above (ring buffer), but only
    // enqueue for upload when diagnostics is on, or error reporting is on
    // and this is an error-class entry.
    const isErrorClassEntry =
      entry.event === "position_regression" ||
      entry.event === "position_jump" ||
      entry.event === "media_error" ||
      entry.event.startsWith("hls_error") ||
      (entry.media_error_code ?? 0) > 0 ||
      (entry.player_error ?? "") !== "";
    const uploadable =
      !activeSessionStoppedRef.current &&
      !playbackSessionOwnershipLostSessionsRef.current.has(sessionId) &&
      (
        playbackDiagnosticsEnabledRef.current ||
        (playbackErrorReportingEnabledRef.current && isErrorClassEntry)
      );
    if (uploadable) {
      clientPlaybackLogPendingRef.current.push(entry);
      if (clientPlaybackLogPendingRef.current.length > CLIENT_PLAYBACK_LOG_MAX_PENDING) {
        clientPlaybackLogPendingRef.current.splice(
          0,
          clientPlaybackLogPendingRef.current.length - CLIENT_PLAYBACK_LOG_MAX_PENDING,
        );
      }
      const dueForFlush = Date.now() - lastClientPlaybackLogFlushAtRef.current >= CLIENT_PLAYBACK_LOG_FLUSH_INTERVAL_MS;
      if (
        dueForFlush ||
        clientPlaybackLogPendingRef.current.length >= PLAYBACK_CLIENT_LOG_MAX_BATCH_ENTRIES ||
        entry.event === "position_regression" ||
        entry.event === "position_jump" ||
        options.flushImmediately
      ) {
        flushClientPlaybackLogs(false, {
          includeRecent: entry.event === "position_regression" || entry.event === "position_jump",
          flushReason: options.flushReason
            ?? (entry.event === "position_regression" || entry.event === "position_jump"
              ? entry.event
              : dueForFlush
                ? "interval"
                : options.flushImmediately
                  ? entry.event
                  : "batch_full"),
        });
      }
    }
    if (
      (entry.event === "position_regression" || entry.event === "position_jump") &&
      !playbackCompletedRef.current &&
      !nextEpisodeStartingRef.current
    ) {
      // Native HLS owns timeline correction. Regression/jump observations
      // are flushed as diagnostics but never promoted into a seek, reload,
      // or replacement-session command (#273, #401, #785).
      if (nativeHlsPlaybackRef.current) {
        reportClientPlaybackStateRef.current?.("native_hls_timeline_anomaly_observed");
        return;
      }
      // A timeline origin change moves the reported absolute position without
      // the content moving: until the media element rebases, origin + a clock
      // still on the previous anchor reads as a jump. Recovering re-anchors one
      // segment later, which manufactures the next identical jump — the loop
      // that reached generation 11 on pbs_00003347 and generation 8 on
      // pbs_00003348, ~16s per cycle with a healthy buffer throughout (#846).
      // Observe it, never act on it; a real stall is still caught by the
      // no-progress watchdog.
      const observedOffsetMS = entry.media_timeline_offset_ms ?? null;
      const previousOffsetMS = lastObservedTimelineOffsetMSRef.current;
      if (observedOffsetMS !== null) {
        lastObservedTimelineOffsetMSRef.current = observedOffsetMS;
      }
      if (
        observedOffsetMS !== null
        && previousOffsetMS !== null
        && observedOffsetMS !== previousOffsetMS
      ) {
        reportClientPlaybackStateRef.current?.("timeline_origin_change_anomaly_observed");
        return;
      }
      const recoveryPositionMS = entry.event === "position_jump"
        ? (entry.expected_absolute_position_ms ?? entry.previous_absolute_position_ms ?? entry.absolute_position_ms)
        : (entry.previous_absolute_position_ms ?? entry.expected_absolute_position_ms ?? entry.absolute_position_ms);
      triggerRuntimeHandoffRecovery(
        entry.event === "position_jump"
          ? "Playback timeline jumped forward and was restarted."
          : "Playback position became inconsistent and needs a clean recovery.",
        recoveryPositionMS ?? undefined,
      );
    }
  }, [buildClientPlaybackLogEntry, flushClientPlaybackLogs, sessionId, triggerRuntimeHandoffRecovery]);
  const queueClientPlaybackLogRef = useRef(queueClientPlaybackLog);
  const flushClientPlaybackLogsRef = useRef(flushClientPlaybackLogs);

  useEffect(() => {
    queueClientPlaybackLogRef.current = queueClientPlaybackLog;
  }, [queueClientPlaybackLog]);

  useEffect(() => {
    flushClientPlaybackLogsRef.current = flushClientPlaybackLogs;
  }, [flushClientPlaybackLogs]);

  // Rebuild the media element for a replaced HLS generation.
  //
  // A new generation is served from a new anchor, and the server reports that
  // anchor as the media timeline origin. That origin is only true of an element
  // hls.js builds against the replacement playlist: hls.js aligns a fresh
  // MediaSource to the first advertised fragment, but appends into an existing
  // one at the fragment's own rebased absolute tfdt. Reusing the element
  // therefore adopts the new origin onto a clock still running on the old
  // anchor and counts the anchor twice. Cycling the reload nonce tears the
  // element down and builds origin and clock together, which is the only state
  // the server's model describes (#846).
  const rebuildMediaElementForGenerationSwap = useCallback((
    reason: string,
    resumeAuthority?: AuthoritativeResumeTuple,
    resumeGenerationSequence: number | null = null,
  ) => {
    // The media load effect starts every element it builds at the session's
    // baseline, which is the resume point for a new session and the wrong
    // place for a mid-session rebuild: every ready window begins at the
    // session anchor, so the replacement would restart there and lurch the
    // viewer backwards (#873). Publish the live playhead as the accepted
    // origin first — the same channel adoptVerifiedNativeHlsTimeline uses —
    // so the replacement seeks to where playback actually is.
    // A response-provided tuple is installed synchronously, before the nonce
    // can attach hls.js to the replacement source. When such an intent is
    // already pending, deriving another target from an element whose clock is
    // not yet verified is forbidden.
    if (!resumeAuthority && pendingResumeAuthorityRef.current) {
      return;
    }
    const video = videoRef.current;
    const resumeSeconds = resumeAuthority?.targetAbsoluteSeconds ?? (video
      ? Math.max(0, getAbsoluteVideoTime(video))
      : Math.max(0, currentTimeRef.current));
    if (resumeSeconds > 0) {
      sessionStartSecondsRef.current = resumeSeconds;
      sessionBaselineSecondsRef.current = resumeSeconds;
      setRuntimeTimeline((previous) => (
        previous.sessionId === currentSessionResourceIdRef.current
          && previous.offsetSeconds === resumeSeconds
          ? previous
          : { sessionId: currentSessionResourceIdRef.current, offsetSeconds: resumeSeconds }
      ));
    }
    const nextInstance = mediaElementReloadNonceRef.current + 1;
    mediaElementReloadNonceRef.current = nextInstance;
    if (resumeAuthority) {
      const sourceSessionId = currentSessionResourceIdRef.current;
      if (!sourceSessionId) {
        return;
      }
      const pendingAuthority: PendingResumeAuthority = {
        tuple: resumeAuthority,
        assigned: false,
        resumeAfterVerification: true,
      };
      pendingResumeAuthorityRef.current = pendingAuthority;
      armResumeAuthorityDeadline(pendingAuthority);
      acceptedResumeAuthorityRef.current = {
        sessionId: sourceSessionId,
        tuple: resumeAuthority,
        generationSequence: resumeGenerationSequence,
      };
      hlsGenerationIdRef.current = resumeAuthority.nextGenerationId;
      mediaTimelineOffsetSecondsRef.current = resumeAuthority.originSeconds;
      setPendingSeek(resumeAuthority.targetAbsoluteSeconds);
      setRuntimeMediaTimeline({
        sessionId: sourceSessionId,
        originSeconds: resumeAuthority.originSeconds,
      });
      setElementTimelineOrigin({
        instance: nextInstance,
        generationId: resumeAuthority.nextGenerationId,
        offsetSeconds: resumeAuthority.originSeconds,
      });
    }
    resumeAfterSessionSwitchRef.current = true;
    queueClientPlaybackLogRef.current?.(`media_element_rebuild_${reason}`, "session", {
      flushImmediately: true,
      flushReason: "media_element_rebuild",
    });
    setMediaElementReloadNonce(nextInstance);
  }, [armResumeAuthorityDeadline, getAbsoluteVideoTime, setPendingSeek]);

  const beginPlaybackCommand = useCallback((
    type: PlaybackCommandType,
    source: PlaybackCommandSource,
    input: string | null = null,
  ): PlaybackCommandState => ({
    id: `${sessionId ?? "session"}-cmd-${++playbackCommandSequenceRef.current}`,
    type,
    source,
    input,
  }), [sessionId]);

  const queuePlaybackCommandLog = useCallback((
    command: PlaybackCommandState,
    phase: PlaybackCommandPhase,
    overrides: Partial<Omit<PlaybackCommandLogDetails, "id" | "phase">> = {},
  ) => {
    const details: PlaybackCommandLogDetails = {
      id: command.id,
      phase,
      type: overrides.type ?? command.type,
      source: overrides.source ?? command.source,
      input: overrides.input === undefined ? command.input : overrides.input,
      blockedReason: overrides.blockedReason ?? null,
      targetPositionSeconds: overrides.targetPositionSeconds ?? null,
      sourcePositionSeconds: overrides.sourcePositionSeconds ?? null,
      sourceSessionId: overrides.sourceSessionId ?? null,
      targetSessionId: overrides.targetSessionId ?? null,
      shouldResume: overrides.shouldResume ?? null,
    };
    queueClientPlaybackLogRef.current(`playback_command_${details.type}_${phase}`, "playback_command", {
      command: details,
      flushImmediately: phase !== "request",
      flushReason: `playback_command_${phase}`,
    });
  }, []);

  const playbackCommandBlockReason = useCallback(() => {
    if (playbackSessionSwitchActive()) {
      return "session_switch";
    }
    if (seekInFlightRef.current) {
      return "seek_in_flight";
    }
    if (seekSwitching) {
      return "seek_switching";
    }
    if (runtimeRenegotiatingRef.current) {
      return "runtime_recovery";
    }
    return null;
  }, [playbackSessionSwitchActive, seekSwitching]);

  useEffect(() => {
    clientPlaybackLogSessionRef.current = sessionId ?? null;
    clientPlaybackLogFlushInFlightRef.current = false;
    clientPlaybackLogPendingRef.current = [];
    clientPlaybackLogRingRef.current = [];
    clientPlaybackLogSeqRef.current = 0;
    playbackCommandSequenceRef.current = 0;
    lastClientPlaybackLogFlushAtRef.current = 0;
    lastClientPlaybackLogPositionMSRef.current = 0;
    lastClientPlaybackLogSampleRef.current = null;
    activePlaybackLogCauseRef.current = null;
    hlsGenerationIdRef.current = null;
    activeSeekIntentRef.current = null;
    clearSeekIntentResultTimer();
  }, [clearSeekIntentResultTimer, sessionId]);

  useEffect(() => {
    if (!sessionId) {
      return;
    }
    queueClientPlaybackLogRef.current("session_active", "session");
    const timer = window.setInterval(() => {
      queueClientPlaybackLogRef.current("interval", "sample");
    }, CLIENT_PLAYBACK_LOG_INTERVAL_MS);
    return () => {
      window.clearInterval(timer);
      queueClientPlaybackLogRef.current("session_cleanup", "session");
      flushClientPlaybackLogsRef.current(true);
    };
  }, [sessionId]);

  const currentAbsolutePlaybackSeconds = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return currentTimeRef.current;
    }
    return getAbsoluteVideoTime(video);
  }, [getAbsoluteVideoTime]);

  const clampSeekIntentSeconds = useCallback((seconds: number) => {
    const upperBound = effectiveDurationRef.current > 0 ? effectiveDurationRef.current : Number.POSITIVE_INFINITY;
    return Math.max(0, Math.min(seconds, upperBound));
  }, []);

  const queueSeekIntentLog = useCallback((
    intent: SeekIntentState,
    phase: SeekIntentPhase,
    overrides: Partial<Pick<
      SeekIntentLogDetails,
      "source" | "input" | "startPositionSeconds" | "targetSeconds" | "previewSeconds" | "resumeExpected" | "resultDeltaMS"
    >> = {},
  ) => {
    const details: SeekIntentLogDetails = {
      id: intent.id,
      phase,
      source: overrides.source ?? intent.source,
      input: overrides.input ?? intent.input,
      startPositionSeconds: overrides.startPositionSeconds ?? intent.startPositionSeconds,
      targetSeconds: overrides.targetSeconds ?? intent.targetSeconds,
      previewSeconds: overrides.previewSeconds ?? intent.previewSeconds,
      resumeExpected: overrides.resumeExpected ?? intent.resumeExpected,
      resultDeltaMS: overrides.resultDeltaMS ?? null,
    };
    queueClientPlaybackLogRef.current(`seek_intent_${phase}`, "seek_intent", {
      seekIntent: details,
      flushImmediately: phase === "commit" || phase === "cancel" || phase === "result",
      flushReason: `seek_intent_${phase}`,
    });
  }, []);

  const beginSeekIntent = useCallback((
    source: SeekIntentSource,
    input: string | null,
    targetSeconds: number,
    options: { previewSeconds?: number | null; resumeExpected?: boolean } = {},
  ) => {
    clearSeekIntentResultTimer();
    const boundedTarget = clampSeekIntentSeconds(targetSeconds);
    const previewSeconds = options.previewSeconds === undefined
      ? boundedTarget
      : options.previewSeconds === null
        ? null
        : clampSeekIntentSeconds(options.previewSeconds);
    const video = videoRef.current;
    const intent: SeekIntentState = {
      id: `${sessionId ?? "session"}-${++seekIntentSequenceRef.current}`,
      source,
      input,
      startedAtMS: Date.now(),
      startPositionSeconds: currentAbsolutePlaybackSeconds(),
      targetSeconds: boundedTarget,
      previewSeconds,
      resumeExpected: options.resumeExpected ?? !(video?.paused ?? !playing),
    };
    activeSeekIntentRef.current = intent;
    queueSeekIntentLog(intent, "start");
    return intent;
  }, [
    clampSeekIntentSeconds,
    clearSeekIntentResultTimer,
    currentAbsolutePlaybackSeconds,
    playing,
    queueSeekIntentLog,
    sessionId,
  ]);

  const updateSeekIntent = useCallback((
    targetSeconds: number,
    options: { source?: SeekIntentSource; input?: string | null; previewSeconds?: number | null } = {},
  ) => {
    const intent = activeSeekIntentRef.current;
    if (!intent) {
      return null;
    }
    const boundedTarget = clampSeekIntentSeconds(targetSeconds);
    const previewSeconds = options.previewSeconds === undefined
      ? boundedTarget
      : options.previewSeconds === null
        ? null
        : clampSeekIntentSeconds(options.previewSeconds);
    intent.targetSeconds = boundedTarget;
    intent.previewSeconds = previewSeconds;
    if (options.source) {
      intent.source = options.source;
    }
    if (options.input !== undefined) {
      intent.input = options.input;
    }
    queueSeekIntentLog(intent, "update");
    return intent;
  }, [clampSeekIntentSeconds, queueSeekIntentLog]);

  const activeOrBeginSeekIntent = useCallback((
    source: SeekIntentSource,
    input: string | null,
    targetSeconds: number,
    options: { previewSeconds?: number | null; resumeExpected?: boolean } = {},
  ) => {
    const intent = activeSeekIntentRef.current;
    if (!intent) {
      return beginSeekIntent(source, input, targetSeconds, options);
    }
    return updateSeekIntent(targetSeconds, {
      source,
      input,
      previewSeconds: options.previewSeconds,
    }) ?? beginSeekIntent(source, input, targetSeconds, options);
  }, [beginSeekIntent, updateSeekIntent]);

  const commitSeekIntent = useCallback((targetSeconds: number, resumeExpected?: boolean) => {
    const intent = activeSeekIntentRef.current;
    if (!intent) {
      return null;
    }
    const boundedTarget = clampSeekIntentSeconds(targetSeconds);
    intent.targetSeconds = boundedTarget;
    intent.previewSeconds = boundedTarget;
    if (resumeExpected !== undefined) {
      intent.resumeExpected = resumeExpected;
    }
    queueSeekIntentLog(intent, "commit");
    activeSeekIntentRef.current = null;
    return { ...intent };
  }, [clampSeekIntentSeconds, queueSeekIntentLog]);

  const cancelSeekIntent = useCallback((targetSeconds?: number | null) => {
    const intent = activeSeekIntentRef.current;
    if (!intent) {
      return;
    }
    if (targetSeconds !== undefined && targetSeconds !== null) {
      intent.targetSeconds = clampSeekIntentSeconds(targetSeconds);
      intent.previewSeconds = intent.targetSeconds;
    }
    queueSeekIntentLog(intent, "cancel");
    activeSeekIntentRef.current = null;
  }, [clampSeekIntentSeconds, queueSeekIntentLog]);

  const scheduleSeekIntentResult = useCallback((intent: SeekIntentState | null, targetSeconds: number) => {
    if (!intent) {
      return;
    }
    clearSeekIntentResultTimer();
    seekIntentResultTimerRef.current = window.setTimeout(() => {
      seekIntentResultTimerRef.current = null;
      const actualSeconds = currentAbsolutePlaybackSeconds();
      queueSeekIntentLog(intent, "result", {
        targetSeconds,
        previewSeconds: targetSeconds,
        resultDeltaMS: Math.floor((actualSeconds - targetSeconds) * 1000),
      });
    }, 750);
  }, [clearSeekIntentResultTimer, currentAbsolutePlaybackSeconds, queueSeekIntentLog]);

  const handoffPlaybackSession = useCallback(async (
    targetItemID: string,
    startPositionMs: number,
    maxVideoBitrate: number | undefined,
    stopPositionMs?: number,
    handoffReason?: PlaybackHandoffReason,
  ) => {
    if (!sessionId || !targetItemID) {
      throw new Error("Missing playback context");
    }

    const sourceSessionId = sessionId;
    const shouldUseServerHandoff = targetItemID === itemId;
    assertActivePlaybackSession(sourceSessionId);
    const createRequest = {
      item_id: targetItemID,
      profile: buildProfile(maxVideoBitrate),
      start_position_ms: startPositionMs,
      handoff_from_session_id: shouldUseServerHandoff ? sourceSessionId : undefined,
      handoff_position_ms: shouldUseServerHandoff ? startPositionMs : undefined,
    };
    const nextSession = await createPlaybackSession(shouldUseServerHandoff && handoffReason
      ? { ...createRequest, handoff_reason: handoffReason }
      : createRequest);

    try {
      assertActivePlaybackSession(sourceSessionId);
    } catch (error) {
      await reportPlaybackEvent(nextSession.data.id, {
        event: "stop",
        position_ms: startPositionMs,
      }).catch(() => {});
      throw error;
    }

    const stopSourceSession = () => reportPlaybackEvent(sourceSessionId, {
      event: "stop",
      position_ms: stopPositionMs ?? startPositionMs,
    }).catch(() => {});

    if (shouldUseServerHandoff) {
      await stopSourceSession();
    } else {
      void stopSourceSession();
    }

    navigate(`/play/${nextSession.data.id}`, { replace: true });
  }, [assertActivePlaybackSession, buildProfile, itemId, navigate, sessionId]);

  const replacePlaybackSession = useCallback(async (
    positionMs: number,
    maxVideoBitrate: number | undefined,
  ) => {
    if (!itemId) {
      throw new Error("Missing playback context");
    }
    await handoffPlaybackSession(itemId, positionMs, maxVideoBitrate);
  }, [handoffPlaybackSession, itemId]);

  const createResumeItemRecovery = useCallback(async (preferredItemId?: string, maxVideoBitrate?: number) => {
    const profile = buildProfile(maxVideoBitrate ?? selectedMaxVideoBitrate);
    const triedItemIds = new Set<string>();
    let cursor: string | undefined;
    let attempts = 0;
    let lastExpiredError: unknown;

    do {
      const resumeItems = await getPlaybackResumeItems({
        limit: EXPIRED_SESSION_RESUME_RECOVERY_PAGE_LIMIT,
        cursor,
      });
      const preferredResume = preferredItemId
        ? resumeItems.data.find((candidate) => resumableItemId(candidate) === preferredItemId)
        : null;
      const orderedResumeItems = preferredResume
        ? [preferredResume, ...resumeItems.data.filter((candidate) => candidate !== preferredResume)]
        : resumeItems.data;

      for (const resumeItem of orderedResumeItems) {
        const candidateItemId = resumableItemId(resumeItem);
        if (triedItemIds.has(candidateItemId)) {
          continue;
        }
        triedItemIds.add(candidateItemId);
        attempts += 1;

        try {
          const nextSession = await createPlaybackSession({
            item_id: candidateItemId,
            profile,
            start_position_ms: Math.max(0, resumeItem.attributes.position_ms),
          });
          return nextSession.data;
        } catch (error) {
          if (isPlaybackSessionExpiredError(error)) {
            lastExpiredError = error;
            if (attempts >= EXPIRED_SESSION_RESUME_RECOVERY_MAX_ATTEMPTS) {
              break;
            }
            continue;
          }
          throw error;
        }
      }

      if (attempts >= EXPIRED_SESSION_RESUME_RECOVERY_MAX_ATTEMPTS) {
        break;
      }
      cursor = resumeItems.page.next_cursor ?? undefined;
    } while (cursor);

    const response = await getPlaybackSessions({ limit: 1 });
    const latest = response.data[0];
    if (!latest) {
      if (lastExpiredError) {
        throw lastExpiredError;
      }
      throw new Error("No playback sessions to recover");
    }
    return latest;
  }, [buildProfile, selectedMaxVideoBitrate]);

  const createExpiredSessionRecovery = useCallback(async (sourceSessionId: string) => {
    const checkpoint = readCheckpoint(sourceSessionId);
    const recoveryItemId = itemId ?? checkpoint?.itemId;
    if (!recoveryItemId) {
      return createResumeItemRecovery();
    }

    const video = videoRef.current;
    const livePositionMs = video
      ? Math.floor(getAbsoluteVideoTime(video) * 1000)
      : Math.floor(currentTimeRef.current * 1000);
    const positionMs = Math.max(
      0,
      checkpoint?.positionMs ?? 0,
      stableSessionPositionMS(livePositionMs),
    );
    const maxVideoBitrate = normalizeRequestedBitrateCap(
      selectedMaxVideoBitrate ?? checkpoint?.maxVideoBitrate,
    );
    try {
      const nextSession = await refreshPlaybackSession(sourceSessionId, {
        item_id: recoveryItemId,
        profile: buildProfile(maxVideoBitrate),
        start_position_ms: positionMs,
      });
      return nextSession.data;
    } catch (error) {
      if (isPlaybackSessionExpiredError(error)) {
        return createResumeItemRecovery(recoveryItemId, maxVideoBitrate);
      }
      throw error;
    }
  }, [
    buildProfile,
    createResumeItemRecovery,
    getAbsoluteVideoTime,
    itemId,
    selectedMaxVideoBitrate,
    stableSessionPositionMS,
  ]);

  const triggerNativeHlsRestartRecovery = useCallback(async (recoveryReason: string) => {
    if (
      !sessionId
      || !itemId
      || !nativeHlsPlaybackRef.current
      || !playbackStartedRef.current
      || runtimeRenegotiatingRef.current
      || playbackSessionSwitchActive()
      || seekInFlightRef.current
      || nativeSeekInFlightRef.current
      || nextEpisodeStartingRef.current
      || userPausedRef.current
    ) {
      return false;
    }

    const video = videoRef.current;
    if (!video || video.paused || video.ended) {
      return false;
    }

    const switchToken = beginPlaybackSessionSwitch("native_hls_restart_recovery");
    if (switchToken === null) {
      queueClientPlaybackLogRef.current("native_hls_restart_recovery_suppressed_session_switch", "session");
      return false;
    }

    const sourceSessionId = sessionId;
    const positionMs = stableSessionPositionMS(Math.floor(getAbsoluteVideoTime(video) * 1000));
    const maxVideoBitrate = normalizeRequestedBitrateCap(requestedMaxVideoBitrateRef.current);

    setRecoveringSession(true);
    setBuffering(true);
    setAwaitingPlaybackStart(true);
    setReadyToStart(false);
    setPlayerError(null);
    clearStartupPlayTimer();
    queueClientPlaybackLogRef.current(`native_hls_restart_recovery_${recoveryReason}`, "session", {
      flushImmediately: true,
      flushReason: `native_hls_restart_recovery_${recoveryReason}`,
    });

    try {
      const nextSession = await refreshPlaybackSession(sourceSessionId, {
        item_id: itemId,
        profile: buildProfile(maxVideoBitrate),
        start_position_ms: positionMs,
        handoff_position_ms: positionMs,
        handoff_reason: "session_refresh",
      });
      await applyRecoveredPlaybackSession(sourceSessionId, nextSession.data, switchToken, {
        forceNativeHlsReload: true,
        recoveryReason,
      });
      return true;
    } catch (error) {
      releasePlaybackSessionSwitch(switchToken);
      if (!isStalePlaybackHandoffError(error)) {
        queueClientPlaybackLogRef.current(`native_hls_restart_recovery_failed_${recoveryReason}`, "session", {
          flushImmediately: true,
          flushReason: `native_hls_restart_recovery_failed_${recoveryReason}`,
        });
      }
      setRecoveringSession(false);
      setBuffering(false);
      setAwaitingPlaybackStart(false);
      setReadyToStart(video.paused);
      return false;
    }
  }, [
    applyRecoveredPlaybackSession,
    beginPlaybackSessionSwitch,
    buildProfile,
    clearStartupPlayTimer,
    getAbsoluteVideoTime,
    itemId,
    playbackSessionSwitchActive,
    releasePlaybackSessionSwitch,
    sessionId,
    stableSessionPositionMS,
  ]);

  const clearRuntimeRecoveryRetryTimer = useCallback(() => {
    if (runtimeRecoveryRetryTimerRef.current !== null) {
      window.clearTimeout(runtimeRecoveryRetryTimerRef.current);
      runtimeRecoveryRetryTimerRef.current = null;
    }
  }, []);

  const recoverPlaybackRuntime = useCallback(async (fallbackMessage: string) => {
    if (!sessionId || !itemId || runtimeRenegotiatingRef.current) {
      return;
    }
    if (nativeHlsPlaybackRef.current) {
      queueClientPlaybackLogRef.current("native_hls_runtime_recovery_ignored", "session");
      reportClientPlaybackStateRef.current?.("native_hls_runtime_recovery_ignored");
      setRecoveringSession(false);
      setBuffering(false);
      setAwaitingPlaybackStart(false);
      setReadyToStart(false);
      setPlayerError(fallbackMessage);
      return;
    }
    const switchToken = beginPlaybackSessionSwitch("runtime_recovery");
    if (switchToken === null) {
      queueClientPlaybackLogRef.current("runtime_recovery_suppressed_session_switch", "session");
      return;
    }
    if (
      seekInFlightRef.current
      || nextEpisodeStartingRef.current
    ) {
      releasePlaybackSessionSwitch(switchToken);
      queueClientPlaybackLogRef.current("runtime_recovery_suppressed_session_switch", "session");
      return;
    }
    if (runtimeRecoveryCountRef.current >= 2) {
      releasePlaybackSessionSwitch(switchToken);
      setRecoveringSession(false);
      setBuffering(false);
      setAwaitingPlaybackStart(false);
      setReadyToStart(false);
      setPlayerError(fallbackMessage);
      return;
    }

    runtimeRenegotiatingRef.current = true;
    runtimeRecoveryCountRef.current += 1;
    lastRuntimeRecoveryAtMSRef.current = Date.now();
    setRecoveringSession(true);
    setPlayerError(null);
    setBuffering(true);
    setAwaitingPlaybackStart(true);
    setReadyToStart(false);
    clearStartupPlayTimer();

    if (hlsRetryTimerRef.current !== null) {
      window.clearTimeout(hlsRetryTimerRef.current);
      hlsRetryTimerRef.current = null;
    }

    const video = videoRef.current;
    const overridePositionMS = runtimeRecoveryPositionOverrideMSRef.current;
    runtimeRecoveryPositionOverrideMSRef.current = null;
    const rawPositionMs = video
      ? Math.floor(getAbsoluteVideoTime(video) * 1000)
      : Math.floor(currentTimeRef.current * 1000);
    const positionMs = overridePositionMS !== null
      ? Math.max(0, Math.floor(overridePositionMS))
      : stableSessionPositionMS(rawPositionMs);
    const sourceSessionId = sessionId;

    try {
      const recovered = await recoverPlaybackDelivery(
        sourceSessionId,
        positionMs,
        hlsGenerationIdRef.current ?? delivery?.hls_generation_id,
      );
      assertActivePlaybackSession(sourceSessionId);
      queueClientPlaybackLogRef.current("runtime_recovery_delivery_command", "session");
      const previousGenerationID = hlsGenerationIdRef.current;
      const refreshedGenerationID = recovered.data.attributes.hls_generation_id?.trim();
      if (refreshedGenerationID) {
        hlsGenerationIdRef.current = refreshedGenerationID;
      }
      const replacementOriginSeconds = playbackSessionTimelineOriginSeconds(recovered.data.attributes);
      adoptVerifiedNativeHlsTimeline(
        sourceSessionId,
        positionMs / 1000,
        replacementOriginSeconds,
      );
      const manifestUrl = `/api/v1/playback/sessions/${sourceSessionId}/manifest`;
      const currentVideo = videoRef.current;
      // The recovery may or may not have replaced the generation. Only a
      // replacement moves the timeline origin, and only then must the element
      // be rebuilt; a same-generation recovery keeps its clock and its buffer.
      const generationReplaced = !!refreshedGenerationID
        && !!previousGenerationID
        && refreshedGenerationID !== previousGenerationID;
      if (hlsRef.current && generationReplaced) {
        rebuildMediaElementForGenerationSwap("recover_delivery");
      } else if (hlsRef.current) {
        hlsRef.current.stopLoad();
        hlsRef.current.loadSource(manifestUrl);
        // Load the replacement from its first fragment, then restore the exact
        // point inside that fragment on the generation-relative clock.
        setPendingSeek(positionMs / 1000);
        hlsRef.current.startLoad(0);
      } else if (currentVideo) {
        currentVideo.src = manifestUrl;
        currentVideo.load();
      }
      if (currentVideo && !generationReplaced && !userPausedRef.current) {
        void currentVideo.play().catch(() => {
          autoplayBlockedRef.current = true;
          setStartupAutoplayBlocked(true);
        });
      }
      hlsRestartHandoffTriggeredRef.current = false;
      hlsNetworkOutageStartedAtRef.current = null;
      hlsNetworkRetryRef.current = 0;
      hlsBadSegmentErrorCountRef.current = 0;
      runtimeRenegotiatingRef.current = false;
      setRecoveringSession(false);
      setBuffering(true);
      setAwaitingPlaybackStart(false);
      setReadyToStart(false);
      releasePlaybackSessionSwitch(switchToken);
      return;
    } catch (error) {
      let recoveryFailure = error;
      if (isStalePlaybackHandoffError(recoveryFailure)) {
        releasePlaybackSessionSwitch(switchToken);
        return;
      }
      if (isPlaybackSessionExpiredError(recoveryFailure)) {
        try {
          const nextSession = await createExpiredSessionRecovery(sourceSessionId);
          runtimeRenegotiatingRef.current = false;
          await applyRecoveredPlaybackSession(sourceSessionId, nextSession, switchToken);
          return;
        } catch (recoveryError) {
          if (isStalePlaybackHandoffError(recoveryError)) {
            releasePlaybackSessionSwitch(switchToken);
            return;
          }
          recoveryFailure = recoveryError;
        }
      }
      if (!playbackStartedRef.current) {
        try {
          const nextSession = await createExpiredSessionRecovery(sourceSessionId);
          runtimeRenegotiatingRef.current = false;
          await applyRecoveredPlaybackSession(sourceSessionId, nextSession, switchToken);
          return;
        } catch (recoveryError) {
          if (isStalePlaybackHandoffError(recoveryError)) {
            releasePlaybackSessionSwitch(switchToken);
            return;
          }
          recoveryFailure = recoveryError;
        }
      }
      runtimeRenegotiatingRef.current = false;
      if (isTemporaryPlaybackRecoveryError(recoveryFailure)) {
        releasePlaybackSessionSwitch(switchToken);
        runtimeRecoveryCountRef.current = Math.max(0, runtimeRecoveryCountRef.current - 1);
        setRecoveringSession(false);
        setBuffering(false);
        setAwaitingPlaybackStart(false);
        setReadyToStart(video?.paused ?? false);
        setPlayerError(null);
        clearRuntimeRecoveryRetryTimer();
        runtimeRecoveryRetryTimerRef.current = window.setTimeout(() => {
          recoverPlaybackRuntimeRef.current?.(fallbackMessage);
        }, 5_000);
        return;
      }
      setRecoveringSession(false);
      setBuffering(false);
      setAwaitingPlaybackStart(false);
      setReadyToStart(false);
      releasePlaybackSessionSwitch(switchToken);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      setPlayerError(fallbackMessage);
    }
  }, [
    adoptVerifiedNativeHlsTimeline,
    applyRecoveredPlaybackSession,
    assertActivePlaybackSession,
    beginPlaybackSessionSwitch,
    createExpiredSessionRecovery,
    clearStartupPlayTimer,
    clearRuntimeRecoveryRetryTimer,
    delivery?.hls_generation_id,
    getAbsoluteVideoTime,
    itemId,
    rebuildMediaElementForGenerationSwap,
    releasePlaybackSessionSwitch,
    sessionId,
    setPendingSeek,
    stableSessionPositionMS,
  ]);

  async function handleCopyPlaybackDebug() {
    if (!sessionId) {
      setCopyDebugStatus("failed");
      return;
    }
    const clientState = buildClientPlaybackState("manual_debug_copy");
    void reportPlaybackEventWithAdaptation({
      event: "client_state",
      position_ms: clientState.current_time_ms,
      client_state: clientState,
    });
    const latestBuildFreshness = await buildFreshness.refresh();
    const payload = {
      captured_at: new Date().toISOString(),
      session_id: sessionId,
      item_id: itemId ?? null,
      url: window.location.href,
      user_agent: navigator.userAgent,
      web_client: {
        current: {
          version: CLIENT_BUILD_INFO.version,
          commit: CLIENT_BUILD_INFO.commit,
          build_date: CLIENT_BUILD_INFO.buildDate,
          build_id: CLIENT_BUILD_INFO.buildId,
          stamp: formatBuildDateStamp(CLIENT_BUILD_INFO.buildDate),
        },
        deployed: latestBuildFreshness.expectedBuild ? {
          version: latestBuildFreshness.expectedBuild.version ?? null,
          commit: latestBuildFreshness.expectedBuild.commit ?? null,
          build_date: latestBuildFreshness.expectedBuild.date ?? null,
          build_id: latestBuildFreshness.expectedBuild.build_id ?? null,
          stamp: buildIdentityDateStamp(latestBuildFreshness.expectedBuild),
        } : null,
        stale: latestBuildFreshness.stale,
        update_available: latestBuildFreshness.stale,
        last_checked_at: latestBuildFreshness.lastCheckedAt,
        check_error: latestBuildFreshness.error?.message ?? null,
      },
      player: {
        buffering_visible: showBufferingIndicator,
        playing,
        awaiting_playback_start: awaitingPlaybackStart,
        ready_to_start: readyToStart,
        player_error: playerError,
        decoded_video: formatDecodedResolution(decodedVideoSize),
      },
      delivery: delivery ? {
        state: delivery.state,
        playback_health: delivery.playback_health,
        delivery_type: delivery.delivery_type,
        transcode_state: delivery.transcode_state,
        target_video_bitrate: delivery.target_video_bitrate,
        requested_max_video_bitrate: delivery.requested_max_video_bitrate,
        segment_duration_seconds: delivery.segment_duration_seconds,
        initial_buffer_seconds: delivery.initial_buffer_seconds,
        active_variant_name: delivery.active_variant_name,
        stall_count: delivery.stall_count,
        last_bandwidth_bps: delivery.last_bandwidth_bps,
      } : null,
      client_state: clientState,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopyDebugStatus("copied");
    } catch {
      setCopyDebugStatus("failed");
    }
  }

  const reportBandwidthSample = useCallback(() => {
    if (!sessionId || runtimeRenegotiatingRef.current || !playbackStartedRef.current) {
      return;
    }
    if (nativeHlsPlaybackRef.current) {
      return;
    }
    const measuredBandwidth = getMeasuredBandwidth();
    if (measuredBandwidth <= 0) {
      return;
    }
    recordBandwidthObservation(measuredBandwidth);
    const now = Date.now();
    const previous = lastBandwidthReportValueRef.current;
    const deltaRatio = previous > 0
      ? Math.abs(measuredBandwidth - previous) / previous
      : 1;
    if (now-lastBandwidthReportAtRef.current < BANDWIDTH_REPORT_INTERVAL_MS && deltaRatio < BANDWIDTH_REPORT_DELTA_RATIO) {
      return;
    }

    lastBandwidthReportAtRef.current = now;
    lastBandwidthReportValueRef.current = measuredBandwidth;

    const video = videoRef.current;
    const positionMs = Math.floor(getAbsoluteVideoTime(video) * 1000);
    recordObservedPositionMS(positionMs);
    void reportPlaybackEventWithAdaptation({
      event: "bandwidth_sample",
      position_ms: positionMs,
      measured_bandwidth_bps: measuredBandwidth,
      variant_name: activeObservedVariantName,
    });
  }, [
    activeObservedVariantName,
    getAbsoluteVideoTime,
    getMeasuredBandwidth,
    recordBandwidthObservation,
    recordObservedPositionMS,
    reportPlaybackEventWithAdaptation,
    sessionId,
  ]);

  useEffect(() => {
    attemptStartupPlaybackRef.current = attemptStartupPlayback;
  }, [attemptStartupPlayback]);

  useEffect(() => {
    requestStartupPlaybackRef.current = requestStartupPlayback;
  }, [requestStartupPlayback]);

  useEffect(() => {
    reportBandwidthSampleRef.current = reportBandwidthSample;
  }, [reportBandwidthSample]);

  useEffect(() => {
    reportClientPlaybackStateRef.current = (reason: string) => {
      reportClientPlaybackState(reason);
    };
  }, [reportClientPlaybackState]);

  const handleRecoveredHlsNetworkOutage = useCallback(() => {
    const outageStartedAt = hlsNetworkOutageStartedAtRef.current;
    hlsNetworkOutageStartedAtRef.current = null;
    if (outageStartedAt !== null) {
      queueClientPlaybackLogRef.current("hls_network_recovered", "hls_event");
    }
  }, []);

  useEffect(() => {
    recoverPlaybackRuntimeRef.current = (fallbackMessage: string) => {
      void recoverPlaybackRuntime(fallbackMessage);
    };
  }, [recoverPlaybackRuntime]);

  useEffect(() => {
    nativeHlsLowQualitySinceRef.current = null;
    nativeHlsLowQualityRecoveryCountRef.current = 0;
    nativeHlsLowQualityCooldownUntilRef.current = 0;
  }, [currentSessionResourceId]);

  useEffect(() => {
    if (!buildFreshness.stale) {
      nativeHlsBuildRecoveryKeyRef.current = null;
      return;
    }
    if (!nativeHlsDelivery || !currentSessionResourceId || !itemId) {
      return;
    }
    if (!nativeHlsPlaybackRef.current || !playbackStartedRef.current || !playing || userPausedRef.current) {
      return;
    }

    const buildKey = nativeHlsRestartBuildKey(buildFreshness.expectedBuild);
    if (nativeHlsBuildRecoveryKeyRef.current === buildKey) {
      return;
    }
    nativeHlsBuildRecoveryKeyRef.current = buildKey;
    void triggerNativeHlsRestartRecovery("build_stale").then((started) => {
      if (!started && nativeHlsBuildRecoveryKeyRef.current === buildKey) {
        nativeHlsBuildRecoveryKeyRef.current = null;
      }
    });
  }, [
    buildFreshness.expectedBuild,
    buildFreshness.stale,
    currentSessionResourceId,
    itemId,
    nativeHlsDelivery,
    playing,
    triggerNativeHlsRestartRecovery,
  ]);

  useEffect(() => {
    const checkNativeHlsQuality = () => {
      const snapshot = nativeHlsLowQualitySnapshot(delivery?.variants, decodedVideoSize);
      const video = videoRef.current;
      if (
        !nativeHlsDelivery
        || snapshot === null
        || currentSessionResourceId === null
        || !itemId
        || !nativeHlsPlaybackRef.current
        || !playbackStartedRef.current
        || !playing
        || buffering
        || awaitingPlaybackStart
        || recoveringSession
        || pendingSeekTime !== null
        || seekSwitching
        || qualitySwitching
        || playerError
        || bufferedAheadSeconds < NATIVE_HLS_LOW_QUALITY_MIN_BUFFER_SECONDS
        || !video
        || video.paused
        || video.ended
        || video.seeking
      ) {
        nativeHlsLowQualitySinceRef.current = null;
        return;
      }

      if (nativeHlsLowQualityRecoveryCountRef.current >= NATIVE_HLS_LOW_QUALITY_MAX_RECOVERIES) {
        return;
      }

      const now = Date.now();
      if (now < nativeHlsLowQualityCooldownUntilRef.current) {
        return;
      }

      if (nativeHlsLowQualitySinceRef.current === null) {
        nativeHlsLowQualitySinceRef.current = now;
        queueClientPlaybackLogRef.current(
          `native_hls_low_quality_watch decoded=${snapshot.decodedHeight} target=${snapshot.targetHeight} threshold=${snapshot.thresholdHeight}`,
          "session",
        );
        return;
      }

      if (now - nativeHlsLowQualitySinceRef.current < NATIVE_HLS_LOW_QUALITY_STUCK_MS) {
        return;
      }

      nativeHlsLowQualitySinceRef.current = null;
      nativeHlsLowQualityRecoveryCountRef.current += 1;
      nativeHlsLowQualityCooldownUntilRef.current = now + NATIVE_HLS_LOW_QUALITY_RECOVERY_COOLDOWN_MS;
      queueClientPlaybackLogRef.current(
        `native_hls_low_quality_stuck decoded=${snapshot.decodedHeight} target=${snapshot.targetHeight}`,
        "session",
        {
          flushImmediately: true,
          flushReason: "native_hls_low_quality_stuck",
        },
      );
      void triggerNativeHlsRestartRecovery("low_quality_stuck").then((started) => {
        if (!started) {
          nativeHlsLowQualityRecoveryCountRef.current = Math.max(
            0,
            nativeHlsLowQualityRecoveryCountRef.current - 1,
          );
          nativeHlsLowQualityCooldownUntilRef.current = Date.now() + 30_000;
        }
      });
    };

    checkNativeHlsQuality();
    const timer = window.setInterval(checkNativeHlsQuality, NATIVE_HLS_LOW_QUALITY_WATCHDOG_INTERVAL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [
    awaitingPlaybackStart,
    bufferedAheadSeconds,
    buffering,
    currentSessionResourceId,
    decodedVideoSize,
    delivery?.variants,
    itemId,
    nativeHlsDelivery,
    pendingSeekTime,
    playerError,
    playing,
    qualitySwitching,
    recoveringSession,
    seekSwitching,
    triggerNativeHlsRestartRecovery,
  ]);

  useEffect(() => {
    setSeekHoverTime(null);
    setSeekHoverOffsetPx(null);
    setSeekInteractionValue(null);
    setTrickplayTrack(null);
    setTrickplayRetryTick(0);
    trickplayRetryAfterRef.current = 0;
    trickplayFetchInFlightRef.current = null;
  }, [sessionId, setSeekInteractionValue]);

  useEffect(() => {
    if (!trickplayPlaylistPath) {
      return;
    }
    void loadTrickplayTrack();
  }, [loadTrickplayTrack, trickplayPlaylistPath]);

  useEffect(() => {
    if (!trickplayPlaylistPath || !trickplayTrack?.placeholder) {
      return;
    }
    const retryDelay = Math.max(0, trickplayRetryAfterRef.current - Date.now());
    const timeout = window.setTimeout(() => {
      void loadTrickplayTrack(true).finally(() => {
        setTrickplayRetryTick((current) => current + 1);
      });
    }, retryDelay);
    return () => window.clearTimeout(timeout);
  }, [loadTrickplayTrack, trickplayPlaylistPath, trickplayRetryTick, trickplayTrack]);

  useEffect(() => {
    if (!currentItem || currentItemType !== "episode") {
      setSeriesEpisodes([]);
      return;
    }

    if (!currentItemSeriesID && !currentItemSeriesTitle) {
      setSeriesEpisodes([]);
      return;
    }

    let cancelled = false;
    void getPlaybackEpisodeContext(currentItem).then((episodes) => {
      if (!cancelled) {
        setSeriesEpisodes(episodes);
      }
    }).catch(() => {
      if (!cancelled) {
        setSeriesEpisodes([]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    currentItem,
    currentItem?.id,
    currentItemSeriesID,
    currentItemSeriesTitle,
    currentItemType,
  ]);

  const handleSubtitleSelectChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      setSelectedSubtitleId(value === "" ? null : value);
    },
    [],
  );

  const handleBufferingStart = useCallback(() => {
    const video = videoRef.current;
    if (userPausedRef.current && video?.paused) {
      return;
    }
    setReadyToStart(false);
    clearBufferingIndicatorTimer();
    if (!playbackStartedRef.current || awaitingPlaybackStart) {
      setBuffering(true);
    } else {
      bufferingIndicatorTimerRef.current = window.setTimeout(() => {
        bufferingIndicatorTimerRef.current = null;
        setBuffering(true);
      }, BUFFERING_INDICATOR_DELAY_MS);
    }
    if (!playbackStartedRef.current) {
      return;
    }
    if (bufferingStartedAtRef.current !== null || runtimeRenegotiatingRef.current || seekSwitching || qualitySwitching) {
      return;
    }

    bufferingStartedAtRef.current = Date.now();
    const positionMs = Math.floor(getAbsoluteVideoTime(video) * 1000);
    const nativeHlsPlayback = nativeHlsPlaybackRef.current;
    const measuredBandwidth = nativeHlsPlayback ? 0 : getMeasuredBandwidth();
    const bufferedAheadMS = video
      ? Math.floor(getBufferedAheadSeconds(video, getAbsoluteVideoTime(video), mediaTimelineOffsetSeconds) * 1000)
      : 0;
    recordObservedPositionMS(positionMs);
    void reportPlaybackEventWithAdaptation({
      event: "buffering_start",
      position_ms: positionMs,
      buffer_duration_ms: bufferedAheadMS,
      measured_bandwidth_bps: !nativeHlsPlayback && measuredBandwidth > 0 ? measuredBandwidth : undefined,
      variant_name: nativeHlsPlayback ? undefined : activeObservedVariantName,
    });
  }, [
    activeObservedVariantName,
    awaitingPlaybackStart,
    clearBufferingIndicatorTimer,
    getAbsoluteVideoTime,
    getMeasuredBandwidth,
    qualitySwitching,
    recordObservedPositionMS,
    reportPlaybackEventWithAdaptation,
    seekSwitching,
    mediaTimelineOffsetSeconds,
  ]);

  const handleBufferingEnd = useCallback(() => {
    clearBufferingIndicatorTimer();
    setBuffering(false);
    setAwaitingPlaybackStart(false);
    setReadyToStart(false);
    const video = videoRef.current;
    if (video) {
      const absoluteTime = getAbsoluteVideoTime(video);
      recordObservedPositionMS(Math.floor(absoluteTime * 1000));
      setCurrentTime(absoluteTime);
      clearPendingSeekIfReached(absoluteTime);
    }

    const startedAt = bufferingStartedAtRef.current;
    bufferingStartedAtRef.current = null;
    if (startedAt === null || runtimeRenegotiatingRef.current || seekSwitching || qualitySwitching) {
      return;
    }
    const positionMs = Math.floor(getAbsoluteVideoTime(video) * 1000);
    const nativeHlsPlayback = nativeHlsPlaybackRef.current;
    const measuredBandwidth = nativeHlsPlayback ? 0 : getMeasuredBandwidth();
    const stallDurationMS = Math.max(0, Date.now() - startedAt);
    recordObservedPositionMS(positionMs);
    void reportPlaybackEventWithAdaptation({
      event: "buffering_end",
      position_ms: positionMs,
      buffer_duration_ms: stallDurationMS,
      measured_bandwidth_bps: !nativeHlsPlayback && measuredBandwidth > 0 ? measuredBandwidth : undefined,
      variant_name: nativeHlsPlayback ? undefined : activeObservedVariantName,
    });
  }, [
    activeObservedVariantName,
    clearPendingSeekIfReached,
    clearBufferingIndicatorTimer,
    getAbsoluteVideoTime,
    getMeasuredBandwidth,
    qualitySwitching,
    recordObservedPositionMS,
    reportPlaybackEventWithAdaptation,
    seekSwitching,
    setReadyToStart,
  ]);

  useEffect(() => {
    const hls = hlsRef.current;
    if (!hls) {
      return;
    }
    // Native ABR: hls.js owns level selection (it has built-in EWMA +
    // hysteresis). We only hold a one-time conservative startup cap, then
    // release to fully automatic (-1) once playback is established with a
    // healthy buffer, and never hand-drive nextAutoLevel/nextLoadLevel again.
    // The previous per-tick multi-controller oscillated max<->ultra on fast
    // connections (#393). The cap write is idempotent so a re-run never
    // re-pokes hls.js with an unchanged value (which would re-emit
    // LEVEL_SWITCHING and re-create a storm).
    // Lift a stall-recovery ratchet once the buffer has been deep and
    // stall-free long enough (#667) — otherwise one startup wobble pins the
    // session to a low rung forever regardless of measured bandwidth.
    if (shouldReleaseStallRecoveryCap({
      stallRecoveryLevelCap: stallRecoveryLevelCapRef.current,
      bufferedAheadSeconds,
      msSinceLastStall: Date.now() - (lastStallAtMsRef.current ?? 0),
    })) {
      stallRecoveryLevelCapRef.current = null;
      queueClientPlaybackLogRef.current("hls_stall_recovery_cap_released");
    }
    const { cap, releaseStartupCap } = resolveNativeAbrCap({
      startupLevelCap: startupLevelCapRef.current,
      stallRecoveryLevelCap: stallRecoveryLevelCapRef.current,
      awaitingPlaybackStart,
      playbackStarted: playbackStartedRef.current,
      bufferedAheadSeconds,
      releaseBufferSeconds: BUFFER_PROTECT_HOLD_SECONDS,
    });
    if (releaseStartupCap && startupLevelCapRef.current !== null) {
      startupLevelCapRef.current = null;
    }
    if (cap !== abrCommandedCapRef.current) {
      hls.autoLevelCapping = cap;
      abrCommandedCapRef.current = cap;
    }
    // Once the startup cap has released to fully-automatic, native hls.js ABR
    // climbs one rung at a time, which is needlessly slow on an over-provisioned
    // link (it crawled medium->...->1080p over ~26s on a 14-157Mbps LAN, #399).
    // When the buffer is healthy and measured bandwidth comfortably clears a
    // higher rung, jump straight to the bandwidth-appropriate level. This is
    // one-directional (down stays with native starvation) and clamps to
    // autoLevelCapping, so on the distinct-resolution ladder (#395) it cannot
    // recreate the max<->ultra flap and never exceeds the sustainable ceiling.
    if (cap < 0) {
      const currentHlsLevel = Number.isInteger(hls.currentLevel) && hls.currentLevel >= 0
        ? hls.currentLevel
        : (currentLevel ?? -1);
      const fastUpshiftTarget = resolveFastUpshiftLevel({
        variantBitrates: (delivery?.variants ?? []).map((variant) => variant.bandwidth),
        currentLevel: currentHlsLevel,
        autoLevelCapping: hls.autoLevelCapping ?? -1,
        measuredBandwidthBps: getMeasuredBandwidth(),
        bufferedAheadSeconds,
        playbackStarted: playbackStartedRef.current,
        awaitingPlaybackStart,
        buffering,
        blocked: pendingSeekTime !== null
          || seekSwitching
          || qualitySwitching
          || runtimeRenegotiatingRef.current
          || userPausedRef.current,
      });
      if (fastUpshiftTarget >= 0 && fastUpshiftTarget !== lastFastUpshiftLevelRef.current) {
        hls.nextAutoLevel = fastUpshiftTarget;
        hls.nextLoadLevel = fastUpshiftTarget;
        lastFastUpshiftLevelRef.current = fastUpshiftTarget;
      }
    } else {
      lastFastUpshiftLevelRef.current = -1;
    }
  }, [
    awaitingPlaybackStart,
    bufferedAheadSeconds,
    buffering,
    currentLevel,
    delivery?.variants,
    getMeasuredBandwidth,
    pendingSeekTime,
    qualitySwitching,
    seekSwitching,
  ]);

  useEffect(() => {
    const debugWindow = window as PlaybackDebugWindow;
    const readDebugState = () => {
      const hls = hlsRef.current;
      return {
        currentLevel,
        bufferedAheadSeconds,
        buffering,
        awaitingPlaybackStart,
        playing,
        startupLevelCap: startupLevelCapRef.current,
        stallRecoveryLevelCap: stallRecoveryLevelCapRef.current,
        autoLevelCapping: hls?.autoLevelCapping ?? null,
        nextAutoLevel: hls?.nextAutoLevel ?? null,
        loadLevel: hls?.loadLevel ?? null,
        nextLoadLevel: hls?.nextLoadLevel ?? null,
        bandwidthEstimate: hls?.bandwidthEstimate ?? null,
        levels: hls?.levels?.map((level, index) => ({
          index,
          bitrate: level.bitrate,
          width: level.width,
          height: level.height,
          name: level.name,
        })) ?? [],
        subtitleTrack: hls?.subtitleTrack ?? null,
        subtitleTracks: hls?.subtitleTracks?.map((track, index) => {
          const subtitleTrack = track as MediaPlaylist & { forced?: boolean };
          return {
            index,
            id: subtitleTrack.id,
            name: subtitleTrack.name,
            lang: subtitleTrack.lang,
            default: subtitleTrack.default,
            forced: subtitleTrack.forced ?? null,
          };
        }) ?? [],
      };
    };
    debugWindow.__knotfinPlaybackDebug = readDebugState;
    return () => {
      if (debugWindow.__knotfinPlaybackDebug === readDebugState) {
        delete debugWindow.__knotfinPlaybackDebug;
      }
    };
  }, [
    awaitingPlaybackStart,
    bufferedAheadSeconds,
    buffering,
    currentLevel,
    playing,
  ]);

  // Load manifest from canonical playback session route
  useEffect(() => {
    if (!currentSessionResourceId || !videoRef.current) return;

    const video = videoRef.current;
    // Remove listeners from the previous media load. The typed restore
    // request itself deliberately survives the intentional generation
    // reload and is consumed only after its target becomes seekable.
    const pendingPositionRestoreHandler = nativeHlsPositionRestoreListenerRef.current;
    if (pendingPositionRestoreHandler) {
      video.removeEventListener("loadedmetadata", pendingPositionRestoreHandler);
      video.removeEventListener("durationchange", pendingPositionRestoreHandler);
      video.removeEventListener("progress", pendingPositionRestoreHandler);
      video.removeEventListener("canplay", pendingPositionRestoreHandler);
      video.removeEventListener("seeked", pendingPositionRestoreHandler);
      video.removeEventListener("timeupdate", pendingPositionRestoreHandler);
      nativeHlsPositionRestoreListenerRef.current = null;
    }
    const manifestUrl = `/api/v1/playback/sessions/${currentSessionResourceId}/manifest`;
    const startSeconds = sessionStartSecondsRef.current;
    const nativeHlsDelivery = nativeHlsDeliveryRef.current;
    const initialVideoPositionSeconds = nativeHlsDelivery
      ? generationRelativeMediaTimeSeconds(startSeconds, mediaTimelineOffsetSecondsRef.current)
      : startSeconds;
    const initialPlaybackPositionSeconds = startSeconds;
    const nativeInitialCheckpointRequiresPreroll = nativeHlsDelivery
      && mediaTimelineOffsetSecondsRef.current > 0
      && initialVideoPositionSeconds > 0;
    const durationAtLoad = effectiveDurationRef.current;
    let cancelled = false;
    const getAbsoluteVideoTimeForLoadedSource = (targetVideo: HTMLVideoElement | null | undefined) => {
      const offsetSeconds = mediaTimelineOffsetSecondsRef.current;
      const durationSeconds = effectiveDurationRef.current;
      if (!targetVideo || !Number.isFinite(targetVideo.currentTime)) {
        return clampPlaybackSeconds(offsetSeconds, durationSeconds);
      }
      return clampPlaybackSeconds(offsetSeconds + Math.max(0, targetVideo.currentTime), durationSeconds);
    };

    setPlayerError(null);
    setStartupAutoplayBlocked(false);
    if (sessionAtEndRef.current) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      playbackCompletedRef.current = true;
      video.pause();
      video.removeAttribute("src");
      video.load();
      setCurrentTime(durationAtLoad);
      setPendingSeek(null);
      setPlaybackEnded(true);
      setPlaying(false);
      setBuffering(false);
      setAwaitingPlaybackStart(false);
      setReadyToStart(true);
      setAudioTracks([]);
      setSelectedAudioTrackIndex(-1);
      return () => {
        cancelled = true;
      };
    }
    setBuffering(true);
    setAwaitingPlaybackStart(true);
    setReadyToStart(false);
    setStartupAutoplayBlocked(false);
    setCurrentTime(initialPlaybackPositionSeconds);
    setPendingSeek(
      nativeInitialCheckpointRequiresPreroll
        ? initialPlaybackPositionSeconds
        : (nativeHlsDelivery ? null : initialVideoPositionSeconds),
    );
    setDecodedVideoSize(null);
    setAudioTracks([]);
    setSelectedAudioTrackIndex(-1);
    selectedAudioTrackIdRef.current = deliverySelectedAudioTrackIdRef.current;
    setSelectedAudioTrackId(selectedAudioTrackIdRef.current);
    audioTrackUserSelectedRef.current = false;
    audioTrackAutoSelectedRef.current = false;
    playbackStartedRef.current = false;
    startupPlayRequestRef.current = false;
    startupResumeSeekSettledRef.current = false;
    anchorFragmentDurationSecondsRef.current = null;
    autoplayBlockedRef.current = false;
    startupLevelCapRef.current = startupLevelHintRef.current >= 0 ? startupLevelHintRef.current : null;
    stallRecoveryLevelCapRef.current = null;
    abrCommandedCapRef.current = null;
    lastFastUpshiftLevelRef.current = -1;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    nativeHlsPlaybackRef.current = false;
    if (hlsRetryTimerRef.current !== null) {
      window.clearTimeout(hlsRetryTimerRef.current);
      hlsRetryTimerRef.current = null;
    }
    hlsNetworkOutageStartedAtRef.current = null;
    clearStartupPlayTimer();

    if (nativeHlsDelivery && shouldVideoUseNativeHls(video)) {
      nativeHlsPlaybackRef.current = true;
      setAudioTracks([]);
      setSelectedAudioTrackIndex(-1);
      selectedAudioTrackIdRef.current = null;
      setSelectedAudioTrackId(null);
      let startupRequested = false;
      let checkpointPrerollRequested = false;
      let checkpointReplayCommitPending = false;
      nativeHlsInitialPositionPendingRef.current = nativeInitialCheckpointRequiresPreroll;
      const applyInitialNativeHlsPosition = (): boolean => {
        if (cancelled || !nativeInitialCheckpointRequiresPreroll) {
          return true;
        }
        // Native WebKit reports the moving package duration here, not the
        // title duration. During startup it can be 0, 0.2, or one partial
        // fragment, so clamping the server checkpoint to video.duration turns
        // a valid relative target back into generation zero. The checkpoint
        // and immutable origin already bound this target to the title and the
        // generation's first segment.
        const targetSeconds = initialVideoPositionSeconds;
        const checkpointReached = video.currentTime >= targetSeconds + 0.25;
        if (checkpointReached && !video.ended) {
          return true;
        }
        if (!checkpointPrerollRequested || video.paused || video.ended) {
          if (checkpointReplayCommitPending) {
            return false;
          }
          checkpointPrerollRequested = true;
          // WebKit grows the progressively produced first fragment only while
          // its native decoder runs, and can require play() before it exposes
          // metadata at all. Keep the element hidden and all progress
          // suppressed until raw time reaches the server-authorized target.
          const replayingEndedPackage = video.ended;
          checkpointReplayCommitPending = replayingEndedPackage;
          const playPromise = video.play();
          if (!replayingEndedPackage) {
            void playPromise.catch(() => {});
            return false;
          }
          void playPromise.then(() => {
            // When a short progressive package ends, WebKit resets the raw
            // clock only after play() commits. Re-check that stable post-play
            // state even when it emits no later readiness event.
            checkpointReplayCommitPending = false;
            if (!cancelled) {
              startNativeHlsPlayback();
            }
          }).catch(() => {
            checkpointReplayCommitPending = false;
          });
        }
        return false;
      };
      const startNativeHlsPlayback = () => {
        if (!applyInitialNativeHlsPosition()) {
          return;
        }
        nativeHlsInitialPositionPendingRef.current = false;
        if (nativeInitialCheckpointRequiresPreroll) {
          // The currentTime assignment that commits the checkpoint can emit
          // `seeking` after this native readiness listener returns. Remember
          // that one already-authorized target so the late media event cannot
          // re-open the ordinary buffering overlay after authority is settled.
          nativeHlsInitialPositionSettledTargetRef.current = initialVideoPositionSeconds;
          setPendingSeek(null);
          // WebKit emits dense waiting/playing churn while a progressive
          // package grows. Cancel any delayed indicator from that hidden
          // priming phase before releasing the UI.
          clearBufferingIndicatorTimer();
          setBuffering(false);
          setAwaitingPlaybackStart(false);
          setReadyToStart(false);
        }
        if (startupRequested || playbackStartedRef.current) {
          return;
        }
        if (nativeInitialCheckpointRequiresPreroll && !video.paused) {
          playbackStartedRef.current = true;
          setPlaying(true);
          return;
        }
        startupRequested = true;
        requestStartupPlaybackRef.current?.();
      };
      video.addEventListener("loadedmetadata", startNativeHlsPlayback);
      video.addEventListener("durationchange", startNativeHlsPlayback);
      video.addEventListener("loadeddata", startNativeHlsPlayback);
      video.addEventListener("canplay", startNativeHlsPlayback);
      video.addEventListener("canplaythrough", startNativeHlsPlayback);
      video.addEventListener("progress", startNativeHlsPlayback);
      video.addEventListener("seeked", startNativeHlsPlayback);
      video.addEventListener("timeupdate", startNativeHlsPlayback);
      video.addEventListener("playing", startNativeHlsPlayback);
      video.addEventListener("ended", startNativeHlsPlayback);
      video.src = manifestUrl;
      video.load();
      applyVerifiedNativeHlsPositionRestoreRef.current?.();
      startNativeHlsPlayback();
      return () => {
        clearStartupPlayTimer();
        video.removeEventListener("loadedmetadata", startNativeHlsPlayback);
        video.removeEventListener("durationchange", startNativeHlsPlayback);
        video.removeEventListener("loadeddata", startNativeHlsPlayback);
        video.removeEventListener("canplay", startNativeHlsPlayback);
        video.removeEventListener("canplaythrough", startNativeHlsPlayback);
        video.removeEventListener("progress", startNativeHlsPlayback);
        video.removeEventListener("seeked", startNativeHlsPlayback);
        video.removeEventListener("timeupdate", startNativeHlsPlayback);
        video.removeEventListener("playing", startNativeHlsPlayback);
        video.removeEventListener("ended", startNativeHlsPlayback);
        nativeHlsInitialPositionPendingRef.current = false;
        nativeHlsInitialPositionSettledTargetRef.current = null;
        const positionRestoreHandler = nativeHlsPositionRestoreListenerRef.current;
        if (positionRestoreHandler) {
          video.removeEventListener("loadedmetadata", positionRestoreHandler);
          video.removeEventListener("durationchange", positionRestoreHandler);
          video.removeEventListener("progress", positionRestoreHandler);
          video.removeEventListener("canplay", positionRestoreHandler);
          video.removeEventListener("seeked", positionRestoreHandler);
          video.removeEventListener("timeupdate", positionRestoreHandler);
          nativeHlsPositionRestoreListenerRef.current = null;
        }
        nativeHlsPlaybackRef.current = false;
        if (resumeAfterSessionSwitchRef.current) {
          return;
        }
        video.pause();
        video.removeAttribute("src");
        video.load();
      };
    }

    void import("hls.js").then((module) => {
      if (cancelled) return;

      const HlsLib = module.default;
      if (!HlsLib.isSupported()) {
        nativeHlsPlaybackRef.current = false;
        setBuffering(false);
        setAwaitingPlaybackStart(false);
        setPlayerError("This browser cannot play HLS streams.");
        resumeAfterSessionSwitchRef.current = false;
        return;
      }

      const hlsConfig = {
        ...buildPlayerHlsConfig(
          startupLevelHintRef.current,
          conservativeStartupRef.current,
        ),
        // Client-side batch coalescing (#757) retired in #768: 12s segments
        // give every client — including native Safari, which can never run a
        // custom loader — large transfers server-side, and the edge cache
        // (#756) serves them from short-RTT disk. The batch loader also
        // routed around that cache, so simplicity and speed pointed the same
        // way. The dormant segments-batch endpoint remains server-side.
        xhrSetup: (xhr: XMLHttpRequest, url?: string) => {
          if (typeof url === "string" && !url.includes("/manifest")) {
            return;
          }
          xhr.addEventListener("loadend", () => {
            try {
              const normalizedGenerationID = xhr.getResponseHeader("X-KnotFin-HLS-Generation-Id")?.trim();
              if (normalizedGenerationID) {
                hlsGenerationIdRef.current = normalizedGenerationID;
              }
            } catch {
              // Ignore aborted probe/network requests; the session resource also carries this ID.
            }
          });
        },
        subtitlePreference: masterPlaylistSubtitlesRef.current
          ? hlsSubtitlePreference(
            availableSubtitleTracksRef.current.find((track) => track.id === selectedSubtitleIdRef.current) ?? null,
          )
          : undefined,
      };
      const hls = new HlsLib(hlsConfig);
      nativeHlsPlaybackRef.current = false;
      if (startupLevelCapRef.current !== null) {
        hls.autoLevelCapping = startupLevelCapRef.current;
        hls.nextAutoLevel = startupLevelCapRef.current;
        hls.nextLoadLevel = startupLevelCapRef.current;
      }
      hlsRef.current = hls;
      const syncHlsAudioTracks = (tracks: MediaPlaylist[] = hls.audioTracks ?? []) => {
        const serverTracks = deliveryAudioTracksRef.current;
        const normalizedTracks = tracks.map((track) => ({
          id: track.id,
          name: serverAudioTrackLabel(serverTracks, track, track.name),
          lang: serverAudioTrackLanguage(serverTracks, track, track.lang),
          default: track.default,
        }));
        setAudioTracks(normalizedTracks);
        const activeTrackIndex = hls.audioTrack;
        setSelectedAudioTrackIndex(activeTrackIndex);
        selectedAudioTrackIdRef.current = audioTrackSessionID(serverTracks, tracks, activeTrackIndex);
        setSelectedAudioTrackId(selectedAudioTrackIdRef.current);

        if (!audioTrackUserSelectedRef.current && !audioTrackAutoSelectedRef.current && normalizedTracks.length > 1) {
          const preferredTrackIndex = selectPreferredAudioTrackIndex(
            normalizedTracks,
            preferredAudioLanguagesFromNavigator(navigator),
          );
          audioTrackAutoSelectedRef.current = true;
          if (preferredTrackIndex >= 0 && preferredTrackIndex !== activeTrackIndex) {
            hls.audioTrack = preferredTrackIndex;
            setSelectedAudioTrackIndex(preferredTrackIndex);
            selectedAudioTrackIdRef.current = audioTrackSessionID(serverTracks, tracks, preferredTrackIndex);
            setSelectedAudioTrackId(selectedAudioTrackIdRef.current);
          }
        }
      };
      const syncHlsSubtitleTrack = () => {
        if (!masterPlaylistSubtitlesRef.current) {
          hls.subtitleTrack = -1;
          return;
        }
        const selectedID = selectedSubtitleIdRef.current;
        const serverTracks = availableSubtitleTracksRef.current;
        const selectedIndex = selectedID
          ? serverTracks.findIndex((track) => track.id === selectedID)
          : -1;
        hls.subtitleTrack = selectedIndex >= 0 ? selectedIndex : -1;
      };
      hls.on(HlsLib.Events.AUDIO_TRACKS_UPDATED, (_event, data) => {
        syncHlsAudioTracks(data.audioTracks);
      });
      hls.on(HlsLib.Events.AUDIO_TRACK_SWITCHED, (_event, data) => {
        if (typeof data.id === "number") {
          setSelectedAudioTrackIndex(data.id);
          selectedAudioTrackIdRef.current = audioTrackSessionID(deliveryAudioTracksRef.current, hls.audioTracks ?? [], data.id);
          setSelectedAudioTrackId(selectedAudioTrackIdRef.current);
        } else {
          setSelectedAudioTrackIndex(hls.audioTrack);
          selectedAudioTrackIdRef.current = audioTrackSessionID(deliveryAudioTracksRef.current, hls.audioTracks ?? [], hls.audioTrack);
          setSelectedAudioTrackId(selectedAudioTrackIdRef.current);
        }
      });
      hls.on(HlsLib.Events.MANIFEST_PARSED, () => {
        syncHlsAudioTracks();
        syncHlsSubtitleTrack();
        mediaRecoveryCountRef.current = 0;
        hlsNetworkRetryRef.current = 0;
        hlsBadSegmentErrorCountRef.current = 0;
        handleRecoveredHlsNetworkOutage();
        if (hlsRetryTimerRef.current !== null) {
          window.clearTimeout(hlsRetryTimerRef.current);
          hlsRetryTimerRef.current = null;
        }
        reportBandwidthSampleRef.current?.();
        attemptStartupPlaybackRef.current?.();
      });
      hls.on(HlsLib.Events.LEVEL_LOADED, (_event, data) => {
        const first = data?.details?.fragments?.[0];
        const duration = typeof first?.duration === "number" && Number.isFinite(first.duration) && first.duration > 0
          ? first.duration
          : null;
        if (duration !== null && anchorFragmentDurationSecondsRef.current === null) {
          // The first loaded playlist carries the anchor fragment; later level
          // loads (ABR switches) describe the same timeline and must not
          // reset it.
          anchorFragmentDurationSecondsRef.current = duration;
        }
      });
      hls.on(HlsLib.Events.SUBTITLE_TRACKS_UPDATED, syncHlsSubtitleTrack);
      hls.on(HlsLib.Events.FRAG_LOADED, () => {
        mediaRecoveryCountRef.current = 0;
        hlsNetworkRetryRef.current = 0;
        hlsBadSegmentErrorCountRef.current = 0;
        startupNoMediaTimeoutCountRef.current = 0;
        handleRecoveredHlsNetworkOutage();
        if (hlsRetryTimerRef.current !== null) {
          window.clearTimeout(hlsRetryTimerRef.current);
          hlsRetryTimerRef.current = null;
        }
        reportBandwidthSampleRef.current?.();
        attemptStartupPlaybackRef.current?.();
      });
      hls.on(HlsLib.Events.BUFFER_APPENDED, () => {
        startupNoMediaTimeoutCountRef.current = 0;
        if (!playbackStartedRef.current && video.buffered.length > 0) {
          const firstStart = Math.max(0, video.buffered.start(0));
          if (!Number.isFinite(video.currentTime) || video.currentTime + 0.05 < firstStart) {
            video.currentTime = firstStart;
          }
          // The pending seek is an absolute title position; the element
          // clock is anchor-relative (#980).
          const bufferedRanges = [];
          for (let index = 0; index < video.buffered.length; index += 1) {
            bufferedRanges.push({
              startSeconds: video.buffered.start(index),
              endSeconds: video.buffered.end(index),
            });
          }
          const pendingTarget = bufferedStartupSeekTarget({
            pendingSeekAbsoluteSeconds: pendingSeekTimeRef.current,
            mediaTimelineOffsetSeconds: mediaTimelineOffsetSecondsRef.current,
            bufferedRanges,
          });
          if (pendingTarget !== null) {
            video.currentTime = pendingTarget;
          }
        }
        alignToBufferedStart(video, mediaTimelineOffsetSecondsRef.current);
        attemptStartupPlaybackRef.current?.();
      });
      hls.on(HlsLib.Events.FRAG_BUFFERED, (_event, data) => {
        const frag = data?.frag;
        if (!frag) {
          return;
        }
        const declaredStart = typeof frag.start === "number" ? frag.start : null;
        const actualStart = typeof frag.startPTS === "number" ? frag.startPTS : null;
        if (declaredStart === null || actualStart === null) {
          return;
        }
        const driftMs = Math.round((actualStart - declaredStart) * 1000);
        if (Math.abs(driftMs) < FRAG_TIMELINE_DRIFT_THRESHOLD_MS) {
          return;
        }
        const sn = typeof frag.sn === "number" ? frag.sn : -1;
        const level = typeof frag.level === "number" ? frag.level : -1;
        queueClientPlaybackLogRef.current(
          `frag_timeline_drift level=${level} sn=${sn} declared_ms=${Math.round(declaredStart * 1000)} actual_ms=${Math.round(actualStart * 1000)} drift_ms=${driftMs}`,
          "frag_timeline_drift",
        );
      });
      // Native ABR: log switches for diagnostics but do NOT intervene. The old
      // anti-downswitch pinning here re-forced a "protected" level whenever
      // hls.js tried to drop quality on a healthy buffer, which fought hls.js's
      // own ABR and oscillated max<->ultra (#393). hls.js owns switching now.
      hls.on(HlsLib.Events.LEVEL_SWITCHING, (_event, data) => {
        queueClientPlaybackLogRef.current(`hls_level_switching_${typeof data?.level === "number" ? data.level : "unknown"}`, "hls_event");
      });
      hls.on(HlsLib.Events.LEVEL_SWITCHED, (_event, data) => {
        queueClientPlaybackLogRef.current(`hls_level_switched_${typeof data?.level === "number" ? data.level : "unknown"}`, "hls_event");
        if (typeof data?.level === "number") {
          noteObservedLevel(data.level);
        }
        reportBandwidthSampleRef.current?.();
      });
      hls.loadSource(manifestUrl);
      hls.attachMedia(video);
      hls.on(HlsLib.Events.ERROR, (_event, data) => {
        queueClientPlaybackLogRef.current(`hls_error_${String(data.type ?? "unknown")}_${String(data.details ?? "unknown")}`, "hls_event");
        const now = Date.now();
        const absoluteTime = getAbsoluteVideoTimeForLoadedSource(video);
        const bufferedAhead = getBufferedAheadSeconds(video, absoluteTime, mediaTimelineOffsetSecondsRef.current);
        const statusCode = getHlsErrorStatusCode(data);
        const badSegmentNetworkError = data.type === "networkError"
          && statusCode !== null
          && statusCode >= 400
          && isHlsSegmentNetworkError(data);
        if (data.type === "mediaError" && data.details === "bufferStalledError") {
          const hlsCurrentLevel = Number.isInteger(hls.currentLevel) && hls.currentLevel >= 0
            ? hls.currentLevel
            : null;
          const stalledLevel = getHlsErrorLevel(data) ?? hlsCurrentLevel;
          const nextCap = resolveStallRecoveryLevelCap({
            currentLevel: stalledLevel,
            levelsCount: hls.levels?.length ?? 0,
            existingCap: stallRecoveryLevelCapRef.current,
          });
          lastStallAtMsRef.current = Date.now();
          if (nextCap !== null && nextCap !== stallRecoveryLevelCapRef.current) {
            stallRecoveryLevelCapRef.current = nextCap;
            const activeCap = Number.isInteger(hls.autoLevelCapping) ? hls.autoLevelCapping : -1;
            const appliedCap = activeCap >= 0 ? Math.min(activeCap, nextCap) : nextCap;
            hls.autoLevelCapping = appliedCap;
            abrCommandedCapRef.current = appliedCap;
            queueClientPlaybackLogRef.current(
              `hls_stall_recovery_cap_${nextCap}_from_${stalledLevel ?? "unknown"}`,
              "hls_event",
            );
          }
        }
        if (data.type === "networkError" && hlsNetworkOutageStartedAtRef.current === null) {
          hlsNetworkOutageStartedAtRef.current = now;
        }
        if (data.type === "networkError" && statusCode === 410) {
          setBuffering(false);
          setAwaitingPlaybackStart(false);
          setPlayerError(null);
          if (playbackStartedRef.current) {
            triggerRuntimeHandoffRecovery("Playback session expired while loading media.");
          } else {
            recoverPlaybackRuntimeRef.current?.(
              "Playback session expired before startup completed.",
            );
          }
          return;
        }
        if (badSegmentNetworkError) {
          if (isPlaybackSessionResourceGoneStatus(statusCode)) {
            hlsBadSegmentErrorCountRef.current += 1;
            const outageStartedAt = hlsNetworkOutageStartedAtRef.current ?? now;
            if (
              bufferedAhead <= HLS_BUFFERED_RETRY_THRESHOLD_SECONDS &&
              (
                hlsBadSegmentErrorCountRef.current >= HLS_BAD_SEGMENT_RETRY_LIMIT
                || now - outageStartedAt >= HLS_BAD_SEGMENT_HANDOFF_AFTER_MS
              )
            ) {
              setBuffering(false);
              setAwaitingPlaybackStart(false);
              setPlayerError(null);
              triggerRuntimeHandoffRecovery("Playback session expired while loading media.");
              return;
            }
          }
        }
        if (!data.fatal && data.type === "networkError") {
          const outageStartedAt = hlsNetworkOutageStartedAtRef.current;
          if (
            outageStartedAt !== null &&
            now - outageStartedAt >= HLS_RESTART_HANDOFF_AFTER_MS &&
            playbackStartedRef.current &&
            bufferedAhead <= HLS_BUFFERED_RETRY_THRESHOLD_SECONDS
          ) {
            triggerRuntimeHandoffRecovery("Playback stalled after a network interruption; reconnecting.");
          }
          return;
        }
        if (!data.fatal) return;
        if (
          data.type === "networkError" &&
          !playbackStartedRef.current &&
          bufferedAhead <= 0 &&
          (statusCode === 404 || statusCode === 409 || statusCode === 410)
        ) {
          setBuffering(false);
          setAwaitingPlaybackStart(false);
          setPlayerError(null);
          recoverPlaybackRuntimeRef.current?.(
            "Playback session expired before startup completed.",
          );
          return;
        }
        const action = resolveFatalHlsAction({
          errorType: data.type,
          errorDetails: String(data.details ?? ""),
          networkRetryCount: hlsNetworkRetryRef.current,
          mediaRecoveryCount: mediaRecoveryCountRef.current,
          networkOutageElapsedMs: hlsNetworkOutageStartedAtRef.current === null
            ? 0
            : now - hlsNetworkOutageStartedAtRef.current,
          bufferedAheadSeconds: bufferedAhead,
          playbackStarted: playbackStartedRef.current,
          networkStallHandoffAfterMs: HLS_RESTART_HANDOFF_AFTER_MS,
        });

        switch (action.type) {
          case "retry_network":
            hlsNetworkRetryRef.current += 1;
            if (bufferedAhead > HLS_BUFFERED_RETRY_THRESHOLD_SECONDS && !video.paused) {
              setBuffering(false);
              setAwaitingPlaybackStart(false);
            } else {
              setBuffering(true);
              setAwaitingPlaybackStart(true);
            }
            setPlayerError(null);
            if (hlsRetryTimerRef.current !== null) {
              window.clearTimeout(hlsRetryTimerRef.current);
            }
            hlsRetryTimerRef.current = window.setTimeout(() => {
              hls.startLoad();
            }, action.delayMs);
            break;
          case "recover_media":
            mediaRecoveryCountRef.current += 1;
            hlsNetworkOutageStartedAtRef.current = null;
            hls.recoverMediaError();
            break;
          case "handoff_session":
            setBuffering(false);
            setAwaitingPlaybackStart(false);
            setPlayerError(null);
            recoverPlaybackRuntimeRef.current?.(action.message);
            break;
        }
      });
    }).catch(() => {
      if (cancelled) return;
      setBuffering(false);
      setAwaitingPlaybackStart(false);
      setPlayerError("The web player failed to load its HLS runtime.");
      resumeAfterSessionSwitchRef.current = false;
    });

    return () => {
      cancelled = true;
      frontierSeekWaitCancelRef.current?.();
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (hlsRetryTimerRef.current !== null) {
        window.clearTimeout(hlsRetryTimerRef.current);
        hlsRetryTimerRef.current = null;
      }
      hlsNetworkOutageStartedAtRef.current = null;
      clearRuntimeRecoveryRetryTimer();
      clearStartupPlayTimer();
      if (resumeAfterSessionSwitchRef.current) {
        return;
      }
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, [
    clearRuntimeRecoveryRetryTimer,
    clearBufferingIndicatorTimer,
    clearStartupPlayTimer,
    currentSessionResourceId,
    handleRecoveredHlsNetworkOutage,
    mediaElementReloadNonce,
    noteObservedLevel,
    sessionId,
    setPendingSeek,
    triggerRuntimeHandoffRecovery,
  ]);

  // Pin the server's timeline origin to the element the media load effect
  // built. An origin arriving before anything is buffered still describes an
  // element with no clock of its own, so it is adopted; once fragments are
  // appended the clock is fixed and a changed origin means the generation was
  // replaced without a rebuild. Adopting it there is the double-count of #846,
  // so the element is rebuilt instead and the refusal is recorded.
  useEffect(() => {
    if (!hlsJsFullTitleDelivery) {
      return;
    }
    const accepted = acceptedResumeAuthorityForSession;
    const acceptedForSession = accepted?.sessionId === currentSessionResourceId
      ? accepted.tuple
      : null;
    const generationId = acceptedForSession?.nextGenerationId
      ?? delivery?.hls_generation_id?.trim()
      ?? null;
    const expectedOriginSeconds = acceptedForSession?.originSeconds
      ?? mediaTimelineOriginSeconds;
    const pinnedToThisElement = elementTimelineOrigin.instance === mediaElementReloadNonce;
    if (
      pinnedToThisElement
      && elementTimelineOrigin.generationId === generationId
      && elementTimelineOrigin.offsetSeconds === expectedOriginSeconds
    ) {
      return;
    }
    // The server retains a generation's origin across a stopped worker, so a
    // change reaching a live element is real whether or not the generation id
    // moved with it (#873). Only an element with no clock of its own can adopt
    // one; anything else has to be rebuilt.
    const video = videoRef.current;
    const clockEstablished = !!video && video.buffered.length > 0;
    if (!pinnedToThisElement || !clockEstablished) {
      setElementTimelineOrigin({
        instance: mediaElementReloadNonce,
        generationId,
        offsetSeconds: expectedOriginSeconds,
      });
      return;
    }
    queueClientPlaybackLogRef.current?.("timeline_origin_change_refused_on_live_element", "hls_event", {
      flushImmediately: true,
      flushReason: "timeline_origin_change_refused",
    });
    rebuildMediaElementForGenerationSwap("timeline_origin_change");
  }, [
    acceptedResumeAuthorityForSession,
    currentSessionResourceId,
    delivery?.hls_generation_id,
    elementTimelineOrigin,
    hlsJsFullTitleDelivery,
    mediaElementReloadNonce,
    mediaTimelineOriginSeconds,
    rebuildMediaElementForGenerationSwap,
  ]);

  // Report progress to backend periodically
  useEffect(() => {
    if (!sessionId) return;

    progressTimerRef.current = setInterval(() => {
      const video = videoRef.current;
      if (
        !video
        || video.paused
        || pendingResumeAuthorityRef.current
        || resumeAuthorityFailedRef.current
        || nativeHlsInitialPositionPendingRef.current
      ) return;

      const positionMS = Math.floor(getAbsoluteVideoTime(video) * 1000);
      recordObservedPositionMS(positionMS);
      writeLocalPlaybackPosition(itemId ?? null, positionMS);
      if (periodicProgressReportInFlightRef.current) {
        return;
      }
      periodicProgressReportInFlightRef.current = true;
      // Routed through reportPlaybackEventWithAdaptation (rather than
      // calling reportPlaybackEvent directly) so a 403 here -- this is the
      // one call in the periodic heartbeat, unconditional and telemetry-
      // independent -- feeds the disowned-session recovery machinery (#707).
      void reportPlaybackEventWithAdaptation({
        event: "progress",
        position_ms: positionMS,
      }).finally(() => {
        periodicProgressReportInFlightRef.current = false;
      });
    }, 5_000); // Every 5 seconds

    return () => {
      if (progressTimerRef.current !== null) {
        window.clearInterval(progressTimerRef.current);
      }
    };
  }, [getAbsoluteVideoTime, itemId, recordObservedPositionMS, reportPlaybackEventWithAdaptation, sessionId]);

  // Autonomous hls.js wedged-session recovery (#383). A silent server worker
  // stall freezes both the playhead and a nearly empty buffer without emitting
  // a fatal hls.js error. Keep the bounded handoff for that proven server-side
  // starvation case, but never grant it authority over native HLS: WebKit owns
  // native buffering and recovery (#401, #785).
  useEffect(() => {
    if (!sessionId) {
      return;
    }
    stallWatchdogMemoryRef.current = initialStallWatchdogMemory();
    const tick = () => {
      const video = videoRef.current;
      const eligible = !!video
        && stallWatchdogMayRecoverDelivery(nativeHlsPlaybackRef.current)
        && pendingResumeAuthorityRef.current === null
        && !video.paused
        && playbackStartedRef.current
        && !userPausedRef.current
        && !seekInFlightRef.current
        && !runtimeRenegotiatingRef.current
        && !nextEpisodeStartingRef.current
        && !hlsRestartHandoffTriggeredRef.current
        && !playbackSessionSwitchActive();
      const positionMS = video ? Math.floor(getAbsoluteVideoTime(video) * 1000) : 0;
      const videoPositionMS = video && Number.isFinite(video.currentTime)
        ? Math.floor(Math.max(0, video.currentTime) * 1000)
        : 0;
      // Furthest buffered position: the "data is still arriving" signal that
      // keeps the watchdog from tearing down a slow-but-progressing stream.
      const bufferedEndMS = video && video.buffered.length > 0
        ? Math.floor(video.buffered.end(video.buffered.length - 1) * 1000)
        : 0;
      const result = evaluateStallWatchdog(
        {
          eligible,
          positionMS: absolutePositionMS(positionMS),
          videoPositionMS: rawVideoPositionMS(videoPositionMS),
          bufferedEndMS: rawVideoPositionMS(bufferedEndMS),
          nowMS: wallClockMS(Date.now()),
        },
        stallWatchdogMemoryRef.current,
      );
      stallWatchdogMemoryRef.current = result.memory;
      if (result.resetRecoveryBudget) {
        // Only restore the budget after playback has actually held. A recovery
        // loop plays a full healthy segment between attempts, so resetting on
        // any progress cleared the counter every cycle and the >= 2 breaker
        // could never trip (#846).
        if (Date.now() - lastRuntimeRecoveryAtMSRef.current >= RUNTIME_RECOVERY_BUDGET_RESTORE_MS) {
          runtimeRecoveryCountRef.current = 0;
        }
      }
      if (result.action === "recover") {
        queueClientPlaybackLogRef.current("no_progress_watchdog_recovery", "session");
        triggerRuntimeHandoffRecovery("Playback stalled with no progress; reconnecting.");
      }
    };
    const timer = window.setInterval(tick, STALL_WATCHDOG_TICK_MS);
    return () => window.clearInterval(timer);
  }, [getAbsoluteVideoTime, playbackSessionSwitchActive, sessionId, triggerRuntimeHandoffRecovery]);

  // Final-flush position on tab hide / page unload so a deploy or tab close
  // can't strand the resume point ~5s behind the actual playhead.
  useEffect(() => {
    if (!sessionId) return;

    const flush = (reason: "pagehide" | "visibility") => {
      const video = videoRef.current;
      if (
        !video
        || pendingResumeAuthorityRef.current
        || resumeAuthorityFailedRef.current
        || nativeHlsInitialPositionPendingRef.current
      ) return;
      queueClientPlaybackLogRef.current(reason === "pagehide" ? "pagehide" : "visibility_hidden", "lifecycle");
      flushClientPlaybackLogsRef.current(true);
      const positionMS = Math.floor(getAbsoluteVideoTime(video) * 1000);
      if (positionMS <= 0) return;
      recordObservedPositionMS(positionMS);
      writeLocalPlaybackPosition(itemId ?? null, positionMS);
      sendPlaybackEventKeepalive(sessionId, {
        event: reason === "pagehide" ? "pause" : "progress",
        position_ms: positionMS,
      });
    };

    const handlePageHide = () => flush("pagehide");
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") flush("visibility");
    };

    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [getAbsoluteVideoTime, itemId, recordObservedPositionMS, sessionId]);

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    const timer = window.setInterval(() => {
      const video = videoRef.current;
      const seekInteractionActive =
        seekPointerActiveRef.current
        || scrubbing
        || previewSeeking
        || seekSwitching
        || qualitySwitching;
      const idlePaused =
        !!video
        && video.paused
        && !buffering
        && !awaitingPlaybackStart
        && !recoveringSession;

      if (seekInteractionActive || idlePaused) {
        return;
      }

      void refetchSession().catch(() => {});
    }, 15_000);

    return () => {
      window.clearInterval(timer);
    };
  }, [
    awaitingPlaybackStart,
    buffering,
    previewSeeking,
    qualitySwitching,
    recoveringSession,
    scrubbing,
    seekSwitching,
    refetchSession,
    sessionId,
  ]);

  useEffect(() => {
    if (knownDuration > 0) {
      setDuration(knownDuration);
    }
  }, [knownDuration]);

  useEffect(() => {
    clearResumeAuthorityDeadline();
    pendingResumeAuthorityRef.current = null;
    acceptedResumeAuthorityRef.current = null;
    resumeAuthorityFailedRef.current = false;
    recoverAttemptedRef.current = false;
    stoppedRecoveryAttemptedRef.current = null;
    playbackStartedRef.current = false;
    playbackCompletedRef.current = false;
    userPausedRef.current = false;
    hlsNetworkRetryRef.current = 0;
    hlsNetworkOutageStartedAtRef.current = null;
    hlsRestartHandoffTriggeredRef.current = false;
    hlsBadSegmentErrorCountRef.current = 0;
    runtimeRecoveryCountRef.current = 0;
    mediaRecoveryCountRef.current = 0;
    startupNoMediaTimeoutCountRef.current = 0;
    sessionSwitchInProgressRef.current = null;
    seekInFlightRef.current = false;
    nativeSeekInFlightRef.current = false;
    nativeSeekTargetSecondsRef.current = null;
    nextEpisodeStartingRef.current = false;
    retryCountRef.current = 0;
    bufferingStartedAtRef.current = null;
    runtimeRenegotiatingRef.current = false;
    startupGateReleasedRef.current = false;
    startupBufferDeadlineRef.current = 0;
    startupResumeSeekSettledRef.current = false;
    anchorFragmentDurationSecondsRef.current = null;
    startupPlayRequestRef.current = false;
    previousObservedLevelRef.current = null;
    autoplayBlockedRef.current = false;
    lastClientStateReportRef.current = null;
    if (hlsRef.current) {
      hlsRef.current.autoLevelCapping = -1;
    }
    abrCommandedCapRef.current = -1;
    setRecoveringSession(false);
    setPlaying(false);
    setBuffering(false);
    setPlayerError(null);
    setQualitySwitching(false);
    setSeekSwitching(false);
    setPendingSeek(null);
    setAwaitingPlaybackStart(true);
    setReadyToStart(false);
    setPreviewSeeking(false);
    setCurrentLevel(null);
    setDecodedVideoSize(null);
    setPlaybackEnded(false);
    previewWasPlayingRef.current = false;
    setPreviewTime(null);
    keySeekDirectionRef.current = null;
    seekRailKeyRef.current = null;
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (hlsRetryTimerRef.current !== null) {
      window.clearTimeout(hlsRetryTimerRef.current);
      hlsRetryTimerRef.current = null;
    }
    clearRuntimeRecoveryRetryTimer();
    clearStartupPlayTimer();
    clearStaleStartupRecoveryTimer();
    clearBufferingIndicatorTimer();
    clearNativeSeekSettleTimer();
    return () => {
      clearResumeAuthorityDeadline();
      pendingResumeAuthorityRef.current = null;
      resumeAuthorityFailedRef.current = false;
    };
  }, [
    clearBufferingIndicatorTimer,
    clearNativeSeekSettleTimer,
    clearResumeAuthorityDeadline,
    clearRuntimeRecoveryRetryTimer,
    clearStaleStartupRecoveryTimer,
    clearStartupPlayTimer,
    sessionId,
    setPendingSeek,
    setPreviewTime,
  ]);

  useEffect(() => {
    if (!sessionId || !itemId) return;
    writeCheckpoint({
      sessionId,
      itemId,
      positionMs: Math.floor(currentTime * 1000),
      maxVideoBitrate: selectedMaxVideoBitrate,
    });
  }, [sessionId, itemId, currentTime, selectedMaxVideoBitrate]);

  useEffect(() => {
    if (!sessionId || session.loading || !session.error || recoveringSession) {
      return;
    }

    if (!isPlaybackSessionExpiredError(session.error)) {
      if (!isTemporaryPlaybackRecoveryError(session.error) && retryCountRef.current >= 5) {
        return;
      }
      retryCountRef.current += 1;
      retryTimerRef.current = window.setTimeout(() => {
        session.refetch();
      }, 1000);
      return;
    }

    if (recoverAttemptedRef.current) {
      return;
    }

    const sourceSessionId = sessionId;
    const switchToken = beginPlaybackSessionSwitch("expired_session_recovery");
    if (switchToken === null) {
      queueClientPlaybackLogRef.current("expired_recovery_suppressed_session_switch", "session");
      return;
    }
    recoverAttemptedRef.current = true;
    setRecoveringSession(true);
    setBuffering(true);
    setAwaitingPlaybackStart(true);
    setReadyToStart(false);
    setPlayerError(null);

    createExpiredSessionRecovery(sourceSessionId)
      .then((nextSession) => applyRecoveredPlaybackSession(sourceSessionId, nextSession, switchToken, {
        forceNativeHlsReload: nativeHlsPlaybackRef.current,
        recoveryReason: "expired_session_recovery",
      }))
      .catch((error) => {
        releasePlaybackSessionSwitch(switchToken);
        if (isStalePlaybackHandoffError(error)) {
          return;
        }
        if (isTemporaryPlaybackRecoveryError(error)) {
          recoverAttemptedRef.current = false;
          retryTimerRef.current = window.setTimeout(() => {
            setRecoveringSession(false);
            void session.refetch().catch(() => {});
          }, 2_000);
          return;
        }
        setRecoveringSession(false);
        setBuffering(false);
        setAwaitingPlaybackStart(false);
        setPlayerError("Playback session expired after the server restarted. Start playback again from the item page.");
      });
  }, [
    applyRecoveredPlaybackSession,
    beginPlaybackSessionSwitch,
    createExpiredSessionRecovery,
    releasePlaybackSessionSwitch,
    recoveringSession,
    session,
    session.error,
    session.loading,
    sessionId,
  ]);

  useEffect(() => {
    if (!sessionId || session.loading || !currentSessionResource || recoveringSession) {
      return;
    }

    const attrs = currentSessionResource.attributes;
    if (attrs.state !== "stopped" || !itemId) {
      return;
    }
    if (stoppedRecoveryAttemptedRef.current === sessionId) {
      return;
    }
    const sourceSessionId = sessionId;
    const switchToken = beginPlaybackSessionSwitch("stopped_session_recovery");
    if (switchToken === null) {
      queueClientPlaybackLogRef.current("stopped_recovery_suppressed_session_switch", "session");
      return;
    }
    stoppedRecoveryAttemptedRef.current = sessionId;

    setRecoveringSession(true);
    getPlaybackSessions({ limit: 20, include: "item" }).then((response) => {
      const replacement = response.data.find((candidate) =>
        candidate.id !== sessionId &&
        candidate.relationships.item.data.id === itemId &&
        candidate.attributes.state !== "stopped",
      );
      if (replacement) {
        try {
          assertActivePlaybackSession(sourceSessionId);
        } catch {
          return;
        }
        releasePlaybackSessionSwitch(switchToken);
        navigate(`/play/${replacement.id}`, { replace: true });
        return;
      }

      try {
        assertActivePlaybackSession(sourceSessionId);
      } catch {
        return undefined;
      }

      return refreshPlaybackSession(sourceSessionId, {
        item_id: itemId,
        profile: buildProfile(normalizeRequestedBitrateCap(attrs.requested_max_video_bitrate)),
        start_position_ms: attrs.position_ms,
        handoff_position_ms: attrs.position_ms,
        handoff_reason: "session_refresh",
      }).then((nextSession) => applyRecoveredPlaybackSession(sourceSessionId, nextSession.data, switchToken, {
        forceNativeHlsReload: nativeHlsPlaybackRef.current,
        recoveryReason: "stopped_session_recovery",
      }));
    }).catch(() => {
      releasePlaybackSessionSwitch(switchToken);
      setRecoveringSession(false);
      setPlayerError("Could not resume playback.");
      setBuffering(false);
      setAwaitingPlaybackStart(false);
    });
  }, [
    applyRecoveredPlaybackSession,
    assertActivePlaybackSession,
    beginPlaybackSessionSwitch,
    buildProfile,
    currentSessionResource,
    itemId,
    navigate,
    releasePlaybackSessionSwitch,
    recoveringSession,
    sessionId,
    session.loading,
  ]);

  // A session becomes unusable in two ways that have nothing to do with the
  // diagnostics/error-reporting opt-in (#707): a 403 confirming the server
  // has permanently disowned this player instance for the session (#705 --
  // seen on a client-log flush, which is opt-in, and on playback-event
  // reporting, which is not), or an awaiting_playback_start timeout, which
  // looks identical to the user whether the cause is a disowned session or a
  // host that just can't keep up. Either way, `markPlaybackSessionUnusable`
  // records the trigger reason and this effect re-acquires the session via
  // the same refresh/handoff path used elsewhere in this file.
  // `refreshPlaybackSession` mints a replacement session (a new id, carrying
  // the position over) whenever the caller isn't the session's current
  // client owner, so on success we adopt whatever session id it returns via
  // `applyRecoveredPlaybackSession`. If refresh itself fails, fall back to a
  // brand-new session for the same item resuming at the last known
  // position, and best-effort delete the disowned session so it stops
  // holding server-side resources. Guarded to attempt at most once per
  // session id so this can never loop.
  useEffect(() => {
    if (
      !sessionId ||
      !itemId ||
      playbackSessionOwnershipLostSessionId !== sessionId ||
      recoveringSession
    ) {
      return;
    }
    if (playbackSessionOwnershipRecoveryAttemptedSessionsRef.current.has(sessionId)) {
      return;
    }
    const sourceSessionId = sessionId;
    const recoveryReason = playbackSessionRecoveryReasonRef.current;
    const switchToken = beginPlaybackSessionSwitch(recoveryReason);
    if (switchToken === null) {
      queueClientPlaybackLogRef.current("playback_session_recovery_suppressed_session_switch", "session");
      return;
    }
    playbackSessionOwnershipRecoveryAttemptedSessionsRef.current.add(sourceSessionId);

    const video = videoRef.current;
    const positionMs = stableSessionPositionMS(
      video ? Math.floor(getAbsoluteVideoTime(video) * 1000) : Math.floor(currentTimeRef.current * 1000),
    );
    const maxVideoBitrate = normalizeRequestedBitrateCap(
      requestedMaxVideoBitrateRef.current ?? selectedMaxVideoBitrate,
    );
    const profile = buildProfile(maxVideoBitrate);

    setRecoveringSession(true);
    setBuffering(true);
    setAwaitingPlaybackStart(true);
    setReadyToStart(false);
    setPlayerError(null);
    queueClientPlaybackLogRef.current(`playback_session_recovery_started_${recoveryReason}`, "session");

    refreshPlaybackSession(sourceSessionId, {
      item_id: itemId,
      profile,
      start_position_ms: positionMs,
      handoff_position_ms: positionMs,
      handoff_reason: "session_refresh",
    }).then((nextSession) => applyRecoveredPlaybackSession(sourceSessionId, nextSession.data, switchToken, {
      forceNativeHlsReload: nativeHlsPlaybackRef.current,
      recoveryReason,
    })).catch((refreshError) => {
      if (isStalePlaybackHandoffError(refreshError)) {
        releasePlaybackSessionSwitch(switchToken);
        return;
      }
      // Re-acquiring the same session id failed outright -- fall back to a
      // fresh session for the same item, resuming where we left off.
      // `Promise.resolve(...)` guards this best-effort cleanup from ever
      // throwing synchronously and short-circuiting the fallback below.
      void Promise.resolve(deletePlaybackSession(sourceSessionId)).catch(() => {});
      createPlaybackSession({
        item_id: itemId,
        profile,
        start_position_ms: positionMs,
      }).then((created) => applyRecoveredPlaybackSession(sourceSessionId, created.data, switchToken, {
        forceNativeHlsReload: nativeHlsPlaybackRef.current,
        recoveryReason,
      })).catch((createError) => {
        releasePlaybackSessionSwitch(switchToken);
        if (isStalePlaybackHandoffError(createError)) {
          return;
        }
        setRecoveringSession(false);
        setBuffering(false);
        setAwaitingPlaybackStart(false);
        setReadyToStart(false);
        setPlayerError("Playback session was lost and could not be restored automatically. Start playback again from the item page.");
      });
    });
  }, [
    applyRecoveredPlaybackSession,
    beginPlaybackSessionSwitch,
    buildProfile,
    playbackSessionOwnershipLostSessionId,
    getAbsoluteVideoTime,
    itemId,
    recoveringSession,
    releasePlaybackSessionSwitch,
    selectedMaxVideoBitrate,
    sessionId,
    stableSessionPositionMS,
  ]);

  useEffect(() => {
    clearStaleStartupRecoveryTimer();
    if (
      !sessionId ||
      !itemId ||
      session.loading ||
      !currentSessionResource ||
      currentSessionResource.attributes.state === "stopped" ||
      staleStartupStatusRef.current.recoveringSession ||
      staleStartupStatusRef.current.playerError ||
      playbackStartedRef.current ||
      // A session handed off at the title's end never loads media: the
      // player shows the ended state instead (#990). Nothing is waited on.
      sessionAtEndRef.current
    ) {
      return;
    }

    const scheduleStartupNoMediaCheck = () => {
      staleStartupRecoveryTimerRef.current = window.setTimeout(() => {
        staleStartupRecoveryTimerRef.current = null;
        const startupStatus = staleStartupStatusRef.current;
        const video = videoRef.current;
        const absoluteTime = video && Number.isFinite(video.currentTime)
          ? mediaTimelineOffsetSeconds + Math.max(0, video.currentTime)
          : currentTimeRef.current;
        const bufferedAhead = video
          ? getBufferedAheadSeconds(video, absoluteTime, mediaTimelineOffsetSeconds)
          : 0;
        const hasMediaData = !!video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
        if (
          playbackStartedRef.current ||
          userPausedRef.current ||
          startupStatus.recoveringSession ||
          startupStatus.playerError ||
          sessionAtEndRef.current ||
          playbackCompletedRef.current ||
          hasMediaData ||
          bufferedAhead > 0
        ) {
          return;
        }

        startupNoMediaTimeoutCountRef.current += 1;
        reportClientPlaybackStateRef.current?.("startup_waiting_for_media");
        hlsRef.current?.startLoad?.();
        attemptStartupPlaybackRef.current?.();
        if (startupNoMediaTimeoutCountRef.current >= STARTUP_NO_MEDIA_TIMEOUT_LIMIT) {
          setBuffering(false);
          setAwaitingPlaybackStart(false);
          setReadyToStart(false);
          setPlayerError("Playback failed to start. The browser may not support this stream or the network is still blocking media.");
          return;
        }
        scheduleStartupNoMediaCheck();
      }, STALE_STARTUP_RECOVERY_TIMEOUT_MS);
    };

    scheduleStartupNoMediaCheck();

    return clearStaleStartupRecoveryTimer;
  }, [
    clearStaleStartupRecoveryTimer,
    currentSessionResource,
    itemId,
    mediaTimelineOffsetSeconds,
    session.loading,
    sessionId,
  ]);

  // Bound how long awaiting_playback_start can persist (#707). The watchdog
  // above only catches literally zero media ever arriving; it disarms for
  // the rest of this effect run as soon as any media trickles in
  // (hasMediaData || bufferedAhead > 0), even a single small buffered range.
  // A host that is falling behind on segment production (observed at load
  // average 39.98) can keep a trickle coming without ever crossing the
  // startup buffer threshold, so awaiting_playback_start persists with no
  // watchdog left armed to catch it -- exactly the "parked indefinitely"
  // postmortem this issue is from. This timer is the outer backstop: it
  // does not care whether anything buffered, only how long the player has
  // been waiting to actually start. On expiry, treat it like a confirmed
  // disowned session and hand off to the same recovery machinery, since the
  // two are indistinguishable to the user.
  useEffect(() => {
    clearAwaitingPlaybackStartTimeoutTimer();
    if (
      !sessionId ||
      !itemId ||
      !awaitingPlaybackStart ||
      session.loading ||
      !currentSessionResource ||
      currentSessionResource.attributes.state === "stopped" ||
      staleStartupStatusRef.current.recoveringSession ||
      staleStartupStatusRef.current.playerError ||
      playbackStartedRef.current
    ) {
      return;
    }

    const sourceSessionId = sessionId;
    awaitingPlaybackStartTimeoutTimerRef.current = window.setTimeout(() => {
      awaitingPlaybackStartTimeoutTimerRef.current = null;
      const startupStatus = staleStartupStatusRef.current;
      if (
        playbackStartedRef.current ||
        userPausedRef.current ||
        startupStatus.recoveringSession ||
        startupStatus.playerError ||
        activePlaybackSessionIdRef.current !== sourceSessionId
      ) {
        return;
      }
      queueClientPlaybackLogRef.current("awaiting_playback_start_timeout", "session");
      reportClientPlaybackStateRef.current?.("awaiting_playback_start_timeout");
      markPlaybackSessionUnusable(sourceSessionId, "playback_session_startup_timeout");
    }, AWAITING_PLAYBACK_START_TIMEOUT_MS);

    return clearAwaitingPlaybackStartTimeoutTimer;
  }, [
    awaitingPlaybackStart,
    clearAwaitingPlaybackStartTimeoutTimer,
    currentSessionResource,
    itemId,
    markPlaybackSessionUnusable,
    session.loading,
    sessionId,
  ]);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
      }
      if (hlsRetryTimerRef.current !== null) {
        window.clearTimeout(hlsRetryTimerRef.current);
      }
      clearStartupPlayTimer();
      clearStaleStartupRecoveryTimer();
      clearAwaitingPlaybackStartTimeoutTimer();
      clearPreviewSeekRepeat();
      clearKeyboardSeekRepeat();
      clearKeyboardSeekCommit();
      clearSeekIntentResultTimer();
      clearNativeSeekSettleTimer();
    };
  }, [
    clearAwaitingPlaybackStartTimeoutTimer,
    clearKeyboardSeekCommit,
    clearKeyboardSeekRepeat,
    clearNativeSeekSettleTimer,
    clearPreviewSeekRepeat,
    clearSeekIntentResultTimer,
    clearStaleStartupRecoveryTimer,
    clearStartupPlayTimer,
  ]);

  // Auto-hide controls after 3s of no mouse movement
  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = setTimeout(() => {
      if (playing) setControlsVisible(false);
    }, 3000);
  }, [playing]);

  useEffect(() => {
    showControls();
    return () => {
      if (hideTimerRef.current !== null) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [showControls]);

  const startPreviewSeek = useCallback((deltaSeconds: number, source: SeekIntentSource = "programmatic", input: string | null = null) => {
    const video = videoRef.current;
    if (!video || effectiveDuration <= 0) return;
    if (
      seekInFlightRef.current ||
      nativeSeekInFlightRef.current ||
      seekSwitching ||
      (nativeHlsPlaybackRef.current && video.seeking)
    ) {
      return;
    }
    clearKeyboardSeekCommit();

    const wasPreviewSeeking = previewSeekingRef.current;
    if (!previewSeekingRef.current) {
      previewWasPlayingRef.current = !video.paused;
      previewSeekingRef.current = true;
      setPreviewSeeking(true);
      if (source === "document_keyboard" && previewWasPlayingRef.current) {
        const command = beginPlaybackCommand("pause", "seek_preview", input);
        const sourcePositionSeconds = getAbsoluteVideoTime(video);
        queuePlaybackCommandLog(command, "request", { sourcePositionSeconds });
        previewPausedPlaybackRef.current = true;
        queuePlaybackCommandLog(command, "execute", { sourcePositionSeconds });
        video.pause();
        queuePlaybackCommandLog(command, "result", { sourcePositionSeconds });
      } else {
        previewPausedPlaybackRef.current = false;
      }
    }

    const liveCurrentTime = getAbsoluteVideoTime(video);
    const base = seekPreviewTimeRef.current ?? liveCurrentTime;
    const next = Math.max(0, Math.min(base + deltaSeconds, effectiveDuration));
    setPreviewTime(next);
    updateTimelinePreviewGeometryForTime(next);
    setControlsVisible(true);
    if (wasPreviewSeeking) {
      updateSeekIntent(next, { source, input, previewSeconds: next });
    } else {
      beginSeekIntent(source, input, next, {
        previewSeconds: next,
        resumeExpected: previewWasPlayingRef.current,
      });
    }
  }, [
    beginSeekIntent,
    beginPlaybackCommand,
    clearKeyboardSeekCommit,
    effectiveDuration,
    getAbsoluteVideoTime,
    queuePlaybackCommandLog,
    seekSwitching,
    setPreviewTime,
    updateSeekIntent,
    updateTimelinePreviewGeometryForTime,
  ]);

  const cancelPreviewSeek = useCallback(() => {
    clearKeyboardSeekCommit();
    clearKeyboardSeekRepeat();
    clearPreviewSeekRepeat();
    cancelSeekIntent(seekPreviewTimeRef.current);
    keySeekDirectionRef.current = null;
    previewSeekingRef.current = false;
    const shouldResumePreviewPlayback = previewPausedPlaybackRef.current && previewWasPlayingRef.current;
    previewPausedPlaybackRef.current = false;
    previewWasPlayingRef.current = false;
    setPreviewSeeking(false);
    setPreviewTime(null);
    setSeekHoverOffsetPx(null);
    if (shouldResumePreviewPlayback) {
      const video = videoRef.current;
      if (video) {
        const command = beginPlaybackCommand("play", "seek_preview", "cancel");
        const sourcePositionSeconds = getAbsoluteVideoTime(video);
        queuePlaybackCommandLog(command, "request", {
          sourcePositionSeconds,
          shouldResume: true,
        });
        queuePlaybackCommandLog(command, "execute", {
          sourcePositionSeconds,
          shouldResume: true,
        });
        void video.play()
          .then(() => {
            queuePlaybackCommandLog(command, "result", {
              sourcePositionSeconds: getAbsoluteVideoTime(video),
              shouldResume: true,
            });
          })
          .catch(() => {
            queuePlaybackCommandLog(command, "blocked", {
              blockedReason: "play_rejected",
              sourcePositionSeconds,
              shouldResume: true,
            });
          });
      }
    }
  }, [
    beginPlaybackCommand,
    cancelSeekIntent,
    clearKeyboardSeekCommit,
    clearKeyboardSeekRepeat,
    clearPreviewSeekRepeat,
    getAbsoluteVideoTime,
    queuePlaybackCommandLog,
    setPreviewTime,
  ]);

  const commitPreviewSeek = useCallback(async () => {
    clearKeyboardSeekCommit();
    clearKeyboardSeekRepeat();
    clearPreviewSeekRepeat();
    const target = seekPreviewTimeRef.current;
    const shouldResume = previewWasPlayingRef.current;

    previewSeekingRef.current = false;
    setPreviewSeeking(false);
    previewPausedPlaybackRef.current = false;
    previewWasPlayingRef.current = false;
    setPreviewTime(null);
    setSeekHoverOffsetPx(null);
    if (target === null) return;

    const intent = commitSeekIntent(target, shouldResume);
    await seekToPositionRef.current(
      target,
      shouldResume,
      intent ? playbackCommandSourceForSeekIntent(intent.source) : "seek_commit",
      intent?.input ?? null,
    );
    scheduleSeekIntentResult(intent, target);
  }, [
    clearKeyboardSeekCommit,
    clearPreviewSeekRepeat,
    clearKeyboardSeekRepeat,
    commitSeekIntent,
    scheduleSeekIntentResult,
    setPreviewTime,
  ]);

  const schedulePreviewSeekCommit = useCallback(() => {
    clearKeyboardSeekCommit();
    keyboardSeekCommitTimerRef.current = window.setTimeout(() => {
      keyboardSeekCommitTimerRef.current = null;
      void commitPreviewSeek();
    }, KEYBOARD_SEEK_COMMIT_DEBOUNCE_MS);
  }, [clearKeyboardSeekCommit, commitPreviewSeek]);

  const startKeyboardPreviewSeek = useCallback((
    direction: "backward" | "forward",
    input: string,
    source: "document_keyboard" | "seek_rail_keyboard" = "document_keyboard",
  ) => {
    const deltaSeconds = direction === "backward" ? -10 : 10;
    keySeekDirectionRef.current = direction;
    clearKeyboardSeekRepeat();
    startPreviewSeek(deltaSeconds, source, input);
    keyboardSeekRepeatTimerRef.current = window.setInterval(() => {
      if (keySeekDirectionRef.current !== direction) {
        clearKeyboardSeekRepeat();
        return;
      }
      startPreviewSeek(deltaSeconds, source, input);
    }, KEYBOARD_SEEK_REPEAT_INTERVAL_MS);
  }, [clearKeyboardSeekRepeat, startPreviewSeek]);

  useEffect(() => () => {
    if (bufferedSeekGraceTimerRef.current !== null) {
      window.clearTimeout(bufferedSeekGraceTimerRef.current);
    }
  }, []);

  const finishKeyboardPreviewSeek = useCallback((direction: "backward" | "forward") => {
    if (keySeekDirectionRef.current !== direction) {
      return;
    }
    keySeekDirectionRef.current = null;
    clearKeyboardSeekRepeat();
    // A target inside the buffer seeks in tens of milliseconds, so commit it
    // now; a further tap simply chains another seek (#992). The coalescing
    // delay is kept for targets outside the buffer, where the seek replaces
    // the session or re-anchors and two taps should become one.
    const video = videoRef.current;
    const target = seekPreviewTimeRef.current;
    if (
      video
      && target !== null
      && getBufferedAheadSeconds(video, target, mediaTimelineOffsetSecondsRef.current) > 0
    ) {
      clearKeyboardSeekCommit();
      void commitPreviewSeek();
      return;
    }
    schedulePreviewSeekCommit();
  }, [clearKeyboardSeekCommit, clearKeyboardSeekRepeat, commitPreviewSeek, schedulePreviewSeekCommit]);

  useEffect(() => {
    function handleWindowBlur() {
      cancelPreviewSeek();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "hidden") {
        cancelPreviewSeek();
      }
    }

    window.addEventListener("blur", handleWindowBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("blur", handleWindowBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [cancelPreviewSeek]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const video = videoRef.current;
      if (!video) return;
      if (e.defaultPrevented || e.isComposing || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const isArrowSeekShortcut = e.key === "ArrowLeft" || e.key === "ArrowRight";
      if (isArrowSeekShortcut
        ? shouldIgnoreDocumentArrowSeekShortcut(e.target)
        : shouldIgnoreDocumentPlaybackShortcut(e.target)) return;

      switch (e.key) {
        case " ":
        case "k":
          e.preventDefault();
          if (e.repeat) break;
          togglePlay(e.key === " " ? "document_keyboard_space" : "document_keyboard_k", e.key === " " ? "space" : "k");
          break;
        case "f":
          if (e.repeat) break;
          if (document.fullscreenElement) document.exitFullscreen();
          else document.documentElement.requestFullscreen();
          break;
        case "m":
          if (e.repeat) break;
          applyAudioState(volumeLevel, !muted);
          break;
        case "ArrowLeft":
        case "j":
          e.preventDefault();
          if (keySeekDirectionRef.current === null) {
            startKeyboardPreviewSeek("backward", e.key);
          }
          break;
        case "ArrowRight":
        case "l":
          e.preventDefault();
          if (keySeekDirectionRef.current === null) {
            startKeyboardPreviewSeek("forward", e.key);
          }
          break;
        case "Escape":
          void handleStop("document_keyboard_escape", "Escape");
          break;
      }
      showControls();
    }

    function handleKeyUp(e: KeyboardEvent) {
      if (e.defaultPrevented || e.isComposing) {
        return;
      }
      if (keySeekDirectionRef.current === null && shouldIgnoreDocumentPlaybackShortcut(e.target)) {
        return;
      }
      switch (e.key) {
        case "ArrowLeft":
        case "j":
          finishKeyboardPreviewSeek("backward");
          break;
        case "ArrowRight":
        case "l":
          finishKeyboardPreviewSeek("forward");
          break;
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showControls, startKeyboardPreviewSeek, finishKeyboardPreviewSeek, applyAudioState, muted, volumeLevel]);

  async function handleStop(
    source: PlaybackCommandSource = "stop_button",
    input: string | null = "click",
    stopPositionMs?: number,
  ) {
    const video = videoRef.current;
    const sourcePositionSeconds = video ? getAbsoluteVideoTime(video) : currentTimeRef.current;
    const command = beginPlaybackCommand("stop", source, input);
    queuePlaybackCommandLog(command, "request", {
      sourcePositionSeconds,
      sourceSessionId: sessionId ?? null,
      shouldResume: false,
    });
    if (sessionId) {
      if (video) {
        queuePlaybackCommandLog(command, "execute", {
          sourcePositionSeconds,
          sourceSessionId: sessionId,
          shouldResume: false,
        });
        frontierSeekWaitCancelRef.current?.();
        hlsRef.current?.stopLoad?.();
        video.pause();
        setPlaying(false);
        setBuffering(false);
        setAwaitingPlaybackStart(false);
        setReadyToStart(true);
        const positionMs = stopPositionMs ?? stableSessionPositionMS(Math.floor(getAbsoluteVideoTime(video) * 1000));
        void reportPlaybackEvent(sessionId, {
          event: "stop",
          position_ms: positionMs,
        }).catch(() => {});
        queuePlaybackCommandLog(command, "result", {
          sourcePositionSeconds: getAbsoluteVideoTime(video),
          sourceSessionId: sessionId,
          shouldResume: false,
        });
      }
    } else {
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "missing_session",
        sourcePositionSeconds,
        sourceSessionId: null,
        shouldResume: false,
      });
    }
    navigate(-1);
  }

  async function handleQualityChange(event: ChangeEvent<HTMLSelectElement>) {
    const requestedQuality = event.target.value;
    const video = videoRef.current;
    const sourcePositionSeconds = video ? getAbsoluteVideoTime(video) : currentTimeRef.current;
    const command = beginPlaybackCommand("session_handoff", "quality_change", requestedQuality);
    queuePlaybackCommandLog(command, "request", {
      sourcePositionSeconds,
      sourceSessionId: sessionId ?? null,
      shouldResume: !(video?.paused ?? !playing),
    });
    if (!sessionId || !itemId) {
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "missing_playback_context",
        sourcePositionSeconds,
        sourceSessionId: sessionId ?? null,
      });
      return;
    }
    if (nativeHlsPlaybackRef.current) {
      queueClientPlaybackLogRef.current("native_hls_quality_change_ignored", "session");
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "native_hls_browser_owned_quality",
        sourcePositionSeconds,
        sourceSessionId: sessionId,
      });
      return;
    }

    const option = QUALITY_OPTIONS.find((candidate) => candidate.id === requestedQuality);
    if (!option || option.id === currentQuality) {
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: option ? "same_quality" : "unknown_quality",
        sourcePositionSeconds,
        sourceSessionId: sessionId,
      });
      return;
    }
    const switchToken = beginPlaybackSessionSwitch("quality_change");
    if (switchToken === null) {
      queueClientPlaybackLogRef.current("quality_change_suppressed_session_switch", "session");
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "session_switch",
        sourcePositionSeconds,
        sourceSessionId: sessionId,
      });
      return;
    }

    const rawPositionMs = video
      ? Math.floor(getAbsoluteVideoTime(video) * 1000)
      : Math.floor(currentTime * 1000);
    const positionMs = stableSessionPositionMS(rawPositionMs);
    setPlayerError(null);
    setBuffering(true);
    setAwaitingPlaybackStart(true);
    setReadyToStart(false);
    setQualitySwitching(true);
    let navigated = false;

    try {
      video?.pause();
      queuePlaybackCommandLog(command, "execute", {
        sourcePositionSeconds,
        sourceSessionId: sessionId,
      });

      await replacePlaybackSession(positionMs, option.maxVideoBitrate);
      queuePlaybackCommandLog(command, "result", {
        targetPositionSeconds: positionMs / 1000,
        sourcePositionSeconds,
        sourceSessionId: sessionId,
      });
      navigated = true;
    } catch (error) {
      if (isStalePlaybackHandoffError(error)) {
        return;
      }
      setBuffering(false);
      setAwaitingPlaybackStart(false);
      setPlayerError("Could not switch stream quality.");
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "quality_change_failed",
        sourcePositionSeconds,
        sourceSessionId: sessionId,
      });
    } finally {
      if (!navigated) {
        releasePlaybackSessionSwitch(switchToken);
        setQualitySwitching(false);
      }
    }
  }

  function handleAudioTrackChange(event: ChangeEvent<HTMLSelectElement>) {
    const nextTrackIndex = Number(event.target.value);
    if (!Number.isInteger(nextTrackIndex) || nextTrackIndex < 0 || nextTrackIndex >= audioTracks.length) {
      return;
    }
    audioTrackUserSelectedRef.current = true;
    setSelectedAudioTrackIndex(nextTrackIndex);
    selectedAudioTrackIdRef.current = audioTrackSessionID(deliveryAudioTracksRef.current, hlsRef.current?.audioTracks ?? [], nextTrackIndex);
    setSelectedAudioTrackId(selectedAudioTrackIdRef.current);
    if (hlsRef.current) {
      hlsRef.current.audioTrack = nextTrackIndex;
    }
  }

  function currentProfile() {
    return currentStreamProfile();
  }

  // Resolves "buffered" once the target lies inside video.buffered, "superseded"
  // when a stop or media teardown cancels the wait, or "timeout" after
  // HLS_SEEK_FRONTIER_WAIT_TIMEOUT_MS so the caller can fall back to replacing
  // the session (#943).
  function waitForFrontierSeekTarget(
    video: HTMLVideoElement,
    targetSeconds: number,
  ): Promise<"buffered" | "superseded" | "timeout"> {
    const sourceSessionId = activePlaybackSessionIdRef.current;
    return new Promise((resolve) => {
      let settled = false;
      const finish = (outcome: "buffered" | "superseded" | "timeout") => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearInterval(poll);
        window.clearTimeout(deadline);
        if (frontierSeekWaitCancelRef.current === cancel) {
          frontierSeekWaitCancelRef.current = null;
        }
        resolve(outcome);
      };
      const cancel = () => finish("superseded");
      const poll = window.setInterval(() => {
        if (activePlaybackSessionIdRef.current !== sourceSessionId || videoRef.current !== video) {
          finish("superseded");
          return;
        }
        if (isTimeBuffered(video, targetSeconds, mediaTimelineOffsetSecondsRef.current)) {
          finish("buffered");
        }
      }, HLS_SEEK_FRONTIER_POLL_INTERVAL_MS);
      const deadline = window.setTimeout(() => finish("timeout"), HLS_SEEK_FRONTIER_WAIT_TIMEOUT_MS);
      frontierSeekWaitCancelRef.current = cancel;
    });
  }

  async function seekToPosition(
    targetSeconds: number,
    shouldResume = true,
    source: PlaybackCommandSource = "programmatic",
    input: string | null = null,
  ) {
    const video = videoRef.current;
    const rawSourcePositionSeconds = video ? getAbsoluteVideoTime(video) : currentTimeRef.current;
    const sourcePositionSeconds = Number.isFinite(rawSourcePositionSeconds)
      ? Math.max(0, rawSourcePositionSeconds)
      : 0;
    const command = beginPlaybackCommand("seek", source, input);
    queuePlaybackCommandLog(command, "request", {
      targetPositionSeconds: targetSeconds,
      sourcePositionSeconds,
      sourceSessionId: sessionId ?? null,
      shouldResume,
    });
    if (!sessionId || !itemId || effectiveDuration <= 0) {
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "missing_playback_context",
        targetPositionSeconds: targetSeconds,
        sourcePositionSeconds,
        sourceSessionId: sessionId ?? null,
        shouldResume,
      });
      return;
    }
    if (seekInFlightRef.current) {
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "seek_in_flight",
        targetPositionSeconds: targetSeconds,
        sourcePositionSeconds,
        sourceSessionId: sessionId,
        shouldResume,
      });
      return;
    }
    if (nativeSeekInFlightRef.current) {
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "native_hls_seek_in_flight",
        targetPositionSeconds: targetSeconds,
        sourcePositionSeconds,
        sourceSessionId: sessionId,
        shouldResume,
      });
      return;
    }
    const blockedReason = playbackCommandBlockReason();
    if (blockedReason) {
      if (blockedReason === "session_switch") {
        queueClientPlaybackLogRef.current("seek_suppressed_session_switch", "session");
      }
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason,
        targetPositionSeconds: targetSeconds,
        sourcePositionSeconds,
        sourceSessionId: sessionId,
        shouldResume,
      });
      return;
    }
    const boundedSeconds = Math.max(0, Math.min(targetSeconds, effectiveDuration));
    const automaticResumeAuthority = pendingResumeAuthorityRef.current;
    let supersededResumeAuthority = false;
    if (automaticResumeAuthority) {
      // User intent wins synchronously. Replace the automatic target before
      // this function can perform any currentTime write, while retaining the
      // independently accepted generation/origin clock.
      clearResumeAuthorityDeadline();
      const retargeted = retargetResumeAuthority(
        automaticResumeAuthority,
        boundedSeconds,
        shouldResume,
      );
      if (!retargeted) {
        resumeAuthorityFailedRef.current = true;
        setPlayerError("Could not apply that seek to the resumed stream. Try reloading playback.");
        return;
      }
      pendingResumeAuthorityRef.current = retargeted;
      resumeAuthorityFailedRef.current = false;
      armResumeAuthorityDeadline(retargeted);
      supersededResumeAuthority = true;
    } else {
      // An explicit seek is also the recovery path from a terminal automatic
      // resume timeout.
      if (resumeAuthorityFailedRef.current) {
        setPlayerError(null);
      }
      resumeAuthorityFailedRef.current = false;
    }
    if (pendingNativeHlsPositionRestoreRef.current) {
      clearNativeHlsPositionRestoreListener();
      pendingNativeHlsPositionRestoreRef.current = null;
    }
    const targetMs = Math.floor(boundedSeconds * 1000);
    const sourceStopMs = stableSessionPositionMS(Math.floor(sourcePositionSeconds * 1000));
    failedSeekTargetRef.current = null;
    resetObservedPositionMS(targetMs);
    setCurrentTime(boundedSeconds);
    setPendingSeek(boundedSeconds, sourcePositionSeconds);
    setReadyToStart(false);
    if (video && !video.paused && (!nativeHlsPlaybackRef.current || !shouldResume)) {
      video.pause();
      setPlaying(false);
    }

    if (boundedSeconds >= effectiveDuration) {
      if (supersededResumeAuthority) {
        clearResumeAuthorityDeadline();
        pendingResumeAuthorityRef.current = null;
      }
      queuePlaybackCommandLog(command, "execute", {
        targetPositionSeconds: boundedSeconds,
        sourcePositionSeconds,
        sourceSessionId: sessionId,
        shouldResume,
      });
      completePlayback(targetMs);
      queuePlaybackCommandLog(command, "result", {
        targetPositionSeconds: boundedSeconds,
        sourcePositionSeconds,
        sourceSessionId: sessionId,
        shouldResume: false,
      });
      return;
    }

    if (video && nativeHlsPlaybackRef.current && isTimeBuffered(video, boundedSeconds, mediaTimelineOffsetSeconds)) {
      queuePlaybackCommandLog(command, "execute", {
        targetPositionSeconds: boundedSeconds,
        sourcePositionSeconds,
        sourceSessionId: sessionId,
        shouldResume,
      });
      setPlayerError(null);
      markNativeSeekInFlight(boundedSeconds);
      try {
        video.currentTime = toRelativeVideoTime(boundedSeconds);
      } catch {
        releaseNativeSeekInFlight(true);
        setBuffering(false);
        setAwaitingPlaybackStart(false);
        setReadyToStart(video.paused);
        setPlayerError("Could not seek to that point.");
        failedSeekTargetRef.current = boundedSeconds;
        setPendingSeek(null);
        queuePlaybackCommandLog(command, "blocked", {
          blockedReason: "native_seek_rejected",
          targetPositionSeconds: boundedSeconds,
          sourcePositionSeconds,
          sourceSessionId: sessionId,
          shouldResume,
        });
        return;
      }
      setCurrentTime(boundedSeconds);
      if (shouldResume) {
        userPausedRef.current = false;
        setBuffering(true);
        setAwaitingPlaybackStart(true);
        void video.play()
          .then(() => {
            queuePlaybackCommandLog(command, "result", {
              targetPositionSeconds: boundedSeconds,
              sourcePositionSeconds: getAbsoluteVideoTime(video),
              sourceSessionId: sessionId,
              shouldResume,
            });
          })
          .catch(() => {
            autoplayBlockedRef.current = true;
            setStartupAutoplayBlocked(true);
            setBuffering(false);
            setAwaitingPlaybackStart(false);
            queuePlaybackCommandLog(command, "blocked", {
              blockedReason: "play_rejected_after_native_seek",
              targetPositionSeconds: boundedSeconds,
              sourcePositionSeconds: getAbsoluteVideoTime(video),
              sourceSessionId: sessionId,
              shouldResume,
            });
          });
      } else {
        setBuffering(false);
        setAwaitingPlaybackStart(false);
        setReadyToStart(true);
        queuePlaybackCommandLog(command, "result", {
          targetPositionSeconds: boundedSeconds,
          sourcePositionSeconds: getAbsoluteVideoTime(video),
          sourceSessionId: sessionId,
          shouldResume,
        });
      }
      reportPlaybackEvent(sessionId, {
        event: shouldResume ? "resume" : "progress",
        position_ms: targetMs,
        client_state: buildClientPlaybackState("native_hls_seek_commit"),
      }).catch(() => {});
      return;
    }

    const commitBufferedSeek = (clientStateReason: "buffered_seek_commit" | "frontier_seek_commit") => {
      if (!video) {
        return;
      }
      video.currentTime = toRelativeVideoTime(boundedSeconds);
      setCurrentTime(boundedSeconds);
      if (shouldResume) {
        setBuffering(true);
        setAwaitingPlaybackStart(true);
        void video.play()
          .then(() => {
            queuePlaybackCommandLog(command, "result", {
              targetPositionSeconds: boundedSeconds,
              sourcePositionSeconds: getAbsoluteVideoTime(video),
              sourceSessionId: sessionId,
              shouldResume,
            });
          })
          .catch(() => {
            setBuffering(false);
            setAwaitingPlaybackStart(false);
            setPendingSeek(null);
            queuePlaybackCommandLog(command, "blocked", {
              blockedReason: "play_rejected_after_buffered_seek",
              targetPositionSeconds: boundedSeconds,
              sourcePositionSeconds: getAbsoluteVideoTime(video),
              sourceSessionId: sessionId,
              shouldResume,
            });
          });
      } else {
        setBuffering(false);
        setAwaitingPlaybackStart(false);
        setPendingSeek(null);
        setReadyToStart(true);
        queuePlaybackCommandLog(command, "result", {
          targetPositionSeconds: boundedSeconds,
          sourcePositionSeconds: getAbsoluteVideoTime(video),
          sourceSessionId: sessionId,
          shouldResume,
        });
      }
      if (sessionId) {
        reportPlaybackEvent(sessionId, {
          event: shouldResume ? "resume" : "progress",
          position_ms: targetMs,
          client_state: buildClientPlaybackState(clientStateReason),
        }).catch(() => {});
      }
    };

    if (video && isTimeBuffered(video, boundedSeconds, mediaTimelineOffsetSeconds)) {
      queuePlaybackCommandLog(command, "execute", {
        targetPositionSeconds: boundedSeconds,
        sourcePositionSeconds,
        sourceSessionId: sessionId,
        shouldResume,
      });
      const supersedingAuthority = supersededResumeAuthority
        ? pendingResumeAuthorityRef.current
        : null;
      if (supersedingAuthority) {
        video.currentTime = supersedingAuthority.tuple.targetRawSeconds;
        setCurrentTime(boundedSeconds);
        supersedingAuthority.assigned = true;
        userPausedRef.current = !shouldResume;
        setBuffering(true);
        setAwaitingPlaybackStart(shouldResume);
        // Media events or the bounded authority retry will corroborate the
        // assignment. Until then there is no play or progress report.
        return;
      }
      if (bufferedSeekGraceTimerRef.current !== null) {
        window.clearTimeout(bufferedSeekGraceTimerRef.current);
      }
      setBufferedSeekGrace(true);
      bufferedSeekGraceTimerRef.current = window.setTimeout(() => {
        bufferedSeekGraceTimerRef.current = null;
        setBufferedSeekGrace(false);
      }, BUFFERING_INDICATOR_DELAY_MS);
      commitBufferedSeek("buffered_seek_commit");
      return;
    }

    const frontierHls = hlsRef.current;
    const frontierGapSeconds = video && frontierHls && !nativeHlsPlaybackRef.current && !supersededResumeAuthority
      ? seekFrontierGapSeconds(video, boundedSeconds, mediaTimelineOffsetSeconds)
      : null;
    if (video && frontierHls && frontierGapSeconds !== null && frontierGapSeconds <= HLS_SEEK_FRONTIER_GRACE_SECONDS) {
      queuePlaybackCommandLog(command, "execute", {
        targetPositionSeconds: boundedSeconds,
        sourcePositionSeconds,
        sourceSessionId: sessionId,
        shouldResume,
      });
      queueClientPlaybackLogRef.current(
        `seek_frontier_wait gap_ms=${Math.round(frontierGapSeconds * 1000)} target_ms=${targetMs}`,
        "session",
      );
      setPlayerError(null);
      setBuffering(true);
      setAwaitingPlaybackStart(shouldResume);
      // hls.js keeps filling forward from the buffer end while the element is
      // paused; startLoad at the playhead makes it re-read the live playlist
      // now instead of at its next 6-12s poll.
      frontierHls.stopLoad();
      frontierHls.startLoad(video.currentTime);
      seekInFlightRef.current = true;
      const outcome = await waitForFrontierSeekTarget(video, boundedSeconds);
      seekInFlightRef.current = false;
      if (outcome === "buffered") {
        commitBufferedSeek("frontier_seek_commit");
        return;
      }
      if (outcome === "superseded") {
        queuePlaybackCommandLog(command, "blocked", {
          blockedReason: "frontier_wait_superseded",
          targetPositionSeconds: boundedSeconds,
          sourcePositionSeconds,
          sourceSessionId: sessionId,
          shouldResume,
        });
        return;
      }
      queueClientPlaybackLogRef.current("seek_frontier_wait_timeout", "session");
    }

    setPlayerError(null);
    if (supersededResumeAuthority) {
      clearResumeAuthorityDeadline();
      pendingResumeAuthorityRef.current = null;
    }
    const switchToken = beginPlaybackSessionSwitch("seek_replace");
    if (switchToken === null) {
      setPendingSeek(null);
      queueClientPlaybackLogRef.current("seek_suppressed_session_switch", "session");
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "session_switch",
        targetPositionSeconds: boundedSeconds,
        sourcePositionSeconds,
        sourceSessionId: sessionId,
        shouldResume,
      });
      return;
    }
    queuePlaybackCommandLog(command, "execute", {
      targetPositionSeconds: boundedSeconds,
      sourcePositionSeconds,
      sourceSessionId: sessionId,
      shouldResume,
    });
    userPausedRef.current = false;
    setStartupAutoplayBlocked(false);
    setBuffering(true);
    setAwaitingPlaybackStart(true);
    setReadyToStart(false);
    setSeekSwitching(true);
    seekInFlightRef.current = true;
    const sourceSessionId = sessionId;
    const requestId = ++seekRequestRef.current;
    let navigated = false;

    try {
      if (shouldResume) {
        resumeAfterSessionSwitchRef.current = true;
      } else {
        resumeAfterSessionSwitchRef.current = false;
      }

      assertActivePlaybackSession(sourceSessionId);
      const nextSession = await createPlaybackSession({
        item_id: itemId,
        profile: currentProfile(),
        start_position_ms: targetMs,
      });

      try {
        assertActivePlaybackSession(sourceSessionId);
      } catch (error) {
        await reportPlaybackEvent(nextSession.data.id, {
          event: "stop",
          position_ms: targetMs,
        }).catch(() => {});
        throw error;
      }

      await reportPlaybackEvent(sourceSessionId, {
        event: "stop",
        position_ms: sourceStopMs,
        client_state: buildClientPlaybackState("seek_session_replace"),
      }).catch(() => {});

      if (requestId === seekRequestRef.current) {
        queuePlaybackCommandLog(command, "result", {
          targetPositionSeconds: boundedSeconds,
          sourcePositionSeconds,
          sourceSessionId,
          targetSessionId: nextSession.data.id,
          shouldResume,
        });
        navigate(`/play/${nextSession.data.id}`, { replace: true });
        navigated = true;
      }
    } catch {
      setBuffering(false);
      setPlayerError("Could not seek to that point.");
      failedSeekTargetRef.current = boundedSeconds;
      setPendingSeek(null);
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "seek_failed",
        targetPositionSeconds: boundedSeconds,
        sourcePositionSeconds,
        sourceSessionId,
        shouldResume,
      });
    } finally {
      if (requestId === seekRequestRef.current && !navigated) {
        releasePlaybackSessionSwitch(switchToken);
        seekInFlightRef.current = false;
        setSeekSwitching(false);
      }
    }
  }
  seekToPositionRef.current = seekToPosition;

  function togglePlay(source: PlaybackCommandSource = "programmatic", input: string | null = null) {
    const video = videoRef.current;
    const command = beginPlaybackCommand(video?.paused ? "play" : "pause", source, input);
    const sourcePositionSeconds = video ? getAbsoluteVideoTime(video) : currentTimeRef.current;
    queuePlaybackCommandLog(command, "request", {
      sourcePositionSeconds,
      sourceSessionId: sessionId ?? null,
    });
    if (!video) {
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "missing_video_element",
        sourcePositionSeconds,
        sourceSessionId: sessionId ?? null,
      });
      return;
    }
    const blockedReason = playbackCommandBlockReason();
    if (blockedReason) {
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason,
        sourcePositionSeconds,
        sourceSessionId: sessionId ?? null,
      });
      return;
    }

    if (video.paused) {
      if (playbackEnded) {
        const absoluteTime = getAbsoluteVideoTime(video);
        const replayPosition = Number.isFinite(absoluteTime) ? absoluteTime : currentTimeRef.current;
        if (effectiveDuration > 0 && replayPosition >= effectiveDuration - 0.25) {
          queuePlaybackCommandLog(command, "execute", {
            targetPositionSeconds: 0,
            sourcePositionSeconds,
            sourceSessionId: sessionId ?? null,
            shouldResume: true,
          });
          void seekToPosition(0, true, source, input ?? "replay");
          queuePlaybackCommandLog(command, "result", {
            targetPositionSeconds: 0,
            sourcePositionSeconds,
            sourceSessionId: sessionId ?? null,
            shouldResume: true,
          });
          return;
        }
        playbackCompletedRef.current = false;
        setPlaybackEnded(false);
      }
      userPausedRef.current = false;
      setStartupAutoplayBlocked(false);
      queuePlaybackCommandLog(command, "execute", {
        sourcePositionSeconds,
        sourceSessionId: sessionId ?? null,
        shouldResume: true,
      });
      if (!playbackStartedRef.current) {
        attemptStartupPlayback();
        queuePlaybackCommandLog(command, "result", {
          sourcePositionSeconds,
          sourceSessionId: sessionId ?? null,
          shouldResume: true,
        });
        return;
      }
      setAwaitingPlaybackStart(true);
      setReadyToStart(false);
      const sourceSessionId = sessionId;
      const resumePositionMs = stableSessionPositionMS(Math.floor(getAbsoluteVideoTime(video) * 1000));
      const resumeAccepted = sourceSessionId
        ? reportPlaybackEvent(sourceSessionId, {
          event: "resume",
          position_ms: resumePositionMs,
        })
        : Promise.resolve(null);
      void resumeAccepted
        .then((response) => {
          if (sourceSessionId) {
            assertActivePlaybackSession(sourceSessionId);
          }
          const resumedGenerationID = response?.data.attributes.hls_generation_id?.trim() ?? "";
          const responseOriginMS = response?.data.attributes.media_timeline_origin_ms;
          const resumeAuthority = authoritativeResumeTuple({
            previousGenerationId: hlsGenerationIdRef.current,
            nextGenerationId: resumedGenerationID,
            responseOriginSeconds: responseOriginMS == null ? Number.NaN : responseOriginMS / 1000,
            targetAbsoluteSeconds: resumePositionMs / 1000,
          });
          if (
            sourceSessionId
            && nativeHlsPlaybackRef.current
            && resumedGenerationID
            && resumedGenerationID !== hlsGenerationIdRef.current
          ) {
            if (!resumeAuthority || response?.data.id !== sourceSessionId) {
              throw new InvalidResumeAuthorityError();
            }
            const verifiedReload = verifiedNativeHlsDeliveryReload({
              previousGenerationId: hlsGenerationIdRef.current,
              nextGenerationId: resumeAuthority.nextGenerationId,
              targetAbsoluteSeconds: resumeAuthority.targetAbsoluteSeconds,
            });
            if (!verifiedReload) {
              throw new InvalidResumeAuthorityError();
            }
            pendingNativeHlsPositionRestoreRef.current = verifiedReload;
            adoptVerifiedNativeHlsTimeline(
              sourceSessionId,
              verifiedReload.targetAbsoluteSeconds,
              resumeAuthority.originSeconds,
            );
            hlsGenerationIdRef.current = resumedGenerationID;
            resumeAfterSessionSwitchRef.current = true;
            queueClientPlaybackLogRef.current("native_hls_resume_delivery_reload", "session", {
              flushImmediately: true,
              flushReason: "native_hls_resume_delivery_reload",
            });
            setMediaElementReloadNonce((value) => value + 1);
            return;
          }
          if (
            sourceSessionId
            && !nativeHlsPlaybackRef.current
            && hlsRef.current
            && resumedGenerationID
            && hlsGenerationIdRef.current
            && resumedGenerationID !== hlsGenerationIdRef.current
          ) {
            // Resuming after the pause grace expires stops the worker with
            // cleanup, so the resume is served by a new generation at a new
            // anchor. Loading it into the live element would adopt that anchor
            // onto a clock still on the previous one and leave the deleted
            // generation's segments buffered where the element will later seek
            // over the hole between them (#846).
            if (!resumeAuthority || response?.data.id !== sourceSessionId) {
              throw new InvalidResumeAuthorityError();
            }
            rebuildMediaElementForGenerationSwap(
              "resume_delivery",
              resumeAuthority,
              response?.data.attributes.hls_generation_sequence ?? null,
            );
            return;
          }
          hlsRef.current?.startLoad();
          return video.play();
        })
        .then(() => {
          queuePlaybackCommandLog(command, "result", {
            sourcePositionSeconds: getAbsoluteVideoTime(video),
            sourceSessionId: sourceSessionId ?? null,
            shouldResume: true,
          });
        })
        .catch((error) => {
          if (error instanceof StalePlaybackHandoffError) {
            return;
          }
          if (error instanceof InvalidResumeAuthorityError) {
            setPlayerError("Could not verify the resumed stream position. Try again or reload playback.");
          }
          setPlaying(false);
          setBuffering(false);
          setAwaitingPlaybackStart(false);
          syncPausedReadyState(video);
          queuePlaybackCommandLog(command, "blocked", {
            blockedReason: "play_rejected",
            sourcePositionSeconds,
            sourceSessionId: sourceSessionId ?? null,
            shouldResume: true,
          });
        });
    } else {
      userPausedRef.current = true;
      hlsRef.current?.stopLoad?.();
      clearStartupPlayTimer();
      startupPlayRequestRef.current = false;
      queuePlaybackCommandLog(command, "execute", {
        sourcePositionSeconds,
        sourceSessionId: sessionId ?? null,
        shouldResume: false,
      });
      video.pause();
      setBuffering(false);
      setAwaitingPlaybackStart(false);
      setReadyToStart(true);
      if (sessionId) {
        const positionMs = stableSessionPositionMS(Math.floor(getAbsoluteVideoTime(video) * 1000));
        reportPlaybackEvent(sessionId, {
          event: "pause",
          position_ms: positionMs,
        }).catch(() => {});
      }
      queuePlaybackCommandLog(command, "result", {
        sourcePositionSeconds: getAbsoluteVideoTime(video),
        sourceSessionId: sessionId ?? null,
        shouldResume: false,
      });
    }
  }

  function toggleMute() {
    if (muted || volumeLevel <= 0) {
      const restoredVolume = lastNonZeroVolumeRef.current > 0 ? lastNonZeroVolumeRef.current : 1;
      applyAudioState(restoredVolume, false);
      return;
    }
    applyAudioState(volumeLevel, true);
  }

  function handleVolumeChange(event: ChangeEvent<HTMLInputElement>) {
    const nextVolume = Math.max(0, Math.min(1, Number(event.target.value)));
    const nextMuted = nextVolume <= 0 ? true : false;
    applyAudioState(nextVolume, nextMuted);
  }

  function handleVideoSurfaceClick(source: PlaybackCommandSource = "video_surface_click", input: string | null = "click") {
    showControls();
    togglePlay(source, input);
  }

  function handlePreviewSeekButtonPointerDown(deltaSeconds: number, event: PointerEvent<HTMLButtonElement>) {
    if (effectiveDuration <= 0 || seekSwitching) {
      return;
    }
    event.preventDefault();
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    showControls();
    clearPreviewSeekRepeat();
    const input = deltaSeconds < 0 ? "skip_back_10" : "skip_forward_10";
    startPreviewSeek(deltaSeconds, "seek_button", input);
    previewSeekRepeatTimerRef.current = window.setInterval(() => {
      startPreviewSeek(deltaSeconds, "seek_button", input);
    }, 350);
  }

  function handlePreviewSeekButtonPointerUp(event: PointerEvent<HTMLButtonElement>) {
    event.preventDefault();
    void commitPreviewSeek();
  }

  function handlePreviewSeekButtonPointerCancel(event: PointerEvent<HTMLButtonElement>) {
    event.preventDefault();
    cancelPreviewSeek();
  }

  function handlePreviewSeekButtonClick(deltaSeconds: number, event: MouseEvent<HTMLButtonElement>) {
    if (event.detail !== 0 || effectiveDuration <= 0 || seekSwitching) {
      return;
    }
    startPreviewSeek(deltaSeconds, "seek_button", deltaSeconds < 0 ? "skip_back_10" : "skip_forward_10");
    void commitPreviewSeek();
  }

  function handleRestartClick() {
    if (effectiveDuration <= 0 || seekSwitching) {
      return;
    }
    showControls();
    const shouldResume = !(videoRef.current?.paused ?? !playing);
    void seekToPosition(0, shouldResume, "seek_button", "restart");
  }

  // ⏭ plays the next episode when there is one. Otherwise the title is
  // finished: stop the session at its full duration, which is the same
  // completed marker the next-episode handoff leaves on the source session,
  // so the server treats it as watched and drops it from Continue Watching.
  async function handleNextClick() {
    if (nextEpisode) {
      await handlePlayNextEpisode();
      return;
    }
    const completedPositionMs = effectiveDurationRef.current > 0
      ? Math.floor(effectiveDurationRef.current * 1000)
      : undefined;
    await handleStop("end_button", "click", completedPositionMs);
  }

  function commitScrub(target: number) {
    clearKeyboardSeekCommit();
    seekPointerActiveRef.current = false;
    seekRailKeyRef.current = null;
    setSeekInteractionValue(target);
    setScrubbing(false);
    setScrubTime(null);
    setSeekHoverTime(null);
    const intent = commitSeekIntent(target, true);
    void seekToPosition(target, true, intent ? playbackCommandSourceForSeekIntent(intent.source) : "progress_drag", intent?.input ?? null)
      .finally(() => {
      scheduleSeekIntentResult(intent, target);
    });
  }

  function cancelScrub() {
    clearKeyboardSeekCommit();
    seekPointerActiveRef.current = false;
    seekRailKeyRef.current = null;
    cancelSeekIntent(seekInteractionValueRef.current);
    setSeekInteractionValue(null);
    setScrubbing(false);
    setScrubTime(null);
    setSeekHoverTime(null);
    setSeekHoverOffsetPx(null);
  }

  function seekGeometryRects(target: HTMLElement): { wrapperRect: DOMRect; inputRect: DOMRect } {
    return {
      wrapperRect: seekWrapRef.current?.getBoundingClientRect() ?? target.getBoundingClientRect(),
      inputRect: target.getBoundingClientRect(),
    };
  }

  function publishSeekDebugSnapshot(
    event: string,
    target: HTMLElement,
    options?: {
      pointerClientX?: number | null;
      hoverTime?: number | null;
      sliderValue?: number;
    },
  ) {
    if (!seekDebugEnabled) {
      return;
    }
    const { wrapperRect, inputRect } = seekGeometryRects(target);
    const pointerClientX = options?.pointerClientX ?? null;
    const clampRatio = (value: number) => Math.max(0, Math.min(1, value));
    const wrapperRatio = pointerClientX !== null && wrapperRect.width > 0
      ? clampRatio((pointerClientX - wrapperRect.left) / wrapperRect.width)
      : null;
    const inputRatio = pointerClientX !== null && inputRect.width > 0
      ? clampRatio((pointerClientX - inputRect.left) / inputRect.width)
      : null;
    const snapshot: SeekDebugSnapshot = {
      event,
      pointerClientX,
      wrapperLeft: wrapperRect.left,
      wrapperWidth: wrapperRect.width,
      inputLeft: inputRect.left,
      inputWidth: inputRect.width,
      wrapperRatio,
      inputRatio,
      hoverTime: options?.hoverTime ?? null,
      sliderValue: options?.sliderValue ?? seekInteractionValueRef.current ?? seekValue,
    };
    setSeekDebugSnapshot(snapshot);
    (window as Window & { __knotfinSeekDebug?: SeekDebugSnapshot }).__knotfinSeekDebug = snapshot;
    const clientState = buildClientPlaybackState(`seek_debug:${event}`, {
      seekDebugSnapshot: snapshot,
    });
    const signature = JSON.stringify(clientState.seek_debug ?? null);
    const previous = lastSeekDebugReportRef.current;
    const now = Date.now();
    if (previous && previous.signature === signature && now - previous.at < 250) {
      return;
    }
    lastSeekDebugReportRef.current = { signature, at: now };
    void reportPlaybackEventWithAdaptation({
      event: "client_state",
      position_ms: clientState.current_time_ms,
      client_state: clientState,
    });
  }

  function seekGeometryRect(target: HTMLElement): DOMRect {
    return seekGeometryRects(target).wrapperRect;
  }

  function sliderTimeForPointer(clientX: number, target: HTMLElement): number {
    return sliderPreviewTimeFromPointer(clientX, seekGeometryRect(target), effectiveDuration);
  }

  function updateSeekPointerGeometry(clientX: number, target: HTMLElement) {
    const rect = seekGeometryRect(target);
    setSeekRailWidthPx(rect.width);
    setSeekHoverOffsetPx(sliderPreviewOffsetFromPointer(clientX, rect));
  }

  function updateSeekHover(clientX: number, target: HTMLElement) {
    if (effectiveDuration <= 0 || scrubbing) {
      return;
    }
    updateSeekPointerGeometry(clientX, target);
    const pointerTime = sliderTimeForPointer(clientX, target);
    setSeekHoverTime(pointerTime);
    publishSeekDebugSnapshot("hover", target, {
      pointerClientX: clientX,
      hoverTime: pointerTime,
    });
  }

  function pointerEventClientX(event: PointerEvent<HTMLElement>): number {
    const nativeEvent = event.nativeEvent as { clientX?: number; pageX?: number; screenX?: number };
    for (const candidate of [nativeEvent.clientX, nativeEvent.pageX, event.clientX, nativeEvent.screenX]) {
      if (Number.isFinite(candidate)) {
        return candidate as number;
      }
    }
    return 0;
  }

  function beginPointerScrub(clientX: number, target: HTMLElement, pointerType: string) {
    if (effectiveDuration <= 0 || seekSwitching) {
      return;
    }
    clearKeyboardSeekCommit();
    seekPointerActiveRef.current = true;
    const pointerTime = sliderTimeForPointer(clientX, target);
    updateSeekPointerGeometry(clientX, target);
    setScrubbing(true);
    setScrubTime(pointerTime);
    setSeekHoverTime(pointerTime);
    setSeekInteractionValue(pointerTime);
    beginSeekIntent(pointerType === "touch" ? "touch_scrub" : "progress_click", pointerType || null, pointerTime, {
      previewSeconds: pointerTime,
      resumeExpected: !(videoRef.current?.paused ?? !playing),
    });
    publishSeekDebugSnapshot("pointer_down", target, {
      pointerClientX: clientX,
      hoverTime: pointerTime,
      sliderValue: pointerTime,
    });
  }

  function updatePointerScrub(clientX: number, target: HTMLElement, pointerType: string) {
    if (!seekPointerActiveRef.current || effectiveDuration <= 0) {
      return;
    }
    const pointerTime = sliderTimeForPointer(clientX, target);
    updateSeekPointerGeometry(clientX, target);
    setScrubbing(true);
    setScrubTime(pointerTime);
    setSeekHoverTime(pointerTime);
    setSeekInteractionValue(pointerTime);
    updateSeekIntent(pointerTime, {
      source: pointerType === "touch" ? "touch_scrub" : "progress_drag",
      input: pointerType || null,
      previewSeconds: pointerTime,
    });
    publishSeekDebugSnapshot("pointer_move", target, {
      pointerClientX: clientX,
      hoverTime: pointerTime,
      sliderValue: pointerTime,
    });
  }

  function clearSeekHover() {
    if (!scrubbing) {
      setSeekHoverTime(null);
      setSeekHoverOffsetPx(null);
    }
  }

  function updateSeekPointerGeometryForValue(value: number, target: HTMLElement) {
    const rect = seekGeometryRect(target);
    const ratio = Math.max(0, Math.min(1, value / Math.max(1, seekMax)));
    setSeekRailWidthPx(rect.width);
    setSeekHoverOffsetPx(ratio * rect.width);
    setSeekInteractionValue(value);
    publishSeekDebugSnapshot("change", target, {
      hoverTime: value,
      sliderValue: value,
    });
  }

  function nudgeSeekValue(
    direction: "backward" | "forward" | "start" | "end" | "page_backward" | "page_forward",
    target: HTMLElement,
  ) {
    if (effectiveDuration <= 0 || seekSwitching) {
      return;
    }
    clearKeyboardSeekCommit();
    const baseValue = Math.max(0, Math.min(seekInteractionValueRef.current ?? seekValue, seekMax));
    let nextValue = baseValue;
    switch (direction) {
      case "backward":
        nextValue = Math.max(0, baseValue - 1);
        break;
      case "forward":
        nextValue = Math.min(seekMax, baseValue + 1);
        break;
      case "page_backward":
        nextValue = Math.max(0, baseValue - 10);
        break;
      case "page_forward":
        nextValue = Math.min(seekMax, baseValue + 10);
        break;
      case "start":
        nextValue = 0;
        break;
      case "end":
        nextValue = seekMax;
        break;
    }
    setScrubbing(true);
    setScrubTime(nextValue);
    setSeekHoverTime(nextValue);
    updateSeekPointerGeometryForValue(nextValue, target);
    activeOrBeginSeekIntent("seek_rail_keyboard", direction, nextValue, {
      previewSeconds: nextValue,
      resumeExpected: !(videoRef.current?.paused ?? !playing),
    });
  }

  function scheduleSeekRailKeyboardCommit(target: number) {
    clearKeyboardSeekCommit();
    keyboardSeekCommitTimerRef.current = window.setTimeout(() => {
      keyboardSeekCommitTimerRef.current = null;
      setScrubbing(false);
      setScrubTime(null);
      setSeekHoverTime(null);
      setSeekInteractionValue(target);
      const intent = commitSeekIntent(target, true);
      void seekToPositionRef.current(target, true, "seek_commit", intent?.input ?? "seek_rail_keyboard").finally(() => {
        scheduleSeekIntentResult(intent, target);
      });
    }, KEYBOARD_SEEK_COMMIT_DEBOUNCE_MS);
  }

  function formatTime(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  function formatBitrate(bps: number): string {
    if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
    if (bps >= 1_000) return `${(bps / 1_000).toFixed(0)} kbps`;
    return `${bps} bps`;
  }

  function variantForLevel(level: number | null): PlaybackVariant | null {
    if (!delivery || level === null || level < 0 || level >= delivery.variants.length) {
      return null;
    }
    return delivery.variants[level] ?? null;
  }

  function variantByName(name: string | undefined): PlaybackVariant | null {
    if (!delivery || !name) {
      return null;
    }
    return delivery.variants.find((variant) => variant.name === name) ?? null;
  }

  function formatVariantSummary(variant: PlaybackVariant | null | undefined): string | null {
    if (!variant) {
      return null;
    }
    const parts = [formatVariantResolution(variant), formatBitrate(variant.bandwidth)].filter(Boolean);
    return parts.length > 0 ? parts.join(" • ") : null;
  }

  function formatVariantResolution(variant: PlaybackVariant): string {
    if (variant.height > 0) {
      return `${variant.height}p`;
    }
    if (variant.width > 0 && variant.height > 0) {
      return `${variant.width}x${variant.height}`;
    }
    return "";
  }

  function formatDecodedResolution(size: { width: number; height: number } | null): string | null {
    if (!size || size.width <= 0 || size.height <= 0) {
      return null;
    }
    if (size.height > 0) {
      return `${size.height}p`;
    }
    return `${size.width}x${size.height}`;
  }

  function formatPlaybackHealthLabel(health: "stable" | "degraded" | "critical" | undefined): string {
    switch (health) {
      case "critical":
        return "Buffering now";
      case "degraded":
        return "Connection unstable";
      case "stable":
        return "Playback steady";
      default:
        return "Measuring playback";
    }
  }

  const showBufferingIndicator =
    !playerError
    && !readyToStart
    && !startupAutoplayBlocked
    && !bufferedSeekGrace
    && (buffering || seekSwitching || awaitingPlaybackStart || pendingSeekTime !== null);
  const hideVideoDuringSeekTransition = seekSwitching || (pendingSeekTime !== null && !bufferedSeekGrace);
  const nextEpisodePromptVisible = shouldShowNextEpisodePrompt({
    currentTime: displayedCurrentTime,
    duration: effectiveDuration,
    playbackEnded,
    hasNextEpisode: nextEpisode !== null,
    playerError,
    seekSwitching,
    previewSeeking,
    qualitySwitching,
    pendingSeekTime,
    recoveringSession,
  });
  const nextEpisodeLabel = nextEpisode
    ? [
        nextEpisode.attributes.season_number !== null && nextEpisode.attributes.episode_number !== null
          ? `S${String(nextEpisode.attributes.season_number).padStart(2, "0")} E${String(nextEpisode.attributes.episode_number).padStart(2, "0")}`
          : null,
        nextEpisode.attributes.episode_title ?? nextEpisode.attributes.title,
      ].filter(Boolean).join("  ")
    : "";

  const handlePlayNextEpisode = useCallback(async () => {
    const video = videoRef.current;
    const sourcePositionSeconds = video ? getAbsoluteVideoTime(video) : currentTimeRef.current;
    const command = beginPlaybackCommand("session_handoff", "next_episode", nextEpisode?.id ?? null);
    queuePlaybackCommandLog(command, "request", {
      targetPositionSeconds: 0,
      sourcePositionSeconds,
      sourceSessionId: sessionId ?? null,
      shouldResume: true,
    });
    if (!nextEpisode) {
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "missing_next_episode",
        sourcePositionSeconds,
        sourceSessionId: sessionId ?? null,
        shouldResume: false,
      });
      return;
    }
    if (nextEpisodeStartingRef.current) {
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "next_episode_in_flight",
        targetPositionSeconds: 0,
        sourcePositionSeconds,
        sourceSessionId: sessionId ?? null,
        shouldResume: true,
      });
      return;
    }
    const switchToken = beginPlaybackSessionSwitch("next_episode");
    if (switchToken === null) {
      queueClientPlaybackLogRef.current("next_episode_suppressed_session_switch", "session");
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "session_switch",
        targetPositionSeconds: 0,
        sourcePositionSeconds,
        sourceSessionId: sessionId ?? null,
        shouldResume: true,
      });
      return;
    }
    nextEpisodeStartingRef.current = true;
    resumeAfterSessionSwitchRef.current = true;
    clearNextEpisodeAutoplayTimer();
    const currentPositionMs = video
      ? Math.floor(getAbsoluteVideoTime(video) * 1000)
      : Math.floor(currentTimeRef.current * 1000);
    const completedPositionMs = effectiveDurationRef.current > 0
      ? Math.floor(effectiveDurationRef.current * 1000)
      : currentPositionMs;

    setPlayerError(null);
    setBuffering(true);
    setAwaitingPlaybackStart(true);
    setReadyToStart(false);
    let navigated = false;

    try {
      video?.pause();
      queuePlaybackCommandLog(command, "execute", {
        targetPositionSeconds: 0,
        sourcePositionSeconds,
        sourceSessionId: sessionId ?? null,
        shouldResume: true,
      });
      await handoffPlaybackSession(
        nextEpisode.id,
        0,
        normalizeRequestedBitrateCap(requestedMaxVideoBitrateRef.current),
        completedPositionMs,
      );
      queuePlaybackCommandLog(command, "result", {
        targetPositionSeconds: 0,
        sourcePositionSeconds,
        sourceSessionId: sessionId ?? null,
        shouldResume: true,
      });
      navigated = true;
    } catch (error) {
      if (isStalePlaybackHandoffError(error)) {
        return;
      }
      nextEpisodeStartingRef.current = false;
      resumeAfterSessionSwitchRef.current = false;
      setBuffering(false);
      setAwaitingPlaybackStart(false);
      setReadyToStart(true);
      setPlayerError("Could not start the next episode.");
      queuePlaybackCommandLog(command, "blocked", {
        blockedReason: "next_episode_failed",
        targetPositionSeconds: 0,
        sourcePositionSeconds,
        sourceSessionId: sessionId ?? null,
        shouldResume: true,
      });
    } finally {
      if (!navigated) {
        releasePlaybackSessionSwitch(switchToken);
        nextEpisodeStartingRef.current = false;
        resumeAfterSessionSwitchRef.current = false;
      }
    }
  }, [
    beginPlaybackCommand,
    beginPlaybackSessionSwitch,
    clearNextEpisodeAutoplayTimer,
    getAbsoluteVideoTime,
    handoffPlaybackSession,
    nextEpisode,
    queuePlaybackCommandLog,
    releasePlaybackSessionSwitch,
    sessionId,
  ]);

  useEffect(() => {
    if (!playbackEnded || !nextEpisodePromptVisible || !nextEpisode || recoveringSession || playerError) {
      clearNextEpisodeAutoplayTimer();
      nextEpisodeStartingRef.current = false;
      setNextEpisodeAutoplayRemainingSeconds(NEXT_EPISODE_AUTOPLAY_DELAY_SECONDS);
      return;
    }

    setNextEpisodeAutoplayRemainingSeconds(NEXT_EPISODE_AUTOPLAY_DELAY_SECONDS);
    const startedAt = Date.now();
    nextEpisodeAutoplayCountdownRef.current = window.setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
      setNextEpisodeAutoplayRemainingSeconds(Math.max(0, NEXT_EPISODE_AUTOPLAY_DELAY_SECONDS - elapsedSeconds));
    }, 250);
    nextEpisodeAutoplayTimerRef.current = window.setTimeout(() => {
      void handlePlayNextEpisode();
    }, NEXT_EPISODE_AUTOPLAY_DELAY_SECONDS * 1000);

    return clearNextEpisodeAutoplayTimer;
  }, [
    clearNextEpisodeAutoplayTimer,
    handlePlayNextEpisode,
    nextEpisode,
    nextEpisodePromptVisible,
    playbackEnded,
    playerError,
    recoveringSession,
  ]);

  useEffect(() => {
    if (!showBufferingIndicator || playerError) {
      return;
    }
    const timer = window.setTimeout(() => {
      reportClientPlaybackState(playbackStartedRef.current ? "buffering_persisted" : "startup_buffering_persisted");
    }, 4_000);
    return () => window.clearTimeout(timer);
  }, [playerError, reportClientPlaybackState, showBufferingIndicator]);

  useEffect(() => {
    if (!playerError) {
      return;
    }
    reportClientPlaybackState("player_error");
  }, [playerError, reportClientPlaybackState]);

  useEffect(() => {
    if (copyDebugStatus === "idle") {
      return;
    }
    const timer = window.setTimeout(() => {
      setCopyDebugStatus("idle");
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [copyDebugStatus]);

  useEffect(() => {
    if (!selectedSubtitleId || !currentSessionResourceId || !delivery) {
      return;
    }
    const sessionSubtitleTracks = delivery.available_subtitles ?? [];
    if (sessionSubtitleTracks.length === 0) {
      setSelectedSubtitleId(null);
      return;
    }
    if (!sessionSubtitleTracks.some((track) => track.id === selectedSubtitleId)) {
      setSelectedSubtitleId(null);
    }
  }, [currentSessionResourceId, delivery, selectedSubtitleId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const detachCuePositioning = attachPlayerTextTrackCuePositioning(video);
    return detachCuePositioning;
  }, [currentSessionResourceId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const selectedIndex = selectedSubtitleId
      ? availableSubtitleTracks.findIndex((track) => track.id === selectedSubtitleId)
      : -1;
    if (hlsRef.current) {
      // In HLS master-playlist mode hls.js owns the subtitle track index. The
      // index aligns with the SUBTITLES renditions in the master manifest,
      // which are emitted in the same order as availableSubtitleTracks
      // (server-side `readySubtitleTracks` filters/sorts identically to the
      // client filter above).
      hlsRef.current.subtitleTrack = masterPlaylistSubtitles ? selectedIndex : -1;
    }
    const applyTextTrackModes = () => {
      const selectedTrack = selectedSubtitleId
        ? availableSubtitleTracks.find((track) => track.id === selectedSubtitleId) ?? null
        : null;
      for (let index = 0; index < video.textTracks.length; index += 1) {
        const textTrack = video.textTracks[index];
        if (!textTrack) {
          continue;
        }
        if (masterPlaylistSubtitles) {
          if (hlsRef.current) {
            // hls.js manages mode for its master-playlist tracks; don't fight it.
            continue;
          }
          const isSelected = !!selectedTrack && textTrack === matchingSubtitleTextTrack(video, selectedTrack);
          textTrack.mode = isSelected ? "showing" : "disabled";
          continue;
        }
        const isSelected = !!selectedSubtitleId
          && !!textTrack.id
          && textTrack.id === selectedSubtitleId;
        textTrack.mode = isSelected ? "showing" : "disabled";
      }
      positionPlayerTextTrackCues(video);
    };
    applyTextTrackModes();
    if (!masterPlaylistSubtitles || hlsRef.current) {
      return;
    }
    const textTracks = video.textTracks;
    textTracks.addEventListener?.("addtrack", applyTextTrackModes);
    textTracks.addEventListener?.("change", applyTextTrackModes);
    return () => {
      textTracks.removeEventListener?.("addtrack", applyTextTrackModes);
      textTracks.removeEventListener?.("change", applyTextTrackModes);
    };
  }, [availableSubtitleTracks, masterPlaylistSubtitles, selectedSubtitleId]);

  useEffect(() => {
    if (masterPlaylistSubtitles || !selectedSubtitleTrack) {
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        const video = videoRef.current;
        if (!video || cancelled) {
          return;
        }
        let textTrack = matchingSubtitleTextTrack(video, selectedSubtitleTrack);
        if (!textTrack) {
          textTrack = video.addTextTrack(
            "subtitles",
            selectedSubtitleTrack.attributes.label,
            selectedSubtitleTrack.attributes.language,
          );
        }

        textTrack.mode = "showing";
        if (textTrack.cues && textTrack.cues.length > 0) {
          return;
        }

        const response = await fetch(selectedSubtitleTrack.attributes.url, {
          headers: { accept: "text/vtt,*/*" },
        });
        if (!response.ok || cancelled) {
          return;
        }
        const cues = parseWebVttCues(await response.text());
        if (cancelled || cues.length === 0) {
          return;
        }
        for (const cue of cues) {
          addParsedCueToTextTrack(textTrack, cue);
        }
        positionPlayerTextTrackCues(video);
      })().catch(() => {});
    }, 1000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    currentSessionResourceId,
    masterPlaylistSubtitles,
    selectedSubtitleTrack,
    selectedSubtitleTrack?.attributes.label,
    selectedSubtitleTrack?.attributes.language,
    selectedSubtitleTrack?.attributes.url,
    selectedSubtitleTrack?.id,
  ]);

  const waitingForInitialSession = session.loading && !currentSessionResource;
  if (waitingForInitialSession || (!!session.data && !currentSessionResource && !session.error)) {
    return <div className="player-page player-loading">Loading player...</div>;
  }
  if (recoveringSession && !currentSessionResource) {
    return <div className="player-page player-loading">Recovering playback session...</div>;
  }
  if (session.error && !currentSessionResource) {
    return <div className="player-page player-loading">{playerError ?? "Failed to load playback session"}</div>;
  }

  const seekMax = Math.max(1, effectiveDuration);
  const seekValue = Math.max(0, Math.min(displayedCurrentTime, seekMax));
  const activeSeekPreviewTime = seekPreviewTime ?? (scrubbing && scrubTime !== null ? scrubTime : seekHoverTime);
  const showSeekPreviewTarget =
    activeSeekPreviewTime !== null && (previewSeeking || scrubbing || seekHoverTime !== null);
  const activeTrickplayFrame = locateTrickplayFrame(trickplayTrack, activeSeekPreviewTime ?? -1);
  const seekProgressPercent = seekMax > 0
    ? Math.max(0, Math.min(100, (seekValue / seekMax) * 100))
    : 0;
  const trickplayPreviewStyle = activeTrickplayFrame
    ? {
        backgroundImage: `url("${activeTrickplayFrame.tileUrl}")`,
        backgroundSize: `${activeTrickplayFrame.cols * 100}% ${activeTrickplayFrame.rows * 100}%`,
        backgroundPosition: `${activeTrickplayFrame.cols > 1 ? (activeTrickplayFrame.col / (activeTrickplayFrame.cols - 1)) * 100 : 0}% ${activeTrickplayFrame.rows > 1 ? (activeTrickplayFrame.row / (activeTrickplayFrame.rows - 1)) * 100 : 0}%`,
    }
    : undefined;
  const trickplayPreviewLayout = showSeekPreviewTarget && seekHoverOffsetPx !== null
    ? computePreviewPanelPosition(
        seekHoverOffsetPx,
        seekRailWidthPx,
        TRICKPLAY_PREVIEW_PANEL_WIDTH_PX,
      )
    : null;
  const effectiveMuted = muted || volumeLevel <= 0;
  const measuredBandwidthBps = getMeasuredBandwidth();
  const currentVariant = nativeHlsDelivery
    ? null
    : variantForLevel(currentLevel) ?? variantByName(delivery?.active_variant_name);
  const currentVariantSummary = formatVariantSummary(currentVariant);
  const playerBuildTitle = [
    clientBuildTitle(),
    buildIdentityTitle(buildFreshness.expectedBuild),
    `Freshness: ${buildFreshness.stale ? "new web client available" : "current"}`,
    `Last checked: ${buildFreshness.lastCheckedAt ?? "not checked"}`,
    buildFreshness.error ? `Check error: ${buildFreshness.error.message}` : null,
  ].filter(Boolean).join("\n");
  const playerBuildLabel = buildFreshness.stale
    ? `New ${buildIdentityDateStamp(buildFreshness.expectedBuild) ?? "web"}`
    : clientBuildCompactLabel();
  const effectivePlaybackHealth: "stable" | "degraded" | "critical" | undefined = playerError
    ? "critical"
    : showBufferingIndicator
      ? "critical"
      : (playing && !buffering && !awaitingPlaybackStart && pendingSeekTime === null && !seekSwitching)
        ? "stable"
        : delivery?.playback_health;
  const playbackHealthLabel = formatPlaybackHealthLabel(effectivePlaybackHealth);
  const deliveryReasonCodes = new Set(delivery?.delivery_reason_codes ?? []);
  const sourceLimitedSession = !!delivery
    && delivery.playlist_mode === "single"
    && delivery.variants.length === 1
    && !deliveryReasonCodes.has("source_bitrate_exceeds_budget");
  const sessionSkipMarkers = delivery?.skip_markers ?? [];
  const itemSkipMarkers = currentItem?.attributes.skip_markers ?? [];
  const skipMarkers = [
    ...sessionSkipMarkers,
    ...itemSkipMarkers.filter((itemMarker) => !sessionSkipMarkers.some((sessionMarker) =>
      sessionMarker.type === itemMarker.type
      && sessionMarker.start_ms === itemMarker.start_ms
      && sessionMarker.end_ms === itemMarker.end_ms,
    )),
  ];
  const activeIntroMarker = skipMarkers.find((marker) => {
    if (marker.type !== "intro" || marker.end_ms <= marker.start_ms) {
      return false;
    }
    const startSeconds = marker.start_ms / 1000;
    const endSeconds = marker.end_ms / 1000;
    return currentTime >= startSeconds && currentTime < Math.max(startSeconds, endSeconds - 1);
  }) ?? null;

  return (
    <div
      className="player-page"
      onMouseMove={showControls}
      onClick={showControls}
    >
      <video
        ref={videoRef}
        className={`video-player ${hideVideoDuringSeekTransition ? "video-player--transitioning" : ""}`}
        playsInline
        preload="auto"
        onPlay={() => {
          queueClientPlaybackLogRef.current("media_play", "media_event");
          hlsRef.current?.startLoad();
          userPausedRef.current = false;
          clearStartupPlayTimer();
          startupPlayRequestRef.current = false;
          // playbackStartedRef intentionally NOT set here. The `play` media
          // event fires synchronously when video.play() is called, *before*
          // actual playback begins. iPad Safari can reject autoplay and fire
          // `pause` between `play` and `playing`; if we set playbackStartedRef
          // on `play`, the autoplay-block recovery path bails out
          // (attemptStartupPlayback at the top of this module returns early)
          // and the player wedges thinking it's playing. onPlaying and
          // onTimeUpdate (gated by readyState >= HAVE_CURRENT_DATA) are the
          // truthful sources — they fire only when playback really starts.
          autoplayBlockedRef.current = false;
          setStartupAutoplayBlocked(false);
          clearPlayerErrorIfPlaybackHealthy(videoRef.current);
          setPlaying(true);
          setAwaitingPlaybackStart(false);
          setReadyToStart(false);
        }}
        onPause={() => {
          queueClientPlaybackLogRef.current("media_pause", "media_event");
          const resumeStartupAfterSessionSwitch =
            resumeAfterSessionSwitchRef.current
            && !playbackStartedRef.current
            && !userPausedRef.current;
          if (userPausedRef.current) {
            hlsRef.current?.stopLoad?.();
            resumeAfterSessionSwitchRef.current = false;
          }
          if (!playbackStartedRef.current) {
            if (resumeStartupAfterSessionSwitch) {
              setBuffering(true);
              setAwaitingPlaybackStart(true);
              setReadyToStart(false);
              attemptStartupPlaybackRef.current?.();
            } else {
              releaseStartupWaitState(videoRef.current);
            }
          }
          if (userPausedRef.current) {
            setBuffering(false);
            setAwaitingPlaybackStart(false);
            setReadyToStart(true);
          }
          setPlaying(false);
          if (!resumeStartupAfterSessionSwitch) {
            setAwaitingPlaybackStart(false);
            syncPausedReadyState(videoRef.current);
          }
        }}
        onSeeking={() => {
          queueClientPlaybackLogRef.current("media_seeking", "media_event");
          const settledNativeTarget = nativeHlsInitialPositionSettledTargetRef.current;
          nativeHlsInitialPositionSettledTargetRef.current = null;
          if (
            nativeHlsPlaybackRef.current
            && settledNativeTarget !== null
            && pendingSeekTimeRef.current === null
            && !seekSwitching
            && Math.abs((videoRef.current?.currentTime ?? 0) - settledNativeTarget) <= 0.5
          ) {
            return;
          }
          setReadyToStart(false);
          if (!previewSeeking) {
            setBuffering(true);
          }
        }}
        onSeeked={() => {
          const v = videoRef.current;
          if (!v) return;
          queueClientPlaybackLogRef.current("media_seeked", "media_event");
          attemptStartupPlaybackRef.current?.();
          if (pendingResumeAuthorityRef.current || resumeAuthorityFailedRef.current) {
            return;
          }
          const absoluteTime = getAbsoluteVideoTime(v);
          const bufferedAhead = getBufferedAheadSeconds(v, absoluteTime, mediaTimelineOffsetSeconds);
          releaseNativeSeekIfReached(absoluteTime);
          clearPendingSeekIfReached(absoluteTime);
          syncBufferedAhead(v);
          if (!previewSeeking) {
            if (bufferedAhead > 0.1) {
              setBuffering(false);
              setAwaitingPlaybackStart(false);
              setReadyToStart(true);
            }
            if (v.paused) {
              syncPausedReadyState(v);
            }
          }
        }}
        onWaiting={() => {
          queueClientPlaybackLogRef.current("media_waiting", "media_event");
          handleBufferingStart();
        }}
        onPlaying={() => {
          if (pendingResumeAuthorityRef.current || resumeAuthorityFailedRef.current) {
            videoRef.current?.pause();
            return;
          }
          if (nativeHlsInitialPositionPendingRef.current) {
            setPlaying(false);
            setBuffering(true);
            setAwaitingPlaybackStart(false);
            return;
          }
          queueClientPlaybackLogRef.current("media_playing", "media_event");
          if (videoRef.current) {
            releaseNativeSeekIfReached(getAbsoluteVideoTime(videoRef.current));
          }
          clearPlayerErrorIfPlaybackHealthy(videoRef.current);
          syncDecodedVideoSize(videoRef.current);
          resumeAfterSessionSwitchRef.current = false;
          userPausedRef.current = false;
          clearStartupPlayTimer();
          startupPlayRequestRef.current = false;
          playbackStartedRef.current = true;
          autoplayBlockedRef.current = false;
          setStartupAutoplayBlocked(false);
          setPlaying(true);
          setAwaitingPlaybackStart(false);
          setReadyToStart(false);
          handleBufferingEnd();
        }}
        onCanPlay={() => {
          const v = videoRef.current;
          if (!v) {
            setBuffering(false);
            return;
          }
          queueClientPlaybackLogRef.current("media_canplay", "media_event");
          updateObservedDuration(v);
          syncDecodedVideoSize(v);
          syncBufferedAhead(v);
          attemptStartupPlayback();
          const absoluteTime = getAbsoluteVideoTime(v);
          releaseNativeSeekIfReached(absoluteTime);
          clearPendingSeekIfReached(absoluteTime);
          syncPausedReadyState(v);
        }}
        onTimeUpdate={() => {
          const v = videoRef.current;
          if (v) {
            attemptStartupPlaybackRef.current?.();
            if (pendingResumeAuthorityRef.current || resumeAuthorityFailedRef.current) {
              return;
            }
            if (nativeHlsInitialPositionPendingRef.current) {
              return;
            }
            const absoluteTime = getAbsoluteVideoTime(v);
            clearPlayerErrorIfPlaybackHealthy(v);
            recordObservedPositionMS(Math.floor(absoluteTime * 1000));
            setCurrentTime(absoluteTime);
            if (!v.paused && v.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
              userPausedRef.current = false;
              startupPlayRequestRef.current = false;
              playbackStartedRef.current = true;
              autoplayBlockedRef.current = false;
              setStartupAutoplayBlocked(false);
              setPlaying(true);
              setAwaitingPlaybackStart(false);
              setReadyToStart(false);
              setBuffering(false);
            }
            if (playbackEnded && effectiveDuration > 0 && absoluteTime + 1 < effectiveDuration) {
              playbackCompletedRef.current = false;
              setPlaybackEnded(false);
            }
            if (
              effectiveDuration > 0
              && absoluteTime >= effectiveDuration - 0.4
              && mediaElementCorroboratesCompletion(v)
            ) {
              completePlayback(Math.floor(effectiveDuration * 1000));
            }
            syncDecodedVideoSize(v);
            syncBufferedAhead(v);
            releaseNativeSeekIfReached(absoluteTime);
            clearPendingSeekIfReached(absoluteTime);
            if (knownDuration <= 0 && Number.isFinite(v.duration) && v.duration > 0) {
              updateObservedDuration(v);
            }
          }
        }}
        onLoadedMetadata={() => {
          playbackCompletedRef.current = false;
          setPlaybackEnded(false);
          updateObservedDuration(videoRef.current);
          syncDecodedVideoSize(videoRef.current);
          syncBufferedAhead(videoRef.current);
          syncAudioState(videoRef.current, false);
          attemptStartupPlayback();
          syncPausedReadyState(videoRef.current);
        }}
        onEnded={() => {
          const v = videoRef.current;
          queueClientPlaybackLogRef.current("media_ended", "media_event");
          if (nativeHlsInitialPositionPendingRef.current) {
            setPlaying(false);
            setBuffering(true);
            setAwaitingPlaybackStart(false);
            return;
          }
          const absoluteTime = v ? getAbsoluteVideoTime(v) : currentTimeRef.current;
          const completionDuration = effectiveDurationRef.current > 0
            ? effectiveDurationRef.current
            : effectiveDuration;
          if (isEarlyMediaEnded(absoluteTime, completionDuration)) {
            playbackCompletedRef.current = false;
            setPlaybackEnded(false);
            setCurrentTime(absoluteTime);
            setPlaying(false);
            setBuffering(false);
            setAwaitingPlaybackStart(false);
            setReadyToStart(true);
            reportClientPlaybackStateRef.current?.("media_ended_before_known_duration");
            return;
          }
          completePlayback(
            Math.floor((completionDuration > 0 ? completionDuration : absoluteTime) * 1000),
            true,
          );
        }}
        onVolumeChange={() => {
          syncAudioState(videoRef.current, true);
        }}
        onResize={() => syncDecodedVideoSize(videoRef.current)}
        onDurationChange={() => updateObservedDuration(videoRef.current)}
        onError={() => {
          if (runtimeRenegotiatingRef.current) {
            return;
          }
          reportClientPlaybackState("media_error");
          if (!playbackStartedRef.current) {
            setBuffering(false);
            setAwaitingPlaybackStart(false);
            setPlayerError("Playback failed before it could start.");
            return;
          }
          void recoverPlaybackRuntime("Playback failed and could not recover automatically.");
        }}
        onProgress={() => {
          syncBufferedAhead(videoRef.current);
          attemptStartupPlayback();
        }}
      >
        {inlineSubtitleTracks.map((track) => (
          <track
            key={track.id}
            id={track.id}
            kind="subtitles"
            label={track.attributes.label}
            srcLang={track.attributes.language}
            src={track.attributes.url}
            default={track.id === selectedSubtitleId}
          />
        ))}
      </video>

      <button
        type="button"
        className="player-video-surface"
        aria-label="Toggle playback from video surface"
        tabIndex={-1}
        onClick={() => handleVideoSurfaceClick()}
      />

      {/* Buffering indicator — always visible regardless of controls */}
      {showBufferingIndicator && (
        <div className="player-buffering">
          <div className="player-buffering-spinner" />
          <span>
            {pendingSeekTime !== null
              ? `Jumping to ${formatTime(pendingSeekTime)}...`
              : "Buffering..."}
          </span>
        </div>
      )}

      {playerError && (
        <div className="player-buffering">
          <span>{playerError}</span>
          {sessionId && itemId && (
            <button
              type="button"
              className="player-buffering-action"
              onClick={(event) => {
                event.stopPropagation();
                const failedSeekTarget = failedSeekTargetRef.current;
                if (failedSeekTarget !== null) {
                  failedSeekTargetRef.current = null;
                  void seekToPosition(failedSeekTarget, true, "retry_button", "failed_seek_retry");
                  return;
                }
                void recoverPlaybackRuntime("Playback failed and could not recover automatically.");
              }}
            >
              Retry
            </button>
          )}
        </div>
      )}

      {nextEpisodePromptVisible && nextEpisode && (
        <div
          className="player-next-episode"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <div className="player-next-episode__eyebrow">
            {playbackEnded ? "Next episode ready" : "Up next"}
          </div>
          <div className="player-next-episode__title">{nextEpisodeLabel}</div>
          {playbackEnded && (
            <div className="player-next-episode__countdown">
              Playing automatically in {nextEpisodeAutoplayRemainingSeconds}s
            </div>
          )}
          <button
            type="button"
            className="player-next-episode__action"
            onClick={(event) => {
              event.stopPropagation();
              void handlePlayNextEpisode();
            }}
          >
            Play Next
          </button>
        </div>
      )}

      {activeIntroMarker && !nextEpisodePromptVisible && (
        <button
          type="button"
          className="player-skip-intro"
          onClick={(event) => {
            event.stopPropagation();
            void seekToPosition(Math.max(0, (activeIntroMarker.end_ms / 1000) - 2), true, "skip_intro_button", "skip_intro");
          }}
        >
          Skip Intro
        </button>
      )}

      {(readyToStart ||
        startupAutoplayBlocked ||
        (!awaitingPlaybackStart &&
        !playing &&
        !previewSeeking &&
        !buffering &&
        pendingSeekTime === null &&
        !seekSwitching &&
        !playerError &&
        !nextEpisodePromptVisible)) && (
        <button
          className="player-center-toggle"
          onClick={(event) => {
            event.stopPropagation();
            handleVideoSurfaceClick("center_toggle_click", "click");
          }}
          aria-label="Play"
        >
          ▶
        </button>
      )}

      {/* Top bar */}
      <div className={`player-topbar ${controlsVisible ? "" : "player-controls--hidden"}`}>
        <button className="player-back-btn" onClick={() => void handleStop("stop_button", "click")}>
          ← Back
        </button>
        {currentItem?.attributes.item_type === "episode" ? (
          <div className="player-title-block">
            <span className="player-title-kicker">{episodeOverlaySeriesTitle}</span>
            <span className="player-title">{episodeOverlayTitle}</span>
            {episodeOverlayMeta && <span className="player-title-meta">{episodeOverlayMeta}</span>}
          </div>
        ) : (
          <span className="player-title">{itemTitle}</span>
        )}
        <div className="player-debug-actions">
          <WatchToggleButton
            className={`player-watch-btn${currentItemWatched ? " player-watch-btn--watched" : ""}${watchUpdating ? " player-watch-btn--pending" : ""}`}
            watched={currentItemWatched}
            updating={!canToggleCurrentItemWatched || watchUpdating}
            watchedLabel="Mark as played"
            unwatchedLabel="Mark as unplayed"
            onToggle={() => void handleToggleCurrentItemWatched()}
          />
          {(watchMessage || watchError) && (
            <span className={`player-watch-status${watchError ? " player-watch-status--error" : ""}`}>
              {watchError ?? watchMessage}
            </span>
          )}
          <span className="player-badge" title="Playback session id for this exact browser session.">
            {sessionId}
          </span>
          <button
            type="button"
            className="player-debug-btn"
            onClick={(event) => {
              event.stopPropagation();
              void handleCopyPlaybackDebug();
            }}
          >
            {copyDebugStatus === "copied" ? "Copied" : copyDebugStatus === "failed" ? "Copy Failed" : "Copy Debug"}
          </button>
          <button
            type="button"
            className={`player-debug-btn${playbackDiagnosticsEnabled ? " player-debug-btn--active" : ""}`}
            onClick={(event) => {
              event.stopPropagation();
              togglePlaybackDiagnostics();
            }}
            disabled={diagnosticsToggleBusy}
            title="Stream playback diagnostics to the server for support. Off by default; turn on, reproduce the problem, then turn off."
            aria-pressed={playbackDiagnosticsEnabled}
          >
            {playbackDiagnosticsEnabled ? "● Debug streaming" : "Debug off"}
          </button>
          <button
            type="button"
            className="player-debug-btn"
            onClick={(event) => {
              event.stopPropagation();
              setReportStatus("idle");
              setReportDialogOpen(true);
            }}
            title="Send this session's detailed playback logs to CodifyWorx to help diagnose a problem."
          >
            {reportStatus === "sent" ? "Reported ✓" : reportStatus === "downloaded" ? "Downloaded ✓" : "Report problem"}
          </button>
        </div>

        {reportDialogOpen && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Send playback diagnostics to CodifyWorx"
            onClick={(event) => event.stopPropagation()}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.6)",
              padding: "1rem",
            }}
          >
            <div
              style={{
                maxWidth: "30rem",
                width: "100%",
                background: "#15171c",
                color: "#f5f5f5",
                borderRadius: "10px",
                padding: "1.25rem 1.5rem",
                boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
              }}
            >
              <h2 style={{ marginTop: 0, fontSize: "1.15rem" }}>Send playback diagnostics to CodifyWorx?</h2>
              <p style={{ lineHeight: 1.5 }}>
                To help diagnose this playback problem,{" "}
                <strong>detailed playback logs from this session will be sent to CodifyWorx.</strong> This includes what
                you&rsquo;re watching and technical details about playback &mdash; buffering, quality changes, errors,
                network timing, and app version. It does <strong>not</strong> include your password or payment info.
              </p>
              <p style={{ lineHeight: 1.5 }}>
                Nothing is sent unless you choose <strong>Send to CodifyWorx</strong>. Or choose{" "}
                <strong>Download bundle</strong> to save it to this device and inspect or share it yourself &mdash;
                nothing is sent.
              </p>
              {reportStatus === "failed" && (
                <p role="alert" style={{ color: "#ff8a80" }}>
                  Couldn&rsquo;t send. Please try again.
                </p>
              )}
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "1rem" }}>
                <button
                  type="button"
                  className="player-debug-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    setReportDialogOpen(false);
                  }}
                  disabled={reportStatus === "sending"}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="player-debug-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    downloadPlaybackProblemReport();
                  }}
                  disabled={reportStatus === "sending"}
                >
                  Download bundle
                </button>
                <button
                  type="button"
                  className="player-debug-btn player-debug-btn--active"
                  onClick={(event) => {
                    event.stopPropagation();
                    void submitPlaybackProblemReport();
                  }}
                  disabled={reportStatus === "sending"}
                >
                  {reportStatus === "sending" ? "Sending…" : "Send to CodifyWorx"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stream quality indicator — always visible when streaming */}
        {delivery && (
          <div className="player-stream-info">
            <div className="player-stream-badges">
              {delivery.conservative_streaming && (
                <span
                  className="player-badge player-badge--warn"
                  title="KnotFin is using a more conservative startup profile to keep playback stable."
                >
                  Conservative startup
                </span>
              )}
              <span
                className="player-badge"
                title={sourceLimitedSession
                  ? `Source bitrate: ${formatBitrate(delivery.target_video_bitrate)}. This session is limited by the media itself, not by the connection ceiling.`
                  : `Session bitrate cap: ${formatBitrate(delivery.target_video_bitrate)}. This is the bitrate ceiling for this playback session, not a live speed test.`}
              >
                {sourceLimitedSession ? "Source " : "Cap "}
                {formatBitrate(delivery.target_video_bitrate)}
              </span>
              {sourceLimitedSession && (
                <span
                  className="player-badge"
                  title="KnotFin is already sending this item at the source bitrate for the current session shape, so a faster connection will not raise quality further."
                >
                  Source-limited
                </span>
              )}
              <span
                className={`player-badge player-badge--health ${effectivePlaybackHealth === "critical" ? "player-badge--warn" : ""}`}
                title="Current playback health based on recent buffering and bandwidth samples."
              >
                {playbackHealthLabel}
              </span>
            </div>
            <div className="player-stream-diagnostics">
              {currentVariantSummary && (
                <span
                  className="player-badge player-badge--video"
                  title="Current HLS ladder level metadata for this session."
                >
                  Stream {currentVariantSummary}
                </span>
              )}
              {!currentVariantSummary && nativeHlsDelivery && (
                <span
                  className="player-badge player-badge--video"
                  title="Native HLS is selecting the rendition inside the browser; segment requests show the actual stream variant."
                >
                  Stream Client ABR
                </span>
              )}
              {measuredBandwidthBps > 0 && (
                <span
                  className="player-badge player-badge--throughput"
                  title="Current measured playback throughput from the browser HLS session."
                >
                  Throughput {formatBitrate(measuredBandwidthBps)}
                </span>
              )}
              <span className="player-badge player-badge--buffer" title="How much playable media is currently buffered ahead of the playhead.">
                Buffer {bufferedAheadSeconds.toFixed(1)}s
              </span>
              <span
                className={`player-badge player-badge--build${buildFreshness.stale ? " player-badge--build-stale" : ""}`}
                title={playerBuildTitle}
              >
                {playerBuildLabel}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className={`player-controls ${controlsVisible ? "" : "player-controls--hidden"}`}>
        <div className="player-progress">
          <span className="player-time">{formatTime(displayedCurrentTime)}</span>
          <div className="player-seek-wrap" ref={seekWrapRef}>
            <div className="player-seek-rail" aria-hidden="true">
              <div
                className="player-seek-rail-fill"
                style={{ width: `${seekProgressPercent}%` }}
              />
            </div>
            {showSeekPreviewTarget && activeSeekPreviewTime !== null && trickplayPreviewLayout && (
              <div
                className="player-trickplay-preview"
                style={{
                  left: `${trickplayPreviewLayout.leftPx}px`,
                  width: `${TRICKPLAY_PREVIEW_PANEL_WIDTH_PX}px`,
                }}
              >
                <div
                  className="player-trickplay-preview-pointer"
                  style={{ left: `${trickplayPreviewLayout.pointerWithinPanelPx}px` }}
                />
                {activeTrickplayFrame && (
                  <div className="player-trickplay-preview-image" style={trickplayPreviewStyle} />
                )}
                <div className="player-trickplay-preview-time">{formatTime(activeSeekPreviewTime)}</div>
              </div>
            )}
            <div
              role="slider"
              aria-label="Seek"
              aria-valuemin={0}
              aria-valuemax={seekMax}
              aria-valuenow={Math.floor(seekValue)}
              aria-valuetext={formatTime(seekValue)}
              tabIndex={effectiveDuration <= 0 || seekSwitching ? -1 : 0}
              className="player-seek"
              data-seek-value={seekValue}
              aria-disabled={effectiveDuration <= 0 || seekSwitching}
              onPointerDown={(e) => {
                if (effectiveDuration <= 0 || seekSwitching) {
                  return;
                }
                e.preventDefault();
                if (e.currentTarget.setPointerCapture) {
                  e.currentTarget.setPointerCapture(e.pointerId);
                }
                beginPointerScrub(pointerEventClientX(e), e.currentTarget, e.pointerType);
              }}
              onPointerMove={(e) => {
                if (seekPointerActiveRef.current) {
                  updatePointerScrub(pointerEventClientX(e), e.currentTarget, e.pointerType);
                  return;
                }
                updateSeekHover(pointerEventClientX(e), e.currentTarget);
              }}
              onPointerUp={(e) => {
                const target = seekPointerActiveRef.current
                  ? sliderTimeForPointer(pointerEventClientX(e), e.currentTarget)
                  : seekValue;
                commitScrub(target);
              }}
              onPointerCancel={cancelScrub}
              onMouseLeave={clearSeekHover}
              onBlur={() => {
                if (keySeekDirectionRef.current !== null) {
                  cancelPreviewSeek();
                  return;
                }
                if (scrubbing || seekPointerActiveRef.current || seekRailKeyRef.current !== null) {
                  cancelScrub();
                  return;
                }
                clearSeekHover();
              }}
              onKeyDown={(e) => {
                switch (e.key) {
                  case "ArrowLeft":
                    e.preventDefault();
                    e.stopPropagation();
                    if (keySeekDirectionRef.current === null) {
                      startKeyboardPreviewSeek("backward", e.key, "seek_rail_keyboard");
                    }
                    break;
                  case "ArrowRight":
                    e.preventDefault();
                    e.stopPropagation();
                    if (keySeekDirectionRef.current === null) {
                      startKeyboardPreviewSeek("forward", e.key, "seek_rail_keyboard");
                    }
                    break;
                  case "PageDown":
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.repeat || seekRailKeyRef.current !== null) {
                      return;
                    }
                    seekRailKeyRef.current = e.key;
                    nudgeSeekValue("page_backward", e.currentTarget);
                    break;
                  case "PageUp":
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.repeat || seekRailKeyRef.current !== null) {
                      return;
                    }
                    seekRailKeyRef.current = e.key;
                    nudgeSeekValue("page_forward", e.currentTarget);
                    break;
                  case "Home":
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.repeat || seekRailKeyRef.current !== null) {
                      return;
                    }
                    seekRailKeyRef.current = e.key;
                    nudgeSeekValue("start", e.currentTarget);
                    break;
                  case "End":
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.repeat || seekRailKeyRef.current !== null) {
                      return;
                    }
                    seekRailKeyRef.current = e.key;
                    nudgeSeekValue("end", e.currentTarget);
                    break;
                }
              }}
              onKeyUp={(e) => {
                if (!["ArrowLeft", "ArrowRight", "PageDown", "PageUp", "Home", "End"].includes(e.key)) {
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
                if (e.key === "ArrowLeft") {
                  finishKeyboardPreviewSeek("backward");
                  return;
                }
                if (e.key === "ArrowRight") {
                  finishKeyboardPreviewSeek("forward");
                  return;
                }
                if (seekRailKeyRef.current !== e.key) {
                  return;
                }
                seekRailKeyRef.current = null;
                const target = Math.max(0, Math.min(seekInteractionValueRef.current ?? seekValue, seekMax));
                setSeekInteractionValue(target);
                scheduleSeekRailKeyboardCommit(target);
              }}
            >
              <div
                className="player-seek-thumb"
                aria-hidden="true"
                style={{ left: `${seekProgressPercent}%` }}
              />
            </div>
          </div>
          {seekDebugEnabled && seekDebugSnapshot && (
            <div className="player-seek-debug" data-testid="player-seek-debug">
              <div>
                event {seekDebugSnapshot.event} | pointer{" "}
                {seekDebugSnapshot.pointerClientX !== null ? seekDebugSnapshot.pointerClientX.toFixed(1) : "--"}
              </div>
              <div>
                wrapper {seekDebugSnapshot.wrapperLeft.toFixed(1)} + {seekDebugSnapshot.wrapperWidth.toFixed(1)} | input{" "}
                {seekDebugSnapshot.inputLeft.toFixed(1)} + {seekDebugSnapshot.inputWidth.toFixed(1)}
              </div>
              <div>
                wrapper ratio{" "}
                {seekDebugSnapshot.wrapperRatio !== null ? `${(seekDebugSnapshot.wrapperRatio * 100).toFixed(1)}%` : "--"} | input ratio{" "}
                {seekDebugSnapshot.inputRatio !== null ? `${(seekDebugSnapshot.inputRatio * 100).toFixed(1)}%` : "--"}
              </div>
              <div>
                hover {seekDebugSnapshot.hoverTime !== null ? formatTime(seekDebugSnapshot.hoverTime) : "--:--"} | slider{" "}
                {formatTime(seekDebugSnapshot.sliderValue)}
              </div>
            </div>
          )}
          <span className="player-time">{effectiveDuration > 0 ? formatTime(effectiveDuration) : "--:--"}</span>
        </div>

        <div className="player-controls-row">
          <div className="player-controls-left">
            <IconButton
              className="player-btn"
              align="start"
              label="Back to the beginning"
              onClick={handleRestartClick}
            >
              <RestartIcon />
            </IconButton>
            <IconButton
              className="player-btn"
              label="Skip back 10 seconds"
              onPointerDown={(event) => handlePreviewSeekButtonPointerDown(-10, event)}
              onPointerUp={handlePreviewSeekButtonPointerUp}
              onPointerCancel={handlePreviewSeekButtonPointerCancel}
              onClick={(event) => handlePreviewSeekButtonClick(-10, event)}
            >
              <SkipBackIcon />
            </IconButton>
            <IconButton
              className="player-btn"
              onClick={() => togglePlay("control_button_click", "click")}
              label={playing ? "Pause" : "Play"}
            >
              {playing ? <PauseIcon /> : <PlayIcon />}
            </IconButton>
            <IconButton
              className="player-btn"
              label="Skip forward 10 seconds"
              onPointerDown={(event) => handlePreviewSeekButtonPointerDown(10, event)}
              onPointerUp={handlePreviewSeekButtonPointerUp}
              onPointerCancel={handlePreviewSeekButtonPointerCancel}
              onClick={(event) => handlePreviewSeekButtonClick(10, event)}
            >
              <SkipForwardIcon />
            </IconButton>
            <IconButton
              className="player-btn"
              label={nextEpisode ? "Next episode" : "Finish and mark as played"}
              onClick={() => void handleNextClick()}
            >
              <NextIcon />
            </IconButton>
          </div>

          <div className="player-controls-right">
            {showSubtitleControl && (
              <label className="player-subtitle-control">
                <span className="player-quality-label">CC</span>
                <span className="player-pill-value" aria-hidden="true">
                  {selectedSubtitleTrack ? selectedSubtitleTrack.attributes.label : "Off"}
                </span>
                <select
                  className="player-quality-select"
                  value={selectedSubtitleTrack ? selectedSubtitleTrack.id : ""}
                  onChange={handleSubtitleSelectChange}
                  aria-label="Subtitle track"
                  title={subtitleButtonTitle}
                  disabled={availableSubtitleTracks.length === 0}
                >
                  <option value="">Off</option>
                  {availableSubtitleTracks.map((track) => (
                    <option key={track.id} value={track.id}>
                      {track.attributes.label}
                    </option>
                  ))}
                  {preparingSubtitleTracks.length > 0 && (
                    <optgroup label="Preparing">
                      {preparingSubtitleTracks.map((track) => (
                        <option key={track.id} value={`preparing:${track.id}`} disabled>
                          {track.attributes.label}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </label>
            )}
            {audioTracks.length > 1 && (
              <label className="player-quality-control" title="Audio track">
                <span className="player-quality-label">Audio</span>
                <span className="player-pill-value" aria-hidden="true">
                  {selectedAudioTrackIndex >= 0 && audioTracks[selectedAudioTrackIndex]
                    ? formatAudioTrackLabel(audioTracks[selectedAudioTrackIndex], selectedAudioTrackIndex)
                    : "Audio"}
                </span>
                <select
                  className="player-quality-select"
                  value={selectedAudioTrackIndex >= 0 ? String(selectedAudioTrackIndex) : ""}
                  onChange={handleAudioTrackChange}
                  aria-label="Audio track"
                >
                  {/* Keeps the select on a real option while no track is
                      selected, so choosing track 0 still fires change (#1035). */}
                  {selectedAudioTrackIndex < 0 && (
                    <option value="" disabled hidden>
                      Audio
                    </option>
                  )}
                  {audioTracks.map((track, index) => (
                    <option key={`${track.id ?? index}:${track.lang ?? ""}:${track.name}`} value={index}>
                      {formatAudioTrackLabel(track, index)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="player-volume-control">
              <IconButton
                className="player-btn"
                type="button"
                onClick={toggleMute}
                label={effectiveMuted ? "Unmute" : "Mute"}
              >
                <VolumeIcon level={volumeLevel} muted={effectiveMuted} />
              </IconButton>
              <input
                type="range"
                className="player-volume-slider"
                min={0}
                max={1}
                step={0.05}
                value={effectiveMuted ? 0 : volumeLevel}
                style={{ "--volume-fill": `${Math.round((effectiveMuted ? 0 : volumeLevel) * 100)}%` } as CSSProperties}
                onChange={handleVolumeChange}
                aria-label="Volume"
              />
            </div>
            <label className="player-quality-control" title="Stream quality">
              <span className="player-quality-label">Cap</span>
              <span className="player-pill-value" aria-hidden="true">
                {QUALITY_OPTIONS.find((option) => option.id === currentQuality)?.label ?? "Auto"}
              </span>
              <select
                className="player-quality-select"
                value={currentQuality}
                onChange={handleQualityChange}
                aria-label="Stream bitrate cap"
                disabled={qualitySwitching || !itemId || nativeHlsDelivery}
              >
                {QUALITY_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <IconButton
              className="player-btn"
              align="end"
              label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              onClick={() => {
                if (document.fullscreenElement) document.exitFullscreen();
                else document.documentElement.requestFullscreen();
              }}
            >
              <FullscreenIcon active={isFullscreen} />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
}
